import EtarTaskPicker, { matchedEtarTasks, etarCoverageLabel, waiverCoverageLabel, etarDisplayTitle } from './EtarTaskPicker';
import InspectionBacklog from './InspectionBacklog';
import MaintenanceReadyKpi from './MaintenanceReadyKpi';
import DataCurrencyTab from './DataCurrencyTab';
import UnstackedRtgWork, { layoutStackedRtgWork, layoutUnstackedRtgWork, stackedDriverStripeBackground } from './RtgTimelineWork';
import { estimatedRtgDriverItems } from './RtgEstimatedDrivers';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  RefreshCw,
  ShieldCheck,
  Upload,
  Wrench,
  X,
} from 'lucide-react';
import './modernized_app.css';
import OffstationLocationDisplay from './OffstationLocationDisplay';


const VIEWS = [
  ['overview', 'Readiness'],
  ['rtg', 'Return to Green'],
  ['capacity', 'Shop Capacity'],
  ['changes', 'What Changed'],
  ['history', 'Estimate History'],
  ['waivers', 'Waivers'],
  ['sources', 'Data Currency'],
  ['chat', 'Ask Crystal Ball'],
];

const READINESS_ROLLUPS = [
  ['quarter', 'Quarters'],
  ['month', 'Months'],
  ['week', 'Weeks'],
  ['day', 'Days'],
];

const AIRCRAFT_SCOPE_FILTERS = [
  ['combined', 'Combined'],
  ['hs', 'Homestation'],
  ['os', 'Offstation'],
];

const CAPACITY_CONCERN_FILTERS = [
  ['all', 'Needs Review'],
  ['shortfall', 'Calculated Shortfalls'],
  ['signoff', '7/9 Signoff'],
  ['planning', 'Timing/Input Gaps'],
  ['reviewed', 'Reviewed'],
];

const CRYSTAL_STATUS_PDF_SECTIONS = [
  { key: 'overview', label: 'Summary cards' },
  { key: 'inspection-backlog', label: 'Inspection backlog by task' },
  { key: 'tail-summary', label: 'Tail Summary' },
  { key: 'open-x', label: 'Open X from 8035' },
  { key: 'overdue-9188', label: '9188 -6 Overdue' },
  { key: 'red-9188', label: '9188 -6 Due Soon (10%)' },
  { key: 'watch-9188', label: '9188 -6 Due Soon (25%)' },
  { key: 'missing-dates', label: 'Items Missing Due & Start Dates' },
];

const DEFAULT_CRYSTAL_STATUS_PDF_SECTIONS = Object.fromEntries(
  CRYSTAL_STATUS_PDF_SECTIONS.map(section => [section.key, true])
);

const CRYSTAL_STATUS_PDF_CAPTURE_SCALE = 1.15;
const CRYSTAL_STATUS_PDF_IMAGE_QUALITY = 0.82;
const CAPACITY_CONCERN_PAGE_SIZE = 10;
const CAPACITY_CONCERN_VISIBLE_DRIVER_COUNT = 3;
const CAPACITY_REVIEW_STORAGE_PREFIX = 'mfeCrystalBallReviewedCapacityConcerns';
const LABOR_ASSESSMENT_QUESTION = [
  'Assess labor capacity risk for this Crystal Ball scope.',
  'Prioritize concrete concerns to address, including the month, site, MDS, shop, affected aircraft, driver work, missing labor/date inputs, roster availability impacts, scheduled-out or partially available personnel, 7/9 signoff gaps, and any same-MDS support candidates.',
  'Separate confirmed facts from inferences and recommend leadership actions.',
].join(' ');
const LABOR_CAPACITY_COUNT_HELP = 'Personnel counted toward this labor-capacity calculation; not a job-assignment count.';
const TAIL_SWAP_TBD_VALUE = 'TBD';
const AVAILABLE_TAILS_HELP_TEXT = 'Shown only for TBD tail swaps. Candidate homestation tails in the same MDS must have every open X planned or cleared before the swap date, then sort by least overdue readiness drivers, least 10% due-soon readiness drivers, least 25% due-soon readiness drivers, and finally least open K delayed discrepancies.';
const CONFIDENCE_HELP_TEXT = 'Confidence is a readiness forecast supportability score: how well the projected ready date is backed by source data, planning dates, labor estimates, shop capacity, 7/9-level coverage, return gates, supply constraints, open X/K conditions, and tail-swap/TBD assumptions. Bands: 90+ fully supported, 75-89 high, 60-74 moderate, 45-59 low, below 45 command attention.';

const STACKED_RTG_DRIVER_CATEGORIES = [
  { key: 'pdm', label: 'PDM', className: 'is-driver-pdm', color: '#5b4b8a' },
  { key: 'hpo', label: 'HPO', className: 'is-driver-hpo', color: '#8f2d56' },
  { key: 'wash', label: 'ACFT Wash', className: 'is-driver-wash', color: '#2f7d46' },
  { key: 'hour900', label: '900 hr', className: 'is-driver-900', color: '#b43d2f' },
  { key: 'hour1800', label: '1800 hr', className: 'is-driver-1800', color: '#0f766e' },
  { key: 'rtic', label: 'RTIC', className: 'is-driver-rtic', color: '#2f5f9f' },
  { key: 'openx', label: 'Open X', className: 'is-driver-openx', color: '#b66a0b' },
  { key: 'alert', label: 'Alert', className: 'is-driver-alert', color: '#d6a21f' },
  { key: 'other', label: 'Other Drivers', className: 'is-driver-other', color: '#627682' },
];

const STACKED_RTG_DRIVER_CATEGORY_BY_KEY = new Map(
  STACKED_RTG_DRIVER_CATEGORIES.map(category => [category.key, category])
);
const RTG_TIMELINE_ZOOM_STEPS = [100, 125, 150, 175, 200];
const RTG_RETURN_HIGHLIGHT = 'return-date';
const RTG_TAIL_SWAP_HIGHLIGHT = 'tail-swap';

const AFRC_HEADQUARTERS_LOCATION = 'Air Force Reserve Command';

const AFRC_ROLLUP_LOCATION_NAMES = [
  // Add one quoted location display name per line, exactly as it appears in the site selector / BaseLocation.name.
  // Format: 'Pittsburgh ARS', not 'PA / Pittsburgh ARS' and not an object with state/country fields.
  // Matching trims whitespace and ignores case, but keep the spelling/capitalization identical to the selector for readability.
  // Do not include 'Air Force Reserve Command' here unless the HQ organization itself should be part of the rollup.
  'Barksdale Air Force Base',
  'Beale Air Force Base',
  'Ft Worth NAS (Carswell)',
  'Davis-Monthan Air Force Base',
  'Dobbins Air Reserve Base',
  'Grissom Air Reserve Base',
  'Hill Air Force Base',
  'Homestead Air Reserve Base',
  'JBSA Lackland (Kelly ANG)',
  'Joint Base Andrews',
  'Keesler Air Force Base',
  'March Air Reserve Base',
  'Maxwell Air Force Base',
  'Minneapolis-St Paul ARS',
  'Moody Air Force Base',
  'Niagara Falls ARS',
  'Patrick Air Force Base',
  'Peterson Air Force Base',
  'Pittsburgh ARS',
  'Robins Air Force Base',
  'Scott Air Force Base',
  'Seymour Johnson Air Force Base',
  'Tinker Air Force Base',
  'Tucson Intl',
  'Westover Air Reserve Base',
  'Whiteman Air Force Base',
  'Wright-Patterson Air Force Base',
  'Youngstown ARS',
];

const DEFAULT_EXCLUDED_ROLLUP_LOCATION_NAMES = [
  'Tampa International Airport (Demo)',
  'Tampa International Demo',
];

function normalizedLocationKey(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function defaultIncludedRollupLocations(locations) {
  const excluded = new Set(DEFAULT_EXCLUDED_ROLLUP_LOCATION_NAMES.map(normalizedLocationKey));
  return (locations || []).filter(location => !excluded.has(normalizedLocationKey(location)));
}

function isAfRcHeadquartersLocation(value) {
  const normalized = normalizedLocationKey(value);
  return Boolean(normalized) && (
    normalized === AFRC_HEADQUARTERS_LOCATION.toLowerCase()
    || normalized.endsWith(`- ${AFRC_HEADQUARTERS_LOCATION.toLowerCase()}`)
    || normalized.includes(AFRC_HEADQUARTERS_LOCATION.toLowerCase())
  );
}

function fmtDate(value, withTime = false) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString([], withTime
    ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { month: 'short', day: 'numeric' });
}

function fmtPlanDate(value) {
  if (!value) return '--';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return fmtDate(value);
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString([], { month: 'short', day: 'numeric' });
}

function fmtReviewDate(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtProjectedReadyDate(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDayMonthYear(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const day = date.toLocaleDateString([], { day: '2-digit' });
  const month = date.toLocaleDateString([], { month: 'short' });
  const year = date.toLocaleDateString([], { year: '2-digit' });
  return `${day} ${month} ${year}`;
}

function fmtHours(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '--';
  return `${number.toFixed(1).replace(/\.0$/, '')}h`;
}

function fmtForecastChange(value) {
  if (value == null) return '--';
  const hours = Number(value);
  if (!Number.isFinite(hours)) return '--';
  const sign = hours > 0 ? '+' : hours < 0 ? '-' : '';
  const magnitude = Math.abs(hours);
  if (magnitude <= 72) return `${sign}${magnitude}h`;
  const days = Math.floor(magnitude / 24);
  return `${sign}${days}d ${fmtHours(magnitude - days * 24)}`;
}

function parseOptionalMs(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function truncateText(value, maxLength = 96) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '--';
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}...` : text;
}

function uniqueList(values) {
  return [...new Set((values || []).map(value => String(value || '').trim()).filter(Boolean))];
}

function reviewActorLabel(user) {
  return (
    user?.displayName
    || user?.display_name
    || user?.name
    || user?.fullName
    || user?.username
    || user?.email
    || 'Unknown user'
  );
}

function capacityDriverDateLabel(item) {
  const start = fmtDate(item?.plannedStartAt || item?.startAt || item?.startDate);
  const finish = fmtDate(item?.plannedFinishAt || item?.finishAt || item?.finishDate);
  if (start !== '--' && finish !== '--' && start !== finish) return `${start} - ${finish}`;
  if (start !== '--') return start;
  if (finish !== '--') return finish;
  return '--';
}

function capacityConcernPeriod(row) {
  return capacityPeakPeriod(row, 'shortfallHours') || capacityPeakPeriod(row, 'workHours');
}

function capacityItemIdentity(item) {
  return [
    item?.tail || '',
    item?.serialNumber || '',
    item?.type || '',
    item?.id || '',
    item?.jcn || '',
    item?.label || item?.risk || '',
    item?.shopName || item?.shop || '',
    item?.plannedStartAt || '',
    item?.plannedFinishAt || '',
    item?.hours || '',
  ].join('|');
}

function uniqueCapacityItems(items = []) {
  const seen = new Set();
  return (items || []).filter(item => {
    const key = capacityItemIdentity(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function capacityConcernItems(row, peakPeriod, mode = 'all') {
  const periodItems = Array.isArray(peakPeriod?.items) ? peakPeriod.items : [];
  const rowItems = Array.isArray(row?.items) ? row.items : [];
  const unscheduledItems = Array.isArray(row?.unscheduledItems) ? row.unscheduledItems : [];
  if (mode === 'shortfall') return uniqueCapacityItems(periodItems.length ? periodItems : rowItems).filter(Boolean);
  if (mode === 'signoff') return uniqueCapacityItems(rowItems.length ? rowItems : unscheduledItems).filter(Boolean);
  if (mode === 'unscheduled') return uniqueCapacityItems(unscheduledItems).filter(Boolean);
  return uniqueCapacityItems([...periodItems, ...rowItems, ...unscheduledItems]).filter(Boolean);
}

function capacityConcernDriverSummary(items = []) {
  const labels = uniqueList((items || []).map(item => item?.label || item?.risk || item?.jcn));
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1} more driver${labels.length === 2 ? '' : 's'}`;
}

function capacityMetricValue(primary, fallback = null) {
  return primary === null || primary === undefined ? fallback : primary;
}

function capacityConcernItemCount(concern) {
  const explicitCount = Number(concern?.itemCount || 0);
  if (explicitCount > 0) return explicitCount;
  return Array.isArray(concern?.items) && concern.items.length ? concern.items.length : 1;
}

function capacityConcernInputGapStats(concerns = []) {
  const activeInputGaps = (concerns || []).filter(concern => !concern.reviewed && concern.type === 'planning');
  return activeInputGaps.reduce((stats, concern) => {
    const count = capacityConcernItemCount(concern);
    const labelText = [
      concern.label,
      ...(concern.items || []).map(item => item?.risk),
    ].filter(Boolean).join(' ').toLowerCase();
    const fullText = [
      concern.label,
      concern.title,
      concern.action,
      ...(concern.items || []).flatMap(item => [item?.risk, item?.label]),
    ].filter(Boolean).join(' ').toLowerCase();
    const isLaborGap = /labor estimate missing|missing labor|labor input/.test(labelText);
    const isTimingGap = /planning window|capacity window|planned date|planned start|planned finish|date|timing/.test(fullText);

    stats.concerns += 1;
    stats.tasks += count;
    if (isLaborGap) stats.laborTasks += count;
    else if (isTimingGap) stats.timingTasks += count;
    else stats.otherTasks += count;
    return stats;
  }, {
    concerns: 0,
    tasks: 0,
    laborTasks: 0,
    timingTasks: 0,
    otherTasks: 0,
  });
}

function capacityConcernTails(row, items = []) {
  return uniqueList([
    ...(row?.tails || []),
    ...items.map(item => item.tail || item.serialNumber),
  ]);
}

function earliestCapacityConcernDate(items = []) {
  const timestamps = items
    .flatMap(item => [item?.plannedStartAt, item?.plannedFinishAt])
    .map(parseOptionalMs)
    .filter(Number.isFinite);
  if (!timestamps.length) return null;
  return new Date(Math.min(...timestamps)).toISOString();
}

function planningConcernAction(riskText) {
  if (/labor/i.test(riskText)) return 'Enter labor estimate in planning row';
  if (/start|finish|date/i.test(riskText)) return 'Add planned start and finish dates';
  if (/signoff/i.test(riskText)) return 'Assign 7/9 signoff coverage';
  return 'Review planning input';
}

function buildCapacityConcerns(rows = [], planningRisks = []) {
  const concerns = [];
  (rows || []).forEach(row => {
    const peak = capacityConcernPeriod(row);
    const location = row.location || 'Unknown location';
    const mds = row.mds || 'Unspecified MDS';
    const shop = row.shopName || 'Unassigned shop';
    if (Number(row.shortfallHours || 0) > 0) {
      const items = capacityConcernItems(row, peak, 'shortfall');
      const tails = capacityConcernTails(row, items);
      const driverSummary = capacityConcernDriverSummary(items);
      const shortfallHours = capacityMetricValue(peak?.shortfallHours, row.shortfallHours);
      concerns.push({
        id: `shortfall-${location}-${mds}-${shop}`,
        type: 'shortfall',
        tone: Number(row.peakShortfallHours || shortfallHours || 0) >= 40 ? 'critical' : 'warning',
        label: 'Capacity Shortfall',
        title: `${shop} short ${fmtHours(shortfallHours || row.peakShortfallHours || row.shortfallHours)} in ${peak?.label || 'peak month'}${driverSummary ? ` from ${driverSummary}` : ''}`,
        context: `${location} | ${mds}`,
        month: peak?.label || '--',
        demand: capacityMetricValue(peak?.workHours, row.workHours),
        capacity: capacityMetricValue(peak?.capacityHours, row.capacityHours),
        shortfall: shortfallHours,
        peakUtilization: capacityMetricValue(peak?.utilizationPercent, row.peakUtilizationPercent),
        tails,
        tailCount: tails.length,
        items,
        itemCount: items.length,
        supportEligible: row.supportEligible || [],
        action: (row.supportEligible || []).length ? 'Evaluate listed support candidates' : 'Rephase work or add capacity',
        sortScore: 4000 + Number(row.shortfallHours || 0) + Number(row.peakUtilizationPercent || 0),
      });
    }
    if (row.signoffRisk) {
      const items = capacityConcernItems(row, peak, 'signoff');
      const tails = capacityConcernTails(row, items);
      const driverSummary = capacityConcernDriverSummary(items);
      concerns.push({
        id: `signoff-${location}-${mds}-${shop}`,
        type: 'signoff',
        tone: 'critical',
        label: '7/9 Signoff Risk',
        title: `${shop} has planned ${mds} demand with no 7/9 signoff coverage${driverSummary ? ` for ${driverSummary}` : ''}`,
        context: location,
        month: peak?.label || '--',
        demand: row.workHours,
        capacity: null,
        shortfall: null,
        peakUtilization: null,
        tails,
        tailCount: tails.length,
        items,
        itemCount: items.length,
        supportEligible: row.supportEligible || [],
        action: 'Assign 7/9 signoff coverage',
        sortScore: 3500 + Number(row.workHours || 0),
      });
    }
    if (Number(row.unscheduledHours || 0) > 0) {
      const items = capacityConcernItems(row, peak, 'unscheduled');
      const tails = capacityConcernTails({}, items);
      const driverSummary = capacityConcernDriverSummary(items);
      concerns.push({
        id: `unscheduled-${location}-${mds}-${shop}`,
        type: 'planning',
        tone: 'warning',
        label: 'Planning Window Risk',
        title: `${shop} has ${fmtHours(row.unscheduledHours)} waiting on planning inputs before capacity can be calculated${driverSummary ? ` due to ${driverSummary}` : ''}`,
        context: `${location} | ${mds}`,
        month: '--',
        demand: row.unscheduledHours,
        capacity: null,
        shortfall: null,
        peakUtilization: null,
        tails,
        tailCount: tails.length,
        items,
        itemCount: items.length,
        supportEligible: row.supportEligible || [],
        action: 'Add planning dates so labor timing can be calculated',
        sortScore: 2500 + Number(row.unscheduledHours || 0),
      });
    }
  });

  const planningGroups = new Map();
  (planningRisks || []).forEach((risk, index) => {
    const riskText = risk.risk || 'Planning input missing';
    if (/signoff/i.test(riskText) && risk.type === 'capacity') return;
    const tailOrScope = risk.tail || risk.serialNumber || 'site-level';
    const key = [
      riskText,
      risk.location || 'Unknown location',
      risk.mds || 'Unspecified MDS',
      tailOrScope,
    ].join('|');
    if (!planningGroups.has(key)) {
      planningGroups.set(key, {
        riskText,
        tailOrScope,
        location: risk.location || 'Unknown location',
        mds: risk.mds || 'Unspecified MDS',
        items: [],
        firstIndex: index,
      });
    }
    planningGroups.get(key).items.push(risk);
  });

  planningGroups.forEach(group => {
    const tails = capacityConcernTails({}, group.items);
    const titleScope = group.tailOrScope === 'site-level' ? group.location : group.tailOrScope;
    concerns.push({
      id: `planning-${group.location}-${group.mds}-${group.tailOrScope}-${group.riskText}`,
      type: 'planning',
      tone: /signoff|labor/i.test(group.riskText) ? 'critical' : 'warning',
      label: group.riskText,
      title: `${group.riskText}: ${group.items.length} item${group.items.length === 1 ? '' : 's'} on ${titleScope}`,
      context: [group.location, group.mds].filter(Boolean).join(' | ') || 'Planning input',
      month: fmtDate(earliestCapacityConcernDate(group.items)),
      demand: null,
      capacity: null,
      shortfall: null,
      peakUtilization: null,
      tails,
      tailCount: tails.length,
      items: group.items,
      itemCount: group.items.length,
      supportEligible: [],
      action: planningConcernAction(group.riskText),
      sortScore: (/signoff/i.test(group.riskText) ? 3200 : /labor/i.test(group.riskText) ? 3000 : 2200) + Math.min(group.items.length, 50),
    });
  });

  return concerns.sort((left, right) => right.sortScore - left.sortScore || left.title.localeCompare(right.title));
}

function laborRiskReasons(assessment) {
  return (assessment?.reasons || [])
    .filter(reason => /labor|capacity|short|signoff|utilization|planned|estimate|input|return|open x|symbol x|supply|part|TBD|tail-swap|schedule/i.test(reason))
    .slice(0, 3);
}

function scheduleWarnings(forecast) {
  return (forecast?.reasons || []).filter(reason => reason.type === 'schedule_logic');
}

function readinessTone(ready, assigned) {
  if (!assigned) return 'unknown';
  const ratio = ready / assigned;
  if (ratio >= 0.8) return 'green';
  if (ratio >= 0.6) return 'amber';
  return 'red';
}

function aircraftStatusTone(status) {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'CANN') return 'cann';
  if (normalized.startsWith('FMC') || normalized === 'GAR') return 'mc';
  if (normalized.startsWith('P')) return 'p';
  if (normalized.startsWith('NMC')) return 'nmc';
  return 'neutral';
}

function CurrentStatusPill({ status }) {
  const label = status || 'Unknown';
  return <span className={`crystal-ball__status-pill is-${aircraftStatusTone(label)}`}>{label}</span>;
}

function isLaborAssessmentAnswer(answer, question) {
  return answer?.assessmentType === 'labor' || String(question || '').trim() === LABOR_ASSESSMENT_QUESTION;
}

function CrystalBallAnswer({ answer, question, refreshing = false }) {
  const laborAssessment = isLaborAssessmentAnswer(answer, question);
  const primarySections = laborAssessment
    ? [
      ['Recommended Actions', answer.recommendedActions, Wrench],
      ['Missing Inputs', answer.missing, AlertTriangle],
    ]
    : [
      ['Recommended Actions', answer.recommendedActions, Wrench],
      ['Missing', answer.missing, AlertTriangle],
    ];
  const detailSections = laborAssessment
    ? [
      ['Evidence', answer.confirmed, Check],
      ['Inference', answer.inferred, Bot],
    ]
    : [
      ['Confirmed', answer.confirmed, Check],
      ['Inferred', answer.inferred, Bot],
    ];

  return (
    <article className={`crystal-ball__answer${refreshing ? ' is-refreshing' : ''}`}>
      {laborAssessment ? <span className="crystal-ball__answer-label">Leadership Labor Assessment</span> : null}
      <h3>{answer.answer}</h3>
      {primarySections.map(([label, items, Icon]) => items?.length ? (
        <section key={label} className="crystal-ball__answer-priority">
          <h4><Icon size={16} />{label}</h4>
          <ul>{items.map(item => <li key={item}>{item}</li>)}</ul>
        </section>
      ) : null)}
      {detailSections.some(([_label, items]) => items?.length) ? (
        <details className="crystal-ball__answer-details">
          <summary>Evidence Details</summary>
          {detailSections.map(([label, items, Icon]) => items?.length ? (
            <section key={label}>
              <h4><Icon size={16} />{label}</h4>
              <ul>{items.map(item => <li key={item}>{item}</li>)}</ul>
            </section>
          ) : null)}
        </details>
      ) : null}
    </article>
  );
}

function readinessPeriodLabel(period) {
  return period?.label || fmtDate(period?.date);
}

function percentBetween(valueMs, startMs, spanMs) {
  if (!Number.isFinite(valueMs)) return null;
  return Math.max(0, Math.min(100, ((valueMs - startMs) / spanMs) * 100));
}

const DAY_MS = 86400000;

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, count) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function startOfWeek(date) {
  return addDays(startOfDay(date), -date.getDay());
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function nextTimelineBoundary(date, grain) {
  if (grain === 'day') return addDays(date, 1);
  if (grain === 'week') return addDays(date, 7);
  if (grain === 'quarter') return addMonths(date, 3);
  return addMonths(date, 1);
}

function firstTimelineBoundary(startDate, grain) {
  let boundary = startOfMonth(startDate);
  if (grain === 'day') boundary = startOfDay(startDate);
  if (grain === 'week') boundary = startOfWeek(startDate);
  if (grain === 'quarter') boundary = startOfQuarter(startDate);
  while (boundary.getTime() < startDate.getTime()) {
    boundary = nextTimelineBoundary(boundary, grain);
  }
  return boundary;
}

function timelineDisplayEndMs(startMs, endMs, grain) {
  const rawEndDate = startOfDay(new Date(Math.max(endMs, startMs + DAY_MS)));
  let boundary = firstTimelineBoundary(rawEndDate, grain);
  if (boundary.getTime() <= rawEndDate.getTime()) {
    boundary = nextTimelineBoundary(boundary, grain);
  }
  return Math.max(boundary.getTime(), startMs + DAY_MS);
}

function timelineTickLabel(date, grain) {
  if (grain === 'month') {
    return date.toLocaleDateString([], { month: 'short' });
  }
  if (grain === 'quarter') {
    return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.toLocaleDateString([], { year: '2-digit' })}`;
  }
  return fmtDate(date.toISOString());
}

function timelineGridStep(startMs, endMs, grain) {
  const spanDays = Math.max(1, Math.ceil((endMs - startMs) / DAY_MS));
  if (grain === 'day') return Math.max(1, Math.ceil(spanDays / 28));
  if (grain === 'week') return Math.max(1, Math.ceil((spanDays / 7) / 36));
  if (grain === 'month') return Math.max(1, Math.ceil((spanDays / 31) / 30));
  if (grain === 'quarter') return Math.max(1, Math.ceil((spanDays / 92) / 20));
  return 1;
}

function addTimelineGridStep(date, grain, step) {
  if (grain === 'day') return addDays(date, step);
  if (grain === 'week') return addDays(date, step * 7);
  if (grain === 'quarter') return addMonths(date, step * 3);
  return addMonths(date, step);
}

function timelineCellLabel(cellStart, rangeStart, grain) {
  if (cellStart.getTime() === rangeStart.getTime()) {
    return grain === 'month' || grain === 'quarter' ? timelineTickLabel(cellStart, grain) : fmtDate(cellStart.toISOString());
  }
  return timelineTickLabel(cellStart, grain);
}

function timelineTicks(startMs, endMs, grain = 'month') {
  const span = Math.max(DAY_MS, endMs - startMs);
  const startDate = startOfDay(new Date(startMs));
  const endDate = new Date(endMs);
  const step = timelineGridStep(startMs, endMs, grain);
  const cells = [];
  let cellStart = startDate;
  let boundary = firstTimelineBoundary(startDate, grain);
  if (boundary.getTime() <= cellStart.getTime()) {
    boundary = addTimelineGridStep(boundary, grain, step);
  }
  const maxIterations = 400;
  for (let index = 0; cellStart.getTime() < endDate.getTime() && index < maxIterations; index += 1) {
    const cellEnd = new Date(Math.min(boundary.getTime(), endDate.getTime()));
    const left = percentBetween(cellStart.getTime(), startMs, span);
    const finish = percentBetween(cellEnd.getTime(), startMs, span);
    if (left !== null && finish !== null && finish > left) {
      cells.push({
        id: `${grain}-${index}-${cellStart.getTime()}`,
        left: `${left}%`,
        width: `${finish - left}%`,
        label: timelineCellLabel(cellStart, startDate, grain),
      });
    }
    cellStart = cellEnd;
    boundary = addTimelineGridStep(boundary, grain, step);
  }
  if (!cells.length) {
    return [{
      id: `${grain}-0-${startDate.getTime()}`,
      left: '0%',
      width: '100%',
      label: timelineCellLabel(startDate, startDate, grain),
    }];
  }
  return cells
    .filter((cell, index) => {
      if (grain !== 'quarter') return true;
      return cells.findIndex(item => item.label === cell.label) === index;
    })
    .map(cell => ({
      ...cell,
      lineLeft: cell.left,
    }));
}

function normalizedHsOs(value) {
  return String(value || 'HS').trim().toUpperCase() === 'OS' ? 'OS' : 'HS';
}

function filterAircraftScope(rows, scopeFilter, mdsFilter, getAircraft = row => row) {
  return (rows || []).filter((row) => {
    const aircraft = getAircraft(row) || {};
    if (scopeFilter === 'hs' && normalizedHsOs(aircraft.hsOs) !== 'HS') return false;
    if (scopeFilter === 'os' && normalizedHsOs(aircraft.hsOs) !== 'OS') return false;
    if (mdsFilter !== 'all' && (aircraft.mds || 'Unspecified') !== mdsFilter) return false;
    return true;
  });
}

function summarizeStatusReportTails(tails) {
  return (tails || []).reduce((summary, tailRow) => {
    const counts = tailRow.counts || {};
    const openX = Number(counts.openX || 0);
    summary.tails += 1;
    summary.openX += openX;
    summary.overdue9188 += Number(counts.overdue9188 || 0);
    summary.red9188 += Number(counts.red9188 || 0);
    summary.watch9188 += Number(counts.watch9188 || 0);
    summary.waived9188 += Number(counts.waived9188 || 0);
    summary.waiverCount += Number(counts.waiverCount ?? counts.waived9188 ?? 0);
    summary.missingDueStart += Number(counts.missingDueStart || 0);
    summary.form781K += Number(counts.form781K || 0);
    if (openX > 0) summary.tailsWithOpenX += 1;
    return summary;
  }, {
    tails: 0,
    tailsWithOpenX: 0,
    openX: 0,
    overdue9188: 0,
    red9188: 0,
    watch9188: 0,
    waived9188: 0,
    waiverCount: 0,
    missingDueStart: 0,
    form781K: 0,
  });
}

function waived9188Count(row) {
  const counts = row?.counts || {};
  if (counts.waived9188 !== undefined && counts.waived9188 !== null) return Number(counts.waived9188 || 0);
  if (Array.isArray(row?.waived9188)) return row.waived9188.length;
  return ['overdue9188', 'red9188', 'watch9188'].reduce((total, key) => (
    total + ((row?.[key] || []).filter(item => item.waiver).length)
  ), 0);
}

function distinctWaiverCount(row) {
  return Number(row?.counts?.waiverCount ?? waived9188Count(row));
}

function activeOrPendingWaiverCount(row) {
  return Number(row?.counts?.linkedWaiverCount ?? distinctWaiverCount(row));
}

function tailWaiverCoverageLabel(row, includePending = false) {
  if (includePending) return waiverCoverageLabel(activeOrPendingWaiverCount(row), Number(row?.counts?.linkedWaiverTaskCount ?? waived9188Count(row)));
  return waiverCoverageLabel(distinctWaiverCount(row), waived9188Count(row));
}

function compareG081TailPriority(left, right) {
  const leftCounts = left?.counts || {};
  const rightCounts = right?.counts || {};
  const keys = ['overdue9188', 'red9188', 'watch9188', 'waived9188', 'openX', 'missingDueStart', 'form781K'];
  for (const key of keys) {
    const leftValue = key === 'waived9188' ? waived9188Count(left) : Number(leftCounts[key] || 0);
    const rightValue = key === 'waived9188' ? waived9188Count(right) : Number(rightCounts[key] || 0);
    if (leftValue !== rightValue) return rightValue - leftValue;
  }
  return String(left?.aircraft?.tail || left?.tail || '').localeCompare(String(right?.aircraft?.tail || right?.tail || ''), undefined, { numeric: true });
}

function rtgMdsLabel(row) {
  return String(row?.mds || '').trim() || 'MDS Unknown';
}

function rtgLocationLabel(row) {
  return String(row?.location || row?.aircraft?.location || '').trim() || 'Location Unknown';
}

function summarizeRtgTimelineGroup(rows) {
  return rows.reduce((summary, row) => {
    if (missingInputCount(row.forecast)) summary.inputNeeds += 1;
    if (!row.forecast?.authorizedReadyNow) summary.notReady += 1;
    const readyMs = parseOptionalMs(row.forecast?.projectedReadyAt);
    if (Number.isFinite(readyMs)) {
      summary.latestReadyMs = summary.latestReadyMs === null ? readyMs : Math.max(summary.latestReadyMs, readyMs);
    }
    return summary;
  }, {
    inputNeeds: 0,
    notReady: 0,
    latestReadyMs: null,
  });
}

function groupRtgTimelineRowsByLocationMds(rows = []) {
  const groups = new Map();
  rows.forEach(row => {
    const locationLabel = rtgLocationLabel(row);
    const mdsLabel = rtgMdsLabel(row);
    const id = `${locationLabel}::${mdsLabel}`;
    if (!groups.has(id)) {
      groups.set(id, {
        id,
        label: locationLabel,
        locationLabel,
        mdsLabel,
        rows: [],
      });
    }
    groups.get(id).rows.push(row);
  });
  return [...groups.values()]
    .map(group => ({
      ...group,
      ...summarizeRtgTimelineGroup(group.rows),
      rows: [...group.rows].sort((left, right) => {
        const firstGreen = row => Math.min(
          ...(row.readyWindows || []).map(window => window.startMs).filter(Number.isFinite),
          Infinity,
        );
        const leftGreen = firstGreen(left);
        const rightGreen = firstGreen(right);
        if (leftGreen !== rightGreen) return leftGreen - rightGreen;
        return String(left.tail || '').localeCompare(String(right.tail || ''), undefined, { numeric: true });
      }),
    }))
    .sort((left, right) => (
      left.locationLabel.localeCompare(right.locationLabel, undefined, { numeric: true })
      || (left.mdsLabel === 'MDS Unknown') - (right.mdsLabel === 'MDS Unknown')
      || left.mdsLabel.localeCompare(right.mdsLabel, undefined, { numeric: true })
    ));
}

function flattenStatusReportTailItems(tails, key) {
  return (tails || []).flatMap((tailRow) => {
    const aircraft = tailRow.aircraft || {};
    return (tailRow[key] || []).map(item => ({
      ...item,
      aircraftId: aircraft.id || '',
      tail: aircraft.tail || '--',
      mds: aircraft.mds || '--',
      status: aircraft.status || '--',
      tailLocation: aircraft.location || '',
      hsOs: aircraft.hsOs || '',
      etic: aircraft.etic || null,
    }));
  });
}

function compareStatusReportTailGroups(left, right) {
  const leftOverdue = Number(left?.counts?.overdue9188 || left?.overdueCount || 0);
  const rightOverdue = Number(right?.counts?.overdue9188 || right?.overdueCount || 0);
  if (leftOverdue !== rightOverdue) return rightOverdue - leftOverdue;
  const leftItems = Number(left?.items?.length || 0);
  const rightItems = Number(right?.items?.length || 0);
  if (leftItems !== rightItems) return rightItems - leftItems;
  const leftLocation = String(left?.aircraft?.location || left?.tailLocation || '');
  const rightLocation = String(right?.aircraft?.location || right?.tailLocation || '');
  if (leftLocation !== rightLocation) return leftLocation.localeCompare(rightLocation, undefined, { numeric: true });
  return String(left?.aircraft?.tail || left?.tail || '').localeCompare(
    String(right?.aircraft?.tail || right?.tail || ''),
    undefined,
    { numeric: true }
  );
}

function buildStatusReportTailItemGroups(tails, keys) {
  const itemKeys = Array.isArray(keys) ? keys : [keys];
  return (tails || [])
    .map((tailRow) => {
      const aircraft = tailRow.aircraft || {};
      const items = itemKeys.flatMap((key) => (tailRow[key] || []).map(item => ({
        ...item,
        aircraftId: aircraft.id || '',
        tail: aircraft.tail || '--',
        mds: aircraft.mds || '--',
        status: aircraft.status || '--',
        tailLocation: aircraft.location || '',
        hsOs: aircraft.hsOs || '',
        etic: aircraft.etic || null,
        groupKey: key,
      })));
      return {
        id: aircraft.id || `${aircraft.location || 'location'}-${aircraft.tail || itemKeys.join('-')}`,
        aircraft,
        counts: tailRow.counts || {},
        overdueCount: Number(tailRow.counts?.overdue9188 ?? tailRow.overdue9188?.length ?? 0),
        items,
      };
    })
    .filter(group => group.items.length > 0)
    .sort(compareStatusReportTailGroups);
}

function StatusReportTailGroupHeader({ group, colSpan, label }) {
  const aircraft = group.aircraft || {};
  const itemCount = group.items?.length || 0;
  const overdueCount = Number(group.overdueCount || 0);
  return (
    <tr className="reports-status-tail-group-row">
      <td colSpan={colSpan}>
        <div className="reports-status-tail-group">
          <span className="reports-status-tail-group__identity">
            <strong>{aircraft.tail || '--'}</strong>
            <span>{aircraft.mds || '--'} | <AircraftStationDisplay aircraft={aircraft} /></span>
          </span>
          <span className="reports-status-tail-group__count">
            {itemCount} {label}{itemCount === 1 ? '' : 's'}{overdueCount ? ` | ${overdueCount} overdue` : ''}
          </span>
        </div>
      </td>
    </tr>
  );
}

function summarizeMissingDueStartByLocation(rows) {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const key = `${row.tailLocation || 'Unknown'}|${row.mds || 'Unspecified'}`;
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        location: row.tailLocation || 'Unknown',
        mds: row.mds || 'Unspecified',
        scheduledTasks: 0,
        f8035Jobs: 0,
        readinessDrivers: 0,
        tails: new Set(),
      });
    }
    const group = groups.get(key);
    group.tails.add(row.tail);
    if (row.itemType === '8035 Job') group.f8035Jobs += 1;
    else group.scheduledTasks += 1;
    if (row.affectsReadiness) group.readinessDrivers += 1;
  });
  return Array.from(groups.values())
    .map(group => ({
      ...group,
      tailCount: group.tails.size,
      total: group.scheduledTasks + group.f8035Jobs,
      tails: Array.from(group.tails).sort((left, right) => String(left).localeCompare(String(right), undefined, { numeric: true })),
    }))
    .sort((left, right) => {
      if (left.total !== right.total) return right.total - left.total;
      return `${left.location} ${left.mds}`.localeCompare(`${right.location} ${right.mds}`);
    });
}

function summarizeMissingDueStartByTail(rows) {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const key = `${row.aircraftId || row.tail || 'unknown-tail'}`;
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        aircraftId: row.aircraftId || '',
        tail: row.tail || '--',
        mds: row.mds || '--',
        status: row.status || '',
        tailLocation: row.tailLocation || '',
        hsOs: row.hsOs || '',
        scheduledTasks: 0,
        f8035Jobs: 0,
        readinessDrivers: 0,
        items: [],
      });
    }
    const group = groups.get(key);
    if (row.itemType === '8035 Job') group.f8035Jobs += 1;
    else group.scheduledTasks += 1;
    if (row.affectsReadiness) group.readinessDrivers += 1;
    group.items.push(row);
  });
  return Array.from(groups.values())
    .map(group => ({
      ...group,
      total: group.scheduledTasks + group.f8035Jobs,
      otherItems: (group.scheduledTasks + group.f8035Jobs) - group.readinessDrivers,
      items: group.items.sort((left, right) => {
        if (Boolean(left.affectsReadiness) !== Boolean(right.affectsReadiness)) return left.affectsReadiness ? -1 : 1;
        return String(left.discrepancy || left.noun || left.jcn || left.wuc || '').localeCompare(String(right.discrepancy || right.noun || right.jcn || right.wuc || ''), undefined, { numeric: true });
      }),
    }))
    .sort((left, right) => {
      if (left.total !== right.total) return right.total - left.total;
      const leftHasReadinessDriver = left.readinessDrivers > 0;
      const rightHasReadinessDriver = right.readinessDrivers > 0;
      if (leftHasReadinessDriver !== rightHasReadinessDriver) return leftHasReadinessDriver ? -1 : 1;
      return String(left.tail || '').localeCompare(String(right.tail || ''), undefined, { numeric: true });
    });
}

function summarizeCapacityRowsByLocation(rows, periods = []) {
  const map = new Map();
  (rows || []).forEach(row => {
    const location = row.location || 'Unknown';
    if (!map.has(location)) {
      map.set(location, {
        location,
        mds: new Set(),
        workHours: 0,
        capacityHours: 0,
        shortfallHours: 0,
        spareHours: 0,
        peakUtilizationPercent: 0,
        overtaxedCells: 0,
        extraCapacityCells: 0,
        fullTimePeople: 0,
        productivePeople: 0,
        signoffQualifiedPeople: 0,
        signoffRiskCells: 0,
        periods: periods.map(period => ({ id: period.id, label: period.label, workHours: 0, capacityHours: 0, shortfallHours: 0, spareHours: 0 })),
      });
    }
    const summary = map.get(location);
    if (row.mds) summary.mds.add(row.mds);
    summary.workHours += Number(row.workHours || 0);
    summary.capacityHours += Number(row.capacityHours || 0);
    summary.shortfallHours += Number(row.shortfallHours || 0);
    summary.spareHours += Number(row.spareHours || 0);
    summary.peakUtilizationPercent = Math.max(summary.peakUtilizationPercent, Number(row.peakUtilizationPercent || 0));
    if (Number(row.shortfallHours || 0) > 0) summary.overtaxedCells += 1;
    if (Number(row.spareHours || 0) > 0) summary.extraCapacityCells += 1;
    if (row.signoffRisk) summary.signoffRiskCells += 1;
    summary.fullTimePeople += Number(row.fullTimePeople || 0);
    summary.productivePeople += Number(row.productivePeople || 0);
    summary.signoffQualifiedPeople += Number(row.signoffQualifiedPeople || 0);
    (row.periods || []).forEach(period => {
      const target = summary.periods.find(item => item.id === period.id);
      if (!target) return;
      target.workHours += Number(period.workHours || 0);
      target.capacityHours += Number(period.capacityHours || 0);
      target.shortfallHours += Number(period.shortfallHours || 0);
      target.spareHours += Number(period.spareHours || 0);
    });
  });
  return [...map.values()].map(row => ({
    ...row,
    mds: [...row.mds].sort(),
    workHours: Math.round(row.workHours * 10) / 10,
    capacityHours: Math.round(row.capacityHours * 10) / 10,
    shortfallHours: Math.round(row.shortfallHours * 10) / 10,
    spareHours: Math.round(row.spareHours * 10) / 10,
    periods: row.periods.map(period => ({
      ...period,
      workHours: Math.round(period.workHours * 10) / 10,
      capacityHours: Math.round(period.capacityHours * 10) / 10,
      shortfallHours: Math.round(period.shortfallHours * 10) / 10,
      spareHours: Math.round(period.spareHours * 10) / 10,
    })),
  })).sort((left, right) => {
    if ((left.shortfallHours > 0) !== (right.shortfallHours > 0)) return left.shortfallHours > 0 ? -1 : 1;
    return left.location.localeCompare(right.location);
  });
}

function capacityPeakPeriod(row, metric = 'shortfallHours') {
  return (row.periods || []).reduce((best, period) => (
    Number(period[metric] || 0) > Number(best?.[metric] || 0) ? period : best
  ), null);
}

function tailMatchesCapacityItem(item, tail) {
  const tailValue = String(tail?.tail || '').trim();
  const serialValue = String(tail?.serialNumber || '').trim();
  const itemTail = String(item?.tail || '').trim();
  const itemSerial = String(item?.serialNumber || '').trim();
  return Boolean(
    (tailValue && itemTail && itemTail === tailValue)
    || (serialValue && itemSerial && itemSerial === serialValue)
  );
}

function tailCapacityRows(tail, capacityRows = []) {
  return (capacityRows || [])
    .map(row => {
      const matchingPeriods = (row.periods || []).filter(period => (
        (period.items || []).some(item => tailMatchesCapacityItem(item, tail))
      ));
      const matchingItems = [
        ...(row.items || []).filter(item => tailMatchesCapacityItem(item, tail)),
        ...(row.unscheduledItems || []).filter(item => tailMatchesCapacityItem(item, tail)),
      ];
      if (!matchingPeriods.length && !matchingItems.length) return null;
      return { ...row, matchingPeriods, matchingItems };
    })
    .filter(Boolean);
}

function tailPlanningRisks(tail, planningRisks = []) {
  return (planningRisks || []).filter(risk => tailMatchesCapacityItem(risk, tail));
}

function confidenceBandForPercent(percent) {
  if (percent >= 90) return { level: 'supported', label: 'Fully supported' };
  if (percent >= 75) return { level: 'high', label: 'High confidence' };
  if (percent >= 60) return { level: 'medium', label: 'Moderate confidence' };
  if (percent >= 45) return { level: 'low', label: 'Low confidence' };
  return { level: 'unsupported', label: 'Planning unsupported' };
}

function confidenceInputPenalty(text) {
  const value = String(text || '');
  if (/offstation estimated return date/i.test(value)) {
    return { points: 25, cap: 35, reason: 'Offstation return date is missing, so the recovery start gate is not supportable.' };
  }
  if (/open F8035 Symbol X|8035 X|Symbol X/i.test(value) && /planned finish|ETIC|Status/i.test(value)) {
    return { points: 24, cap: 40, reason: 'Open X condition needs a planned clear date before the projected ready date is supportable.' };
  }
  if (/Return to Green work plan/i.test(value)) {
    return { points: 22, cap: 45, reason: 'Return-to-Green work plan is missing.' };
  }
  if (/Current Status ETIC is expired/i.test(value)) {
    return { points: 18, cap: 50, reason: 'Current Status ETIC is expired.' };
  }
  if (/planned finish|ETIC/i.test(value)) {
    return { points: 14, reason: value };
  }
  if (/labor estimate|duration estimate/i.test(value)) {
    return { points: 12, reason: value };
  }
  if (/planned start/i.test(value)) {
    return { points: 10, reason: value };
  }
  if (/supply|part|constraint/i.test(value)) {
    return { points: 15, reason: value };
  }
  return { points: 6, reason: value };
}

function confidencePlanningRiskPenalty(risk) {
  const label = `${risk?.risk || 'Planning input missing'}${risk?.label ? `: ${truncateText(risk.label, 72)}` : ''}`;
  if (/labor/i.test(risk?.risk)) return { points: 12, reason: label };
  if (/finish|ETIC/i.test(risk?.risk)) return { points: 14, reason: label };
  if (/start/i.test(risk?.risk)) return { points: 10, reason: label };
  return { points: 8, reason: label };
}

function confidenceAssessmentFromScore(score, issues, fallbackReason) {
  const percent = Math.max(0, Math.min(95, Math.round(score)));
  const band = confidenceBandForPercent(percent);
  const sortedIssues = [...issues]
    .filter(item => item.reason)
    .sort((left, right) => Number(right.points || 0) - Number(left.points || 0));
  const reasons = sortedIssues.length
    ? sortedIssues.map(item => item.reason).slice(0, 5)
    : [fallbackReason].filter(Boolean);
  return {
    ...band,
    percent,
    summary: reasons[0] || fallbackReason || band.label,
    reasons,
  };
}

function tailWorkloadRealism(tail, capacityRows = [], planningRisks = []) {
  const forecast = tail?.forecast || {};
  if (forecast.authorizedReadyNow) {
    return {
      level: 'ready',
      label: 'Ready now',
      percent: 100,
      summary: 'Current-state confidence: 100%. Aircraft is already assessed ready to fight.',
      reasons: ['Current-state confidence: aircraft is already assessed ready to fight.'],
    };
  }

  const relatedRows = tailCapacityRows(tail, capacityRows);
  const relatedRisks = tailPlanningRisks(tail, planningRisks);
  const missingInputs = Array.isArray(forecast.missingInputs) ? forecast.missingInputs : [];
  const scheduleReview = scheduleWarnings(forecast);
  const plannedWork = (forecast.reasons || []).filter(reason => ['inspection', '8035', 'customTask'].includes(reason.type));
  const issues = [];
  const caps = [];
  let score = 95;

  relatedRisks.forEach(risk => {
    const penalty = confidencePlanningRiskPenalty(risk);
    score -= penalty.points;
    issues.push(penalty);
  });

  missingInputs.forEach(input => {
    const penalty = confidenceInputPenalty(input);
    score -= penalty.points;
    issues.push(penalty);
    if (Number.isFinite(penalty.cap)) caps.push({ cap: penalty.cap, reason: penalty.reason });
  });

  scheduleReview.forEach(item => {
    const reason = item.message || item.label || 'Schedule logic review needed.';
    score -= 10;
    issues.push({ points: 10, reason });
  });

  relatedRows.forEach(row => {
    if (row.signoffRisk) {
      const reason = `${row.shopName || 'Shop'} ${row.mds || 'MDS'} has no 7/9-level signoff coverage.`;
      score -= 12;
      issues.push({ points: 12, reason });
    }
    (row.matchingPeriods || []).forEach(period => {
      const shortfall = Number(period.shortfallHours || 0);
      const utilization = Number(period.utilizationPercent || 0);
      const shop = row.shopName || 'Shop';
      const mds = row.mds || 'MDS';
      if (shortfall > 0) {
        const points = Math.min(25, 8 + Math.ceil(shortfall / 8) * 3);
        const reason = `${shop} ${mds} short ${fmtHours(shortfall)} in ${period.label}.`;
        score -= points;
        issues.push({ points, reason });
        return;
      }
      if (utilization >= 85) {
        const points = utilization >= 95 ? 12 : utilization >= 90 ? 8 : 5;
        const reason = `${shop} ${mds} utilization is ${utilization.toFixed(0)}% in ${period.label}.`;
        score -= points;
        issues.push({ points, reason });
      }
    });
  });

  if (!plannedWork.length && !forecast.projectedReadyAt) {
    const reason = 'No planned readiness-driver work supports the projected ready date.';
    score -= 28;
    issues.push({ points: 28, reason });
    caps.push({ cap: 35, reason });
  }
  if (!relatedRows.length && plannedWork.length) {
    const reason = 'Planned work exists, but no matching capacity lane was found from labor rows.';
    score -= 20;
    issues.push({ points: 20, reason });
    caps.push({ cap: 74, reason });
  }

  const projectedReadyMs = parseOptionalMs(forecast.projectedReadyAt);
  if (Number.isFinite(projectedReadyMs)) {
    const horizonDays = Math.ceil((projectedReadyMs - Date.now()) / DAY_MS);
    if (horizonDays > 90) {
      score -= 12;
      issues.push({ points: 12, reason: `Projected ready date is ${horizonDays} days out, so forecast uncertainty is higher.` });
    } else if (horizonDays > 60) {
      score -= 8;
      issues.push({ points: 8, reason: `Projected ready date is ${horizonDays} days out, so forecast uncertainty is moderate.` });
    } else if (horizonDays > 30) {
      score -= 5;
      issues.push({ points: 5, reason: `Projected ready date is ${horizonDays} days out.` });
    }
  } else if (plannedWork.length) {
    const reason = 'Projected ready date is missing even though readiness-driver work exists.';
    score -= 18;
    issues.push({ points: 18, reason });
    caps.push({ cap: 55, reason });
  }

  if (caps.length) {
    const mostRestrictive = caps.reduce((lowest, item) => (item.cap < lowest.cap ? item : lowest), caps[0]);
    if (score > mostRestrictive.cap) {
      score = mostRestrictive.cap;
      issues.push({ points: 30, reason: `Score capped at ${mostRestrictive.cap}%: ${mostRestrictive.reason}` });
    }
  }

  return confidenceAssessmentFromScore(
    score,
    issues,
    'Projected readiness is supported by current source data, planning dates, labor estimates, and capacity.'
  );
}

function WorkloadRealismPill({ assessment }) {
  const row = assessment || { level: 'unknown', label: 'Not assessed', percent: null, summary: '' };
  const title = [row.summary || row.label, CONFIDENCE_HELP_TEXT].filter(Boolean).join(' ');
  return (
    <span className={`crystal-ball__realism-pill is-${row.level}`} title={title}>
      <span>{row.label}{Number.isFinite(row.percent) ? ` (${row.percent}%)` : ''}</span>
      <span className="crystal-ball__realism-pill-help" aria-hidden="true">?</span>
      <span className="crystal-ball__realism-pill-tooltip" role="tooltip">{CONFIDENCE_HELP_TEXT}</span>
    </span>
  );
}

function LaborRiskChips({ assessment }) {
  const reasons = laborRiskReasons(assessment);
  if (!reasons.length) return null;
  return (
    <span className="crystal-ball__labor-risk-chips">
      {reasons.map(reason => (
        <small key={reason} title={reason}>{truncateText(reason, 54)}</small>
      ))}
    </span>
  );
}

function CapacityHelpHeader({ children, help }) {
  return (
    <span className="crystal-ball__capacity-help-header">
      <span>{children}</span>
      <button type="button" aria-label={help}>
        <CircleHelp size={13} />
        <span role="tooltip">{help}</span>
      </button>
    </span>
  );
}

function CapacityDriversModal({ concern, onClose, onTailClick }) {
  if (!concern) return null;
  const drivers = concern.items || [];
  return (
    <div
      className="crystal-ball__capacity-driver-modal-backdrop"
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="crystal-ball__capacity-driver-modal" role="dialog" aria-modal="true" aria-labelledby="capacity-driver-modal-title">
        <header>
          <div>
            <span>{concern.label}</span>
            <h3 id="capacity-driver-modal-title">{concern.title}</h3>
            <p>{concern.context} | {drivers.length} driver{drivers.length === 1 ? '' : 's'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close driver list">
            <X size={18} />
          </button>
        </header>
        <div className="crystal-ball__capacity-driver-modal-body">
          {drivers.length ? drivers.map((item, index) => {
            const tail = item.tail || item.serialNumber || '';
            const label = item.label || item.risk || item.jcn || 'Driver work';
            const detailLine = [item.location, item.mds, item.shopName || item.shop].filter(Boolean).join(' | ');
            const key = `${tail || 'scope'}-${item.jcn || item.id || label}-${index}`;
            return (
              <article key={key}>
                <div>
                  <strong>{tail || 'Scope'}</strong>
                  <span>{label}</span>
                  {detailLine ? <small>{detailLine}</small> : null}
                </div>
                <dl>
                  <div><dt>Hours</dt><dd>{item.hours ? fmtHours(item.hours) : '--'}</dd></div>
                  <div><dt>When</dt><dd>{capacityDriverDateLabel(item)}</dd></div>
                  <div><dt>JCN</dt><dd>{item.jcn || '--'}</dd></div>
                </dl>
                {tail ? (
                  <button type="button" onClick={() => onTailClick(tail)}>
                    Open Tail
                  </button>
                ) : null}
              </article>
            );
          }) : (
            <p>No driver work is attached to this concern.</p>
          )}
        </div>
        <footer>
          <button type="button" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

function CapacityConcernRow({ concern, rank, onTailClick, onReviewToggle, onDriverOverflow }) {
  const driverItems = concern.items || [];
  const hiddenDriverCount = Math.max(0, driverItems.length - CAPACITY_CONCERN_VISIBLE_DRIVER_COUNT);
  const support = concern.supportEligible || [];
  return (
    <tr className={`crystal-ball__capacity-concern-row is-${concern.tone}`}>
      <td>{rank}</td>
      <td>
        <span className="crystal-ball__capacity-risk-label">{concern.label}</span>
        <strong>{concern.title}</strong>
        <small>{concern.context}</small>
      </td>
      <td>{concern.month || '--'}</td>
      <td>
        <span>{concern.demand === null || concern.demand === undefined ? '--' : fmtHours(concern.demand)} demand</span>
        <span>{concern.capacity === null || concern.capacity === undefined ? '--' : fmtHours(concern.capacity)} capacity</span>
        <span className={Number(concern.shortfall || 0) > 0 ? 'is-capacity-risk' : ''}>{concern.shortfall === null || concern.shortfall === undefined ? '--' : fmtHours(concern.shortfall)} short</span>
        <span>{Number.isFinite(Number(concern.peakUtilization)) ? `${Number(concern.peakUtilization).toFixed(0)}% peak` : '-- peak'}</span>
      </td>
      <td>
        <div className="crystal-ball__capacity-tail-list">
          {concern.tails?.length ? concern.tails.slice(0, 6).map(tail => (
            <button key={tail} type="button" onClick={() => onTailClick(tail)}>{tail}</button>
          )) : <span>--</span>}
          {Number(concern.tailCount || 0) > 6 ? <span>+{concern.tailCount - 6}</span> : null}
        </div>
      </td>
      <td>
        {driverItems.length ? (
          <div className="crystal-ball__capacity-driver-list">
            {driverItems.slice(0, CAPACITY_CONCERN_VISIBLE_DRIVER_COUNT).map((item, index) => (
              <span key={`${item.tail || item.serialNumber || concern.id}-${item.jcn || index}`}>
                <strong>{item.tail || item.serialNumber || 'Scope'}</strong> {truncateText(item.label || item.risk || item.jcn, 88)}{item.hours ? ` (${fmtHours(item.hours)})` : ''}
              </span>
            ))}
            {hiddenDriverCount > 0 ? (
              <button type="button" className="crystal-ball__capacity-driver-more" onClick={() => onDriverOverflow(concern)}>
                +{hiddenDriverCount} more
              </button>
            ) : null}
          </div>
        ) : '--'}
      </td>
      <td>{support.length ? support.slice(0, 2).map(item => `${item.location} ${fmtHours(item.spareHours)} (${item.basis})`).join(' | ') : 'No candidate shown'}</td>
      <td>
        <span>
          {concern.reviewed
            ? `Reviewed by ${concern.reviewedBy || 'Unknown user'} on ${fmtReviewDate(concern.reviewedAt)}`
            : (concern.action || 'Review')}
        </span>
        <button type="button" className="crystal-ball__capacity-review-button" onClick={() => onReviewToggle(concern.id)}>
          {concern.reviewed ? 'Reopen' : 'Mark Reviewed'}
        </button>
      </td>
    </tr>
  );
}

function AircraftStationDisplay({ aircraft }) {
  const hsOs = normalizedHsOs(aircraft?.hsOs);
  if (hsOs === 'OS') {
    return <>OS | <OffstationLocationDisplay value={aircraft?.osLocation} emptyLabel="offstation location needed" /></>;
  }
  return <>HS | {aircraft?.location || 'homestation'}</>;
}

function readinessHorizonMs(period) {
  const parsed = Date.parse(`${period?.date || ''}T23:59:59Z`);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function forecastUnavailableAt(forecast, horizonMs, ignoreAlert = false) {
  return (forecast?.availabilityWindows || []).some(window => {
    if (window.state !== 'red') return false;
    if (ignoreAlert && window.kind === 'alert') return false;
    const startMs = parseOptionalMs(window.startAt);
    const endMs = parseOptionalMs(window.endAt);
    return Number.isFinite(startMs) && Number.isFinite(endMs) && startMs <= horizonMs && horizonMs < endMs;
  });
}

function forecastAvailableAtPeriod(forecast, period) {
  if (!forecast) return { ready: false, unknown: true };
  const horizonMs = readinessHorizonMs(period);
  const onAlert = forecastUnavailableAt({ availabilityWindows: (forecast.availabilityWindows || []).filter(window => window.kind === 'alert') }, horizonMs);
  const projectedMs = parseOptionalMs(forecast.projectedReadyAt);
  const baseReady = Boolean(forecast.authorizedReadyNow) || (Number.isFinite(projectedMs) && projectedMs <= horizonMs);
  if (!baseReady) {
    return { ready: false, unknown: !Number.isFinite(projectedMs) && !forecast.authorizedReadyNow, onAlert, alertDeduction: false };
  }
  const unavailable = forecastUnavailableAt(forecast, horizonMs);
  return {
    ready: !unavailable,
    unknown: false,
    onAlert,
    alertDeduction: unavailable && !forecastUnavailableAt(forecast, horizonMs, true),
  };
}

function readinessStateForPeriod(tail, period) {
  const forecast = tail?.forecast;
  if (!forecast) return { key: 'unknown', label: 'Unknown inputs' };
  const { ready, unknown, onAlert, alertDeduction } = forecastAvailableAtPeriod(forecast, period);
  if (onAlert) return {
    onAlert: true,
    key: unknown ? 'unknown' : 'not-ready',
    label: unknown ? 'Alert Status; maintenance forecast unresolved'
      : alertDeduction ? 'Alert Status' : 'Alert Status; also excluded by maintenance or availability forecast',
  };
  if (ready && forecast.readyUnderWaiver) return { key: 'waiver', label: 'Ready under waiver' };
  if (ready) return { key: 'ready', label: 'Available by period' };
  if (unknown) return { key: 'unknown', label: 'Unknown inputs' };
  return { key: 'not-ready', label: 'Not ready by period' };
}

function forecastDriverRank(tail) {
  const forecast = tail?.forecast || {};
  if (scheduleWarnings(forecast).length) return 0;
  if (!forecast.authorizedReadyNow && forecast.projectedReadyAt) return 1;
  if (forecast.readyUnderWaiver) return 2;
  if (forecast.missingInputs?.length) return 3;
  if (forecast.authorizedReadyNow) return 4;
  return 5;
}

function forecastDriverLabel(tail) {
  const forecast = tail?.forecast || {};
  if (scheduleWarnings(forecast).length) return 'Schedule review';
  if (forecast.readyUnderWaiver) return 'Waiver dependent';
  if (forecast.missingInputs?.length) return 'Missing inputs';
  if (forecast.authorizedReadyNow) return 'Ready now';
  return forecast.primaryDriver || 'Forecast not calculated';
}

function forecastReadyLabel(row) {
  if (!row?.forecast) return 'Forecast not calculated';
  if (row?.forecast?.authorizedReadyNow) return 'Ready now';
  return fmtDate(row?.forecast?.projectedReadyAt);
}

function tailTimingLabel(tail) {
  if (tail?.etic) return `Current ETIC: ${fmtDayMonthYear(tail.etic)}`;
  if (tail?.forecast?.authorizedReadyNow) return 'Projected ready: Ready now';
  return `Projected ready: ${fmtDayMonthYear(tail?.forecast?.projectedReadyAt)}`;
}

function forecastReasonKey(reason) {
  return [reason?.type, reason?.id || reason?.jcn || reason?.label || reason?.shop].filter(Boolean).join(':');
}

function forecastReasonLabel(reason) {
  if (!reason) return 'Readiness driver';
  if (reason.type === '8035') {
    return [reason.jcn, reason.label].filter(Boolean).join(' | ') || '8035 job';
  }
  if (reason.type === 'inspection') {
    return reason.label || '9188-6 baseline item';
  }
  if (reason.type === 'supply') {
    return `Supply ${reason.label || reason.partNumber || ''}`.trim();
  }
  return reason.label || reason.shop || reason.type || 'Readiness driver';
}

function forecastChangeDriver(row) {
  const latest = row?.latest || {};
  const prior = row?.prior || row?.baseline || {};
  const aircraft = row?.aircraft || {};
  const latestReasons = latest.reasons || [];
  const priorReasonsByKey = new Map((prior.reasons || []).map(reason => [forecastReasonKey(reason), reason]));
  const latestReadyMs = parseOptionalMs(latest.projectedReadyAt);
  const priorReadyMs = parseOptionalMs(prior.projectedReadyAt);

  if (/estimated return date missing/i.test(latest.primaryDriver || '') || latest.missingInputs?.some(item => /estimated return date/i.test(item))) {
    return 'Estimated return date is missing';
  }
  if (/return/i.test(latest.primaryDriver || '') && (aircraft.actualReturnAt || aircraft.estimatedReturnAt)) {
    return `Est Return now ${fmtDate(aircraft.actualReturnAt || aircraft.estimatedReturnAt, true)}`;
  }
  if (/status etic/i.test(latest.primaryDriver || '') && aircraft.etic) {
    return `Status ETIC now ${fmtDate(aircraft.etic, true)}`;
  }

  const plannedReasons = latestReasons
    .map(reason => ({
      reason,
      startMs: parseOptionalMs(reason.plannedStartAt),
      finishMs: parseOptionalMs(reason.plannedFinishAt),
      priorReason: priorReasonsByKey.get(forecastReasonKey(reason)),
    }))
    .filter(item => Number.isFinite(item.startMs) || Number.isFinite(item.finishMs))
    .sort((left, right) => (right.finishMs || right.startMs || 0) - (left.finishMs || left.startMs || 0));

  const movedPlannedItem = plannedReasons.find(item => {
    const previous = item.priorReason;
    const priorStart = parseOptionalMs(previous?.plannedStartAt);
    const priorFinish = parseOptionalMs(previous?.plannedFinishAt);
    return (
      Number.isFinite(item.startMs) && Number.isFinite(priorStart) && Math.abs(item.startMs - priorStart) >= 3600000
    ) || (
      Number.isFinite(item.finishMs) && Number.isFinite(priorFinish) && Math.abs(item.finishMs - priorFinish) >= 3600000
    );
  });
  if (movedPlannedItem) {
    const previous = movedPlannedItem.priorReason;
    const priorFinish = parseOptionalMs(previous?.plannedFinishAt);
    const latestFinish = movedPlannedItem.finishMs;
    const priorStart = parseOptionalMs(previous?.plannedStartAt);
    const latestStart = movedPlannedItem.startMs;
    if (Number.isFinite(priorFinish) && Number.isFinite(latestFinish)) {
      return `${forecastReasonLabel(movedPlannedItem.reason)} finish moved from ${fmtDate(previous.plannedFinishAt, true)} to ${fmtDate(movedPlannedItem.reason.plannedFinishAt, true)}`;
    }
    if (Number.isFinite(priorStart) && Number.isFinite(latestStart)) {
      return `${forecastReasonLabel(movedPlannedItem.reason)} start moved from ${fmtDate(previous.plannedStartAt, true)} to ${fmtDate(movedPlannedItem.reason.plannedStartAt, true)}`;
    }
  }

  const latestFinishDriver = plannedReasons.find(item => Number.isFinite(item.finishMs) && Number.isFinite(latestReadyMs) && Math.abs(item.finishMs - latestReadyMs) <= 12 * 3600000);
  if (latestFinishDriver) {
    return `${forecastReasonLabel(latestFinishDriver.reason)} planned finish ${fmtDate(latestFinishDriver.reason.plannedFinishAt, true)}`;
  }

  const workload = latestReasons.find(reason => reason.type === 'labor' && Number(reason.workHours || 0) > 0);
  if (workload) {
    const shop = workload.shop || 'Unassigned';
    const matchingWork = latestReasons
      .filter(reason => ['inspection', '8035', 'customTask'].includes(reason.type) && (reason.shop || 'Unassigned') === shop)
      .slice(0, 3)
      .map(forecastReasonLabel);
    const ownerText = shop === 'Unassigned' ? 'Labor plan missing / no owning shop' : `${shop} labor workload`;
    return `${ownerText}: ${workload.workHours || '--'} hrs across ${matchingWork.length || 'multiple'} readiness driver${matchingWork.length === 1 ? '' : 's'}${matchingWork.length ? ` (${truncateText(matchingWork.join('; '), 95)})` : ''}`;
  }

  if (Number.isFinite(latestReadyMs) && !Number.isFinite(priorReadyMs)) {
    return `Projected ready date is now ${fmtDate(latest.projectedReadyAt, true)}`;
  }
  return latest.primaryDriver || '--';
}

function forecastChangeNote(row, canSubmit, drafts, setDrafts, submitExplanation) {
  const explanation = row?.explanation;
  const noteKey = row?.latest?.id || explanation?.id || row?.aircraft?.id;
  const savedText = explanation?.text || '';
  const draftValue = Object.prototype.hasOwnProperty.call(drafts, noteKey)
    ? drafts[noteKey]
    : savedText;
  const savedMeta = explanation?.submittedAt
    ? `Saved ${fmtProjectedReadyDate(explanation.submittedAt)}${explanation.submittedBy ? ` by ${explanation.submittedBy}` : ''}`
    : savedText
      ? 'Saved'
      : '';
  if (canSubmit && noteKey) {
    const changed = String(draftValue || '').trim() !== String(savedText || '').trim();
    return (
      <div className="crystal-ball__explanation-entry">
        <textarea
          value={draftValue}
          onChange={event => setDrafts(current => ({ ...current, [noteKey]: event.target.value }))}
          placeholder="Meeting note, action taken, or variance reason"
          rows={2}
        />
        <button type="button" disabled={!changed} onClick={() => submitExplanation(row, noteKey)}>Save</button>
        {savedMeta ? <small>{savedMeta}</small> : null}
      </div>
    );
  }
  if (savedText) {
    return (
      <div className="crystal-ball__explanation-entry is-readonly">
        <span>{savedText}</span>
        {savedMeta ? <small>{savedMeta}</small> : null}
      </div>
    );
  }
  return '';
}

function missingInputCount(forecast) {
  if (!forecast) return 1;
  return Array.isArray(forecast?.missingInputs) ? forecast.missingInputs.length : 0;
}

function planReviewItems(forecast) {
  if (!forecast) return ['Forecast not calculated'];
  const missing = Array.isArray(forecast?.missingInputs) ? forecast.missingInputs : [];
  const warnings = scheduleWarnings(forecast).map(item => item.message || item.label || 'Schedule logic review needed');
  return [...new Set([...missing, ...warnings].filter(Boolean))];
}

function requiresPlanReview(tail) {
  return planReviewItems(tail?.forecast).length > 0;
}

function waiverSummary(items) {
  const references = (items || [])
    .map(item => item.waiver?.reference || item.waiverReference || 'Waiver')
    .filter(Boolean);
  if (!references.length) return '--';
  return truncateText([...new Set(references)].join(', '), 80);
}

function matchLabel(status) {
  const labels = {
    matched: 'Requirement matched',
    tail_matched: 'Tail matched',
    unmatched: 'Unmatched',
    unlinked: 'Task links cleared',
    ambiguous_tail: 'Ambiguous tail',
    active: 'Issued',
    issued: 'Issued',
    pending: 'Pending Issued Date',
    not_issued: 'Not issued',
    closed: 'Closed',
  };
  return labels[status] || status || '--';
}

function etarSummaryCount(summary, ...keys) {
  return keys.reduce((total, key) => total + Number(summary?.[key] || 0), 0);
}

function etarWaiverStatusLabel(item) {
  const status = String(item?.status || item?.waiver?.status || '').trim().toLowerCase();
  if (status === 'issued' || status === 'active') return 'Issued waiver';
  if (status === 'pending' || status === 'not_issued' || status === 'not-issued' || status === 'not issued') return 'Pending Issued Date';
  if (status === 'closed') return 'Closed evidence';
  if (status === 'expired') return 'Pending Issued Date';
  return status ? `${matchLabel(status)} waiver` : 'Waiver status unknown';
}

function etarWaiverStatusClass(item) {
  const status = String(item?.status || item?.waiver?.status || '').trim().toLowerCase();
  if (status === 'issued' || status === 'active') return 'is-issued';
  if (status === 'pending' || status === 'not_issued' || status === 'not-issued' || status === 'not issued' || status === 'expired') return 'is-pending';
  if (status === 'closed') return 'is-closed';
  return 'is-unknown';
}

function etarWaiverStatusRank(item) {
  const status = String(item?.status || item?.waiver?.status || '').trim().toLowerCase();
  if (status === 'issued' || status === 'active') return 0;
  if (status === 'pending' || status === 'not_issued' || status === 'not-issued' || status === 'not issued' || status === 'expired') return 1;
  if (status === 'closed') return 2;
  return 3;
}

function etarIssuedAgeDays(item) {
  return item?.issuedAgeDays ?? item?.daysRemaining ?? null;
}

function sortEtarWaivers(items) {
  return [...(items || [])].sort((left, right) => (
    etarWaiverStatusRank(left) - etarWaiverStatusRank(right)
    || Number(etarIssuedAgeDays(right) ?? -1) - Number(etarIssuedAgeDays(left) ?? -1)
    || String(left.etarNumber || '').localeCompare(String(right.etarNumber || ''), undefined, { numeric: true })
  ));
}

function etarIssuedDate(item) {
  return item?.issuedAt || item?.closedAt || '';
}

function RequirementWaiverLinks({ requirement, etars }) {
  const linked = sortEtarWaivers((etars || []).filter(item => matchedEtarTasks(item).some(task => task.id === requirement.id)));
  const active = requirement.waiver;
  const showActive = active && !linked.some(item => item.waiver?.id === active.id
    || String(item.etarNumber || '').trim().toUpperCase() === String(active.reference || '').trim().toUpperCase());
  const count = linked.length + (showActive ? 1 : 0);
  if (!count) return <span>No linked waiver</span>;
  return <div className="crystal-ball__requirement-waivers">
    <strong>{count} linked waiver{count === 1 ? '' : 's'}</strong>
    {linked.map(item => <div key={item.id}>
      <strong>{etarDisplayTitle(item)}</strong>
      <span className={`crystal-ball__waiver ${etarWaiverStatusClass(item)}`}><ShieldCheck size={14} />{item.etarNumber} | {etarWaiverStatusLabel(item)}</span>
    </div>)}
    {showActive ? <div><strong>{active.reference || 'Waiver'}</strong><span className="crystal-ball__waiver is-issued"><ShieldCheck size={14} />Active waiver</span></div> : null}
  </div>;
}

function RequirementReadinessDriverTag({ requirement }) {
  if (requirement?.affectsReadiness !== true) return null;
  return (
    <span
      className="crystal-ball__readiness-driver-tag"
      title="Marked as a readiness driver in Status"
    >
      Readiness driver
    </span>
  );
}

function normalizedSearchText(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function etarSearchText(item) {
  const requirement = item?.matchedRequirement || {};
  const aircraft = item?.aircraft || {};
  return [
    item?.etarNumber,
    item?.classification,
    item?.nomenclature,
    item?.subject,
    item?.tailNumber,
    item?.normalizedTail,
    item?.serialNumber,
    aircraft.tail,
    aircraft.mds,
    aircraft.location,
    item?.submissionOrg,
    item?.aflcmcOrg,
    item?.issuedAgeDays,
    item?.daysRemaining,
    item?.status,
    matchLabel(item?.matchStatus),
    etarWaiverStatusLabel(item),
    ...matchedEtarTasks(item).flatMap(task => [task.noun, task.wuc]),
    requirement.wuc,
  ].filter(value => value !== null && value !== undefined).join(' ');
}

function etarMatchesSearch(item, query) {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  const searchable = etarSearchText(item).toLowerCase();
  const compactTerm = normalizedSearchText(term);
  return searchable.includes(term) || Boolean(compactTerm && normalizedSearchText(searchable).includes(compactTerm));
}

function plannedWorkDateLabel(label, value) {
  return value ? `${label} ${fmtDate(value, true)}` : `${label} needed`;
}

function tailSwapShortLabel(item) {
  const role = String(item?.tailSwapRole || '').toLowerCase();
  if (role === 'departing') return 'SWAP OUT';
  if (role === 'returning') return 'SWAP IN';
  return 'TAIL SWAP';
}

function tailSwapDirectionLabel(item) {
  const role = String(item?.tailSwapRole || '').toLowerCase();
  if (role === 'departing') return 'Departing';
  if (role === 'returning') return 'Returning';
  return 'Linked Swap';
}

function tailSwapLocationText(value, masked) {
  if (!value) return '';
  return masked ? 'Foreign OS' : value;
}

function isTbdTailSwapPlaceholder(item) {
  return Boolean(item?.swapTbdPlaceholder)
    || String(item?.swapWithTail || '').trim().toUpperCase() === TAIL_SWAP_TBD_VALUE;
}

function tailSwapTbdDurationMonths(item) {
  const months = Number(item?.swapTbdDurationMonths);
  return Number.isFinite(months) && months > 0 ? Math.floor(months) : 0;
}

function addMonthsMs(startMs, months) {
  if (!Number.isFinite(startMs) || !months) return null;
  const end = new Date(startMs);
  end.setUTCMonth(end.getUTCMonth() + months);
  return end.getTime();
}

function tailSwapTbdActiveAtPeriod(item, period) {
  if (!isTbdTailSwapPlaceholder(item)) return false;
  const months = tailSwapTbdDurationMonths(item);
  if (!months) return false;
  const startMs = parseOptionalMs(item.swapDateAt || item.startDate || item.endDate);
  const endMs = addMonthsMs(startMs, months);
  const horizonMs = readinessHorizonMs(period);
  return Number.isFinite(startMs) && Number.isFinite(endMs) && startMs <= horizonMs && horizonMs < endMs;
}

function tailSwapTbdDurationLabel(item) {
  if (!isTbdTailSwapPlaceholder(item)) return '0 months';
  const months = tailSwapTbdDurationMonths(item);
  if (!months) return 'Estimate needed';
  return `${months} month${months === 1 ? '' : 's'}`;
}

function readinessDriverItemCount(row, key) {
  const items = Array.isArray(row?.[key]) ? row[key] : [];
  return items.filter(item => item?.affectsReadiness === true).length;
}

function readinessDriverPressure(row) {
  return {
    overdue: readinessDriverItemCount(row, 'overdue9188'),
    red: readinessDriverItemCount(row, 'red9188'),
    watch: readinessDriverItemCount(row, 'watch9188'),
    openK: Number(row?.counts?.form781K || 0),
  };
}

function openXClearanceSummary(row, swapStartMs) {
  const openXItems = Array.isArray(row?.openX) ? row.openX : [];
  if (!openXItems.length) return { eligible: true, label: 'No open X' };
  let latestFinishMs = null;
  for (const item of openXItems) {
    const finishMs = parseOptionalMs(item?.actualFinishAt || item?.plannedFinishAt);
    if (!Number.isFinite(finishMs) || finishMs > swapStartMs) {
      return { eligible: false, label: 'Open X not planned clear before swap' };
    }
    latestFinishMs = latestFinishMs === null ? finishMs : Math.max(latestFinishMs, finishMs);
  }
  return {
    eligible: true,
    latestFinishMs,
    label: latestFinishMs ? `X clear ${fmtDate(latestFinishMs)}` : 'Open X planned clear',
  };
}

function forecastHasRedWindowOverlap(forecast, startMs, endMs) {
  return (forecast?.availabilityWindows || []).some(window => {
    if (window.state !== 'red') return false;
    const windowStartMs = parseOptionalMs(window.startAt);
    const windowEndMs = parseOptionalMs(window.endAt);
    return Number.isFinite(windowStartMs)
      && Number.isFinite(windowEndMs)
      && windowStartMs < endMs
      && windowEndMs > startMs;
  });
}

function tailAvailableForTbdSwapWindow(tail, item, statusRow) {
  const startMs = parseOptionalMs(item?.swapDateAt || item?.startDate || item?.endDate);
  if (!Number.isFinite(startMs)) return false;
  const openXClearance = openXClearanceSummary(statusRow, startMs);
  if (!openXClearance.eligible) return false;
  const months = tailSwapTbdDurationMonths(item);
  const endMs = addMonthsMs(startMs, months) || startMs + 86400000;
  const forecast = tail?.forecast;
  if (!forecast) return false;
  const projectedReadyMs = parseOptionalMs(forecast.projectedReadyAt);
  const readyBySwap = Boolean(forecast.authorizedReadyNow) || (Number.isFinite(projectedReadyMs) && projectedReadyMs <= startMs);
  return readyBySwap && !forecastHasRedWindowOverlap(forecast, startMs, endMs);
}

function tailSwapSummary(item) {
  if (!item) return '';
  const planLocation = tailSwapLocationText(item.planLocation, item.swapForeignOsMasked);
  return [
    item.eventType || 'Tail Swap',
    fmtPlanDate(item.swapDateAt || item.startDate),
    item.swapWithTail ? `with/for ${item.swapWithTail}` : '',
    item.swapWithLocation || '',
    planLocation ? `to ${planLocation}` : '',
  ].filter(Boolean).join(' | ');
}

function tailSwapTitle(item) {
  if (!item) return '';
  const planLocation = tailSwapLocationText(item.planLocation, item.swapForeignOsMasked);
  return [
    item.eventType || 'Tail Swap',
    item.swapDateAt ? `Date ${fmtPlanDate(item.swapDateAt)}` : '',
    item.swapWithTail ? `Swap with/for ${item.swapWithTail}` : '',
    item.swapWithLocation ? `Base ${item.swapWithLocation}` : '',
    planLocation ? `Projected location ${planLocation}` : '',
    item.remarks || '',
  ].filter(Boolean).join('\n');
}

function plannedReadinessWorkAfterOffstationReturn(detail) {
  const returnAt = detail?.aircraft?.actualReturnAt || detail?.aircraft?.estimatedReturnAt;
  const returnMs = Date.parse(returnAt || '');
  if (!Number.isFinite(returnMs)) return [];

  const requirementsById = new Map((detail?.requirements || []).map(item => [item.id, item]));
  const discrepanciesById = new Map((detail?.delayedDiscrepancies || []).map(item => [item.id, item]));
  return (detail?.forecast?.reasons || [])
    .filter(reason => ['inspection', '8035', 'customTask'].includes(reason.type))
    .map(reason => {
      const sourceRow = reason.type === 'inspection' ? requirementsById.get(reason.id) : reason.type === '8035' ? discrepanciesById.get(reason.id) : null;
      const form781Code = reason.form781Code || sourceRow?.form781Code || '';
      const is781K = String(form781Code || '').trim().toUpperCase() === 'K';
      return {
        id: `${reason.type}-${reason.id || reason.jcn || reason.label}`,
        source: reason.type === 'inspection'
          ? (sourceRow?.sourceReportType === '9188-6' ? '9188-6 planned inspection' : 'Scheduled inspection')
          : reason.type === '8035'
            ? (is781K ? '781K delayed discrepancy' : '8035 job')
            : 'Custom task',
        title: reason.type === 'inspection'
          ? (reason.label || sourceRow?.noun || 'Scheduled inspection')
          : reason.type === '8035'
            ? (is781K ? `781K DD ${reason.jcn || sourceRow?.jcn || ''}`.trim() : (reason.jcn || sourceRow?.jcn || '8035 job'))
            : (reason.label || 'Custom task'),
        shopName: reason.shop || sourceRow?.shopName,
        wuc: sourceRow?.wuc,
        symbolCode: reason.symbolCode || sourceRow?.symbolCode,
        form781Code,
        plannedStartAt: reason.plannedStartAt || sourceRow?.plannedStartAt,
        plannedFinishAt: reason.plannedFinishAt || sourceRow?.plannedFinishAt,
        description: sourceRow?.discrepancy || sourceRow?.notes || reason.label || '',
        scheduleWarning: reason.scheduleWarning || sourceRow?.scheduleWarning,
        affectsReadiness: reason.affectsReadiness ?? sourceRow?.affectsReadiness,
      };
    })
    .filter(item => item.affectsReadiness !== false)
    .filter(item => {
      const startMs = Date.parse(item.plannedStartAt || '');
      const finishMs = Date.parse(item.plannedFinishAt || '');
      if (!Number.isFinite(startMs) || !Number.isFinite(finishMs)) return true;
      return startMs >= returnMs || finishMs >= returnMs;
    })
    .sort((left, right) => (
      (parseOptionalMs(left.plannedStartAt) ?? Number.MAX_SAFE_INTEGER)
      - (parseOptionalMs(right.plannedStartAt) ?? Number.MAX_SAFE_INTEGER)
    ));
}

function timelineWorkItemTitle(reason) {
  const name = String(reason.label || reason.noun || reason.discrepancy || '').trim();
  const fallback = String(reason.jcn || 'Readiness driver').trim();
  return [
    name || fallback,
    reason.jcn ? `JCN ${reason.jcn}` : '',
    reason.plannedStartAt ? `Start ${fmtDate(reason.plannedStartAt)}` : '',
    reason.plannedFinishAt ? `Finish ${fmtDate(reason.plannedFinishAt)}` : '',
  ].filter(Boolean).join(' | ');
}

function stackedRtgDriverName(reason = {}, label = '') {
  const rawName = reason.noun || reason.label || reason.discrepancy || reason.description || label || 'Driver';
  const name = String(rawName || '').replace(/\s+/g, ' ').trim();
  return (name.split(/\s+\|\s+/)[0] || name || 'Driver').trim();
}

function stackedRtgDriverCategory(driverName = '', reason = {}) {
  const text = String(driverName || '').toUpperCase();
  if (reason.type === 'acftPlan' && text === 'ALERT') return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('alert');
  if (/\bPDM\b|PROGRAMMED DEPOT/.test(text)) return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('pdm');
  if (/\bHPO\b/.test(text)) return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('hpo');
  if (/\bACFT\s*WASH\b|\bAIRCRAFT\s*WASH\b|\bWASH\b/.test(text)) return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('wash');
  if (/\b900\b|\b900\s*(HR|HOUR|HOURS)\b/.test(text)) return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('hour900');
  if (/\b1,?800\b|\b1800\s*(HR|HOUR|HOURS)\b/.test(text)) return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('hour1800');
  if (/\bRTIC\b/.test(text)) return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('rtic');
  if (reason.type === '8035' && String(reason.symbolCode || '').trim().toUpperCase() === 'X') {
    return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('openx');
  }
  return STACKED_RTG_DRIVER_CATEGORY_BY_KEY.get('other');
}

function stackedRtgSegmentText(driverName) {
  return truncateText(driverName || 'Driver', 18);
}

function timelineWorkReasons(row) {
  const plannedItems = (row.forecast?.reasons || [])
    .filter(reason => ['inspection', '8035', 'customTask', 'acftPlan'].includes(reason.type))
    .map(reason => {
      const plannedStartMs = parseOptionalMs(reason.plannedStartAt);
      const plannedFinishMs = parseOptionalMs(reason.plannedFinishAt);
      const label = timelineWorkItemTitle(reason);
      const driverName = stackedRtgDriverName(reason, label);
      const driverCategory = stackedRtgDriverCategory(driverName, reason);
      return {
        reason,
        plannedStartMs,
        plannedFinishMs,
        label,
        driverName,
        driverCategory,
        shortLabel: stackedRtgSegmentText(driverName),
      };
    })
    .filter(item => Number.isFinite(item.plannedStartMs) && Number.isFinite(item.plannedFinishMs));
  const estimatedItems = estimatedRtgDriverItems(row, plannedItems, task => {
    const driverName = stackedRtgDriverName(task, task.label);
    return stackedRtgDriverCategory(driverName, task);
  });
  return [...plannedItems, ...estimatedItems];
}

function timelineWindowTitle(row, window, windowStartMs, windowEndMs, workItems) {
  const overlappingReasons = workItems
    .filter(item => item.plannedStartMs < windowEndMs && item.plannedFinishMs > windowStartMs)
    .slice(0, 5)
    .map(item => item.label);
  // Merged availability labels can contain shop workload totals. Keep the
  // named work and status here; task bars provide their own source details.
  const windowLabel = String(window.label || row.forecast?.primaryDriver || '')
    .split(' / ')
    .filter(label => !/\bworkload\s*$/i.test(label) && !/^\s*unassigned\s*$/i.test(label))
    .join(' / ');
  return [
    windowLabel || 'RTG blocked',
    ...overlappingReasons,
  ].filter(Boolean).join('\n');
}

function timelineAvailabilityWindows(row, startMs, spanMs, workItems) {
  const projectedReadyMs = row.forecast?.authorizedReadyNow ? startMs : parseOptionalMs(row.forecast?.projectedReadyAt);
  return (row.forecast?.availabilityWindows || [])
    .filter(window => window.state === 'red')
    .filter(window => window.kind !== 'offstation_return')
    .map((window, index) => {
      const windowStartMs = parseOptionalMs(window.startAt);
      let windowEndMs = parseOptionalMs(window.endAt);
      if (!Number.isFinite(windowStartMs) || !Number.isFinite(windowEndMs) || windowEndMs <= windowStartMs) return null;
      if (window.kind === 'current_rtg' && Number.isFinite(projectedReadyMs)) {
        windowEndMs = Math.min(windowEndMs, projectedReadyMs);
      }
      if (windowEndMs <= windowStartMs) return null;
      const left = percentBetween(windowStartMs, startMs, spanMs);
      const finish = percentBetween(windowEndMs, startMs, spanMs);
      if (left === null || finish === null) return null;
      return {
        id: `${window.kind || 'window'}-${window.sourceId || index}`,
        left: `${left}%`,
        width: `${Math.max(0.9, finish - left)}%`,
        label: timelineWindowTitle(row, window, windowStartMs, windowEndMs, workItems),
      };
    })
    .filter(Boolean)
    .slice(0, 8);
}

function timelineReadyWindows(row, startMs, endMs, spanMs) {
  const projectedReadyMs = row.forecast?.authorizedReadyNow ? startMs : parseOptionalMs(row.forecast?.projectedReadyAt);
  if (!Number.isFinite(projectedReadyMs) || projectedReadyMs >= endMs) return [];
  const blockedIntervals = (row.forecast?.availabilityWindows || [])
    .filter(window => window.state === 'red')
    .filter(window => window.kind !== 'offstation_return')
    .map(window => {
      const blockStart = parseOptionalMs(window.startAt);
      let blockEnd = parseOptionalMs(window.endAt);
      if (!Number.isFinite(blockStart) || !Number.isFinite(blockEnd)) return null;
      if (window.kind === 'current_rtg') blockEnd = Math.min(blockEnd, projectedReadyMs);
      if (blockEnd <= projectedReadyMs || blockStart >= endMs || blockEnd <= blockStart) return null;
      return {
        start: Math.max(blockStart, projectedReadyMs),
        end: Math.min(blockEnd, endMs),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.start - right.start);
  const readyWindows = [];
  let cursor = projectedReadyMs;
  blockedIntervals.forEach((block, index) => {
    if (block.start > cursor) readyWindows.push({ start: cursor, end: block.start, id: `ready-${index}` });
    cursor = Math.max(cursor, block.end);
  });
  if (cursor < endMs) readyWindows.push({ start: cursor, end: endMs, id: `ready-${readyWindows.length}` });
  return readyWindows.map(window => {
    const left = percentBetween(window.start, startMs, spanMs);
    const finish = percentBetween(window.end, startMs, spanMs);
    if (left === null || finish === null) return null;
    return {
      id: window.id,
      startMs: window.start,
      endMs: window.end,
      left: `${left}%`,
      width: `${Math.max(0.9, finish - left)}%`,
      label: row.forecast?.authorizedReadyNow ? 'Ready now' : `Projected usable after ${fmtDate(row.forecast?.projectedReadyAt)}`,
    };
  }).filter(Boolean);
}

function timelineTailSwapMarkers(row, startMs, spanMs) {
  return (row.tailSwaps || [])
    .map((item, index) => {
      const swapMs = parseOptionalMs(item.swapDateAt || item.startDate);
      const left = percentBetween(swapMs, startMs, spanMs);
      if (left === null) return null;
      return {
        id: item.id || `tail-swap-${index}`,
        left: `${left}%`,
        role: String(item.tailSwapRole || '').toLowerCase() || 'swap',
        shortLabel: tailSwapShortLabel(item),
        title: tailSwapTitle(item),
      };
    })
    .filter(Boolean);
}

function tailSwapWatchlistRows(rows) {
  return (rows || [])
    .flatMap(row => (row.tailSwaps || []).map((item, index) => {
      const swapAt = item.swapDateAt || item.startDate || item.endDate || '';
      const swapMs = parseOptionalMs(swapAt);
      const pairKey = tailSwapPairKey(row, item);
      return {
        id: `${row.id || row.tail}-${item.id || index}`,
        aircraft: row,
        item,
        pairKey,
        pairTone: tailSwapPairTone(pairKey),
        swapAt,
        swapMs: Number.isFinite(swapMs) ? swapMs : Number.MAX_SAFE_INTEGER,
      };
    }))
    .sort((left, right) => (
      left.swapMs - right.swapMs
      || String(left.pairKey || '').localeCompare(String(right.pairKey || ''))
      || String(left.aircraft.tail || '').localeCompare(String(right.aircraft.tail || ''))
    ));
}

function tailSwapPairKey(aircraft, item) {
  if (isTbdTailSwapPlaceholder(item)) return '';
  const tail = String(aircraft?.tail || item?.tail || '').trim().toUpperCase();
  const counterpart = String(item?.swapWithTail || '').trim().toUpperCase();
  if (!tail || !counterpart || counterpart === TAIL_SWAP_TBD_VALUE) return '';
  const swapDate = String(item?.swapDateAt || item?.startDate || item?.endDate || '').slice(0, 10);
  return [swapDate, ...[tail, counterpart].sort()].join('|');
}

function tailSwapPairTone(pairKey) {
  if (!pairKey) return 0;
  const sum = Array.from(pairKey).reduce((total, char) => total + char.charCodeAt(0), 0);
  return (sum % 4) + 1;
}

function hasPlannedUnitLaborExemptRtgWork(row) {
  return (row.forecast?.reasons || []).some(reason => (
    ['inspection', '8035', 'customTask'].includes(reason.type)
    && reason.unitLaborRequired === false
    && reason.affectsReadiness !== false
    && reason.plannedStartAt
    && reason.plannedFinishAt
  ));
}

function CrystalBallTab({ api, user, selectedBase, globalView }) {
  const normalizedRole = String(user.role || '').replace(/\s+/g, '').toLowerCase();
  const hasHqRole = (
    ['commandanalyst', 'hqanalyst', 'majcomanalyst', 'afrchq'].includes(normalizedRole)
    || (isAfRcHeadquartersLocation(user.location) && normalizedRole === 'siteadmin')
  );
  const canUseHqRollup = Boolean(user.is_superuser || hasHqRole);
  const [view, setView] = useState('overview');
  const [readinessGrain, setReadinessGrain] = useState('quarter');
  const [rtgTimelineGrain, setRtgTimelineGrain] = useState('month');
  const [rtgTimelineZoom, setRtgTimelineZoom] = useState(RTG_TIMELINE_ZOOM_STEPS[0]);
  const [rtgHighlights, setRtgHighlights] = useState([]);
  const [tailSwapsOnly, setTailSwapsOnly] = useState(false);
  const [rtgStackedRows, setRtgStackedRows] = useState(true);
  const [aircraftScopeFilter, setAircraftScopeFilter] = useState('combined');
  const [mdsFilter, setMdsFilter] = useState('all');
  const [capacityConcernFilter, setCapacityConcernFilter] = useState('all');
  const [capacityConcernPage, setCapacityConcernPage] = useState(1);
  const [reviewedCapacityConcerns, setReviewedCapacityConcerns] = useState({});
  const [capacityReviewFeedback, setCapacityReviewFeedback] = useState('');
  const [capacityDriverModalConcern, setCapacityDriverModalConcern] = useState(null);
  const [tailSwapDurationDrafts, setTailSwapDurationDrafts] = useState({});
  const [tailSwapDurationSaving, setTailSwapDurationSaving] = useState({});
  const [tailSwapDurationFeedback, setTailSwapDurationFeedback] = useState('');
  const [hqMode, setHqMode] = useState(Boolean(user.is_superuser || hasHqRole || globalView));
  // This selection belongs to HQ mode. Location mode uses selectedBase directly.
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [sitePickerOpen, setSitePickerOpen] = useState(false);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [availableLocations, setAvailableLocations] = useState([]);
  const [summary, setSummary] = useState({ mds: [], tails: [], generatedAt: null });
  const [extraReadinessLayouts, setExtraReadinessLayouts] = useState({ source: null, periods: {} });
  const [readinessPeriodsLoading, setReadinessPeriodsLoading] = useState(false);
  const [rtg, setRtg] = useState({ aircraft: [], supportRecommendations: [] });
  const [statusReport, setStatusReport] = useState(null);
  const [statusEvidenceState, setStatusEvidenceState] = useState('loading');
  const [supportLoadStates, setSupportLoadStates] = useState({});
  const [changes, setChanges] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [completionHistory, setCompletionHistory] = useState([]);
  const [completionHistorySearch, setCompletionHistorySearch] = useState('');
  const [showArchivedCompletionHistory, setShowArchivedCompletionHistory] = useState(false);
  const [completionHistoryReviewId, setCompletionHistoryReviewId] = useState('');
  const [etars, setEtars] = useState({ records: [], summary: {} });
  const [bootstrap, setBootstrap] = useState({ alerts: {}, reportTypes: [] });
  const [crystalStatusPdfSections, setCrystalStatusPdfSections] = useState(DEFAULT_CRYSTAL_STATUS_PDF_SECTIONS);
  const [crystalStatusPdfPickerOpen, setCrystalStatusPdfPickerOpen] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailFocus, setDetailFocus] = useState('');
  const [selectedReadinessCell, setSelectedReadinessCell] = useState(null);
  const [evidenceFilter, setEvidenceFilter] = useState('');
  const [evidenceReadinessFilter, setEvidenceReadinessFilter] = useState('all');
  const [evidenceDeploymentFilter, setEvidenceDeploymentFilter] = useState('all');
  const [evidencePeriodDate, setEvidencePeriodDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshPhase, setRefreshPhase] = useState('');
  const [displayedScopeKey, setDisplayedScopeKey] = useState('');
  const [displayedScopeLabel, setDisplayedScopeLabel] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [chatWorking, setChatWorking] = useState(false);
  const [chatStartedAt, setChatStartedAt] = useState(null);
  const [chatElapsedSeconds, setChatElapsedSeconds] = useState(0);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [explanationDrafts, setExplanationDrafts] = useState({});
  const [etarFile, setEtarFile] = useState(null);
  const [etarSearchTerm, setEtarSearchTerm] = useState('');
  const [etarLinkDrafts, setEtarLinkDrafts] = useState({});
  const [etarLinkFeedback, setEtarLinkFeedback] = useState({});
  const isTailSwapDurationWorking = useMemo(() => (
    Object.values(tailSwapDurationSaving).some(Boolean)
  ), [tailSwapDurationSaving]);
  const sitePickerRef = useRef(null);
  const hqSiteSelectionInitializedRef = useRef(false);
  const crystalStatusReportRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const loadRequestRef = useRef(0);
  const completionHistoryRequestRef = useRef(0);
  const waiverSaveVersionRef = useRef(0);
  const isHqRollup = Boolean(canUseHqRollup && hqMode);
  const canSubmitExplanation = (
    user.is_superuser
    || ['production', 'management', 'commander', 'siteadmin'].includes(String(user.role || '').replace(/\s+/g, '').toLowerCase())
    || String(user.shop || '').trim().toLowerCase() === 'production'
  );
  const viewTabs = useMemo(() => (
    isHqRollup
      ? [...VIEWS.slice(0, 2), ['status', 'Backlog / Status'], ...VIEWS.slice(2)]
      : VIEWS
  ), [isHqRollup]);
  const activeSupportKey = { status: 'status', changes: 'changes', history: 'estimates', waivers: 'etars' }[view];
  const activeSupportState = supportLoadStates[activeSupportKey];
  const activeViewReady = !activeSupportKey || activeSupportState === 'ready';
  const filteredCompletionHistory = useMemo(() => {
    const needle = completionHistorySearch.trim().toLowerCase();
    if (!needle) return completionHistory;
    return completionHistory.filter(row => [
      row.tail, row.mds, row.location, row.taskName, row.wuc, row.jcn, row.wes,
      row.shopName, row.sourceEntityType, row.status,
    ].some(value => String(value || '').toLowerCase().includes(needle)));
  }, [completionHistory, completionHistorySearch]);
  const rtgHighlightSet = useMemo(() => new Set(rtgHighlights), [rtgHighlights]);
  const rtgHighlightsActive = rtgHighlights.length > 0;
  const highlightedDriverKeys = useMemo(() => (
    rtgHighlights.filter(key => STACKED_RTG_DRIVER_CATEGORY_BY_KEY.has(key))
  ), [rtgHighlights]);
  const toggleRtgHighlight = key => {
    setRtgHighlights(current => (
      current.includes(key) ? current.filter(item => item !== key) : [...current, key]
    ));
  };
  const scope = useMemo(() => ({
    email: user.email,
    location: selectedBase,
    global: isHqRollup ? true : undefined,
    locations: isHqRollup ? selectedLocations.join('|') : undefined,
  }), [user.email, selectedBase, isHqRollup, selectedLocations]);
  useEffect(() => {
    if (view !== 'history' || activeSupportState !== 'ready') return undefined;
    const requestId = completionHistoryRequestRef.current + 1;
    completionHistoryRequestRef.current = requestId;
    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/crystal-ball/completion-history', {
          params: {
            ...scope,
            q: completionHistorySearch.trim() || undefined,
            includeExcluded: showArchivedCompletionHistory || undefined,
            limit: 200,
          },
        });
        if (completionHistoryRequestRef.current === requestId) {
          setCompletionHistory(response.data.records || []);
        }
      } catch (requestError) {
        if (completionHistoryRequestRef.current === requestId) {
          console.error('Failed to search completed task history', requestError);
        }
      }
    }, completionHistorySearch ? 250 : 0);
    return () => clearTimeout(timer);
  }, [activeSupportState, api, completionHistorySearch, scope, showArchivedCompletionHistory, view]);
  const setCompletionHistoryExclusion = async (row, excluded) => {
    const reason = excluded ? window.prompt('Why should this completion be archived from planning and AI analysis?') : '';
    if (excluded && !String(reason || '').trim()) return;
    setCompletionHistoryReviewId(row.id);
    setError('');
    try {
      const response = await api.patch(`/crystal-ball/completion-history/${row.id}/exclusion`, {
        ...scope,
        excludedFromAnalysis: excluded,
        reason: String(reason || '').trim(),
      });
      if (showArchivedCompletionHistory || !excluded) {
        setCompletionHistory(current => current.map(item => item.id === row.id ? { ...item, ...response.data } : item));
      } else {
        setCompletionHistory(current => current.filter(item => item.id !== row.id));
      }
      try {
        const estimateResponse = await api.get('/crystal-ball/estimate-performance', { params: scope });
        setEstimates(estimateResponse.data.estimates || []);
      } catch (refreshError) {
        console.error('Failed to refresh estimate history after review', refreshError);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Completed task history review could not be saved.');
    } finally {
      setCompletionHistoryReviewId('');
    }
  };
  const loadChatHistory = useCallback(async () => {
    if (!user?.email || !selectedBase) {
      setChatHistory([]);
      return;
    }
    setChatHistoryLoading(true);
    try {
      const response = await api.get('/crystal-ball/chat-history', {
        params: { ...scope, limit: 10 },
      });
      setChatHistory(response.data.history || []);
    } catch (requestError) {
      console.error('Failed to load Crystal Ball chat history', requestError);
      setChatHistory([]);
    } finally {
      setChatHistoryLoading(false);
    }
  }, [api, scope, selectedBase, user?.email]);
  useEffect(() => {
    if (!chatWorking || !chatStartedAt) return undefined;
    const updateElapsed = () => {
      setChatElapsedSeconds(Math.max(0, Math.floor((Date.now() - chatStartedAt) / 1000)));
    };
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(interval);
  }, [chatStartedAt, chatWorking]);
  useEffect(() => {
    if (!loading && view === 'chat') loadChatHistory();
  }, [loadChatHistory, loading, view]);
  const requestedScopeLabel = useMemo(() => {
    if (!isHqRollup) return selectedBase || 'selected site';
    if (selectedLocations.length) {
      if (selectedLocations.length <= 2) return `HQ rollup (${selectedLocations.join(', ')})`;
      return `HQ rollup (${selectedLocations.length} site${selectedLocations.length === 1 ? '' : 's'})`;
    }
    return 'HQ rollup (no sites selected)';
  }, [isHqRollup, selectedBase, selectedLocations]);
  const requestedScopeKey = useMemo(() => (
    isHqRollup
      ? `hq:${[...selectedLocations].sort().join('|') || 'none'}`
      : `site:${selectedBase || ''}`
  ), [isHqRollup, selectedBase, selectedLocations]);
  const capacityReviewStorageKey = useMemo(() => (
    `${CAPACITY_REVIEW_STORAGE_PREFIX}:${requestedScopeKey}`
  ), [requestedScopeKey]);
  const isShowingPreviousScope = Boolean(
    displayedScopeKey
    && displayedScopeKey !== requestedScopeKey
  );
  const isSiteScopeLoading = Boolean(
    isHqRollup
    && !availableLocations.length
    && !selectedLocations.length
    && (loading || refreshing || refreshPhase)
  );
  const includedSiteLabel = isSiteScopeLoading
    ? 'Loading sites...'
    : `${selectedLocations.length} site${selectedLocations.length === 1 ? '' : 's'} included`;
  const rollupSubtitleScope = isSiteScopeLoading
    ? 'HQ readiness rollup | loading sites'
    : `HQ readiness rollup | ${selectedLocations.length} site${selectedLocations.length === 1 ? '' : 's'}`;
  const afrcRollupLocations = useMemo(() => {
    const availableByKey = new Map(
      availableLocations.map(location => [normalizedLocationKey(location), location])
    );
    return Array.from(new Set(
      AFRC_ROLLUP_LOCATION_NAMES
        .map(location => availableByKey.get(normalizedLocationKey(location)))
        .filter(Boolean)
    ));
  }, [availableLocations]);
  const filteredAvailableLocations = useMemo(() => {
    const query = siteSearchTerm.trim().toLowerCase();
    if (!query) return availableLocations;
    return availableLocations.filter(location => location.toLowerCase().includes(query));
  }, [availableLocations, siteSearchTerm]);
  const baseRtgAircraft = useMemo(() => (
    filterAircraftScope(rtg.aircraft || [], aircraftScopeFilter, mdsFilter)
  ), [rtg.aircraft, aircraftScopeFilter, mdsFilter]);
  const tailSwapWatchlist = useMemo(() => tailSwapWatchlistRows(baseRtgAircraft), [baseRtgAircraft]);
  const filteredRtgAircraft = useMemo(() => (
    tailSwapsOnly ? baseRtgAircraft.filter(row => (row.tailSwaps || []).length) : baseRtgAircraft
  ), [baseRtgAircraft, tailSwapsOnly]);
  const statusReportByAircraftId = useMemo(() => {
    const map = new Map();
    (statusReport?.tails || []).forEach(row => {
      if (row.aircraft?.id) map.set(row.aircraft.id, row);
    });
    return map;
  }, [statusReport]);
  const capacityAnalysis = rtg.capacityAnalysis || {};
  const capacityPeriods = capacityAnalysis.periods || [];
  const allCapacityRows = capacityAnalysis.rows || [];
  const allCapacityPlanningRisks = capacityAnalysis.planningRisks || [];
  const enhancedRtgAircraft = useMemo(() => (
    filteredRtgAircraft.map(row => {
      const statusRow = statusReportByAircraftId.get(row.id);
      const overdue9188 = statusRow?.overdue9188 || [];
      const red9188 = statusRow?.red9188 || [];
      const watch9188 = statusRow?.watch9188 || [];
      const openX = statusRow?.openX || [];
      const missingDueStart = statusRow?.missingDueStart || [];
      const counts = statusRow?.counts || {};
      const driverItems = [
        ...openX.map(item => ({ ...item, driverType: '8035 X' })),
        ...missingDueStart.map(item => ({ ...item, driverType: 'Missing due/start' })),
        ...overdue9188.map(item => ({ ...item, driverType: '9188 overdue' })),
        ...red9188.map(item => ({ ...item, driverType: '9188 red' })),
      ];
      return {
        ...row,
        statusReport: statusRow || null,
        counts,
        overdue9188,
        red9188,
        watch9188,
        openX,
        missingDueStart,
        driverItems,
        waiverLabel: waiverSummary(driverItems),
        workloadRealism: tailWorkloadRealism(row, allCapacityRows, allCapacityPlanningRisks),
      };
    }).sort(compareG081TailPriority)
  ), [allCapacityPlanningRisks, allCapacityRows, filteredRtgAircraft, statusReportByAircraftId]);
  const rtgTimeline = useMemo(() => {
    const startMs = new Date().setHours(0, 0, 0, 0);
    const readyTimes = enhancedRtgAircraft.map(row => parseOptionalMs(row.forecast?.projectedReadyAt)).filter(Number.isFinite);
    const returnTimes = enhancedRtgAircraft.map(row => parseOptionalMs(row.actualReturnAt || row.estimatedReturnAt)).filter(Number.isFinite);
    const tailSwapTimes = enhancedRtgAircraft.flatMap(row => (row.tailSwaps || []).map(item => parseOptionalMs(item.swapDateAt || item.startDate))).filter(Number.isFinite);
    const lifecycleTimes = enhancedRtgAircraft.flatMap(row => (
      (row.forecast?.availabilityWindows || [])
        .filter(window => window.kind !== 'offstation_return')
        .flatMap(window => [parseOptionalMs(window.startAt), parseOptionalMs(window.endAt)])
    )).filter(Number.isFinite);
    const endMs = Math.max(startMs + (14 * 86400000), ...readyTimes, ...lifecycleTimes);
    const rawTimelineEndMs = Math.max(endMs, ...returnTimes, ...tailSwapTimes);
    const timelineEndMs = timelineDisplayEndMs(startMs, rawTimelineEndMs, rtgTimelineGrain);
    const span = Math.max(86400000, timelineEndMs - startMs);
    const rows = enhancedRtgAircraft.map(row => {
      const workItems = timelineWorkReasons(row);
      const returnAt = row.actualReturnAt || row.estimatedReturnAt;
      const returnMs = parseOptionalMs(returnAt);
      const readyWindows = timelineReadyWindows(row, startMs, timelineEndMs, span);
      const returnPct = percentBetween(returnMs, startMs, span);
      const readyMarkers = readyWindows.map((window, index) => ({
        id: `ready-marker-${window.id}`,
        left: window.left,
        title: index === 0 && row.forecast?.authorizedReadyNow
          ? 'Ready now'
          : `Usable point ${fmtDate(new Date(window.startMs).toISOString())}`,
      }));
      return {
        ...row,
        returnAt,
        returnLeft: returnPct === null ? null : `${returnPct}%`,
        readyMarkers,
        showPlanMissing: !readyMarkers.length && !hasPlannedUnitLaborExemptRtgWork(row),
        availabilityWindows: timelineAvailabilityWindows(row, startMs, span, workItems),
        stackedWork: layoutStackedRtgWork(workItems, startMs, span),
        unstackedWork: layoutUnstackedRtgWork(workItems, startMs, span),
        readyWindows,
        tailSwapMarkers: timelineTailSwapMarkers(row, startMs, span),
      };
    });
    return {
      startMs,
      endMs: timelineEndMs,
      ticks: timelineTicks(startMs, timelineEndMs, rtgTimelineGrain),
      rows,
      groups: groupRtgTimelineRowsByLocationMds(rows),
    };
  }, [enhancedRtgAircraft, rtgTimelineGrain]);
  const returnDriverJobs = useMemo(() => plannedReadinessWorkAfterOffstationReturn(detail), [detail]);
  const hasOffstationReturnDate = Boolean(detail?.aircraft?.actualReturnAt || detail?.aircraft?.estimatedReturnAt);
  const detailPlanningWarnings = summary.tails.find(tail => tail.id === detail?.aircraft?.id)?.maintenanceReadiness?.planningWarnings || [];
  const detailOverdueRequirements = useMemo(() => (
    (detail?.requirements || []).filter(item => item.sourceReportType === '9188-6' && (item.level === 'overdue' || Number(item.overdueDays || 0) > 0))
  ), [detail]);
  const detailRedRequirements = useMemo(() => (
    (detail?.requirements || []).filter(item => item.sourceReportType === '9188-6' && item.level === 'critical')
  ), [detail]);
  const detailWatchRequirements = useMemo(() => (
    (detail?.requirements || []).filter(item => item.sourceReportType === '9188-6' && item.level === 'warning')
  ), [detail]);
  const detail9188RequirementOptions = useMemo(() => (
    (detail?.requirements || [])
      .filter(item => item.sourceReportType === '9188-6' && item.status !== 'cancelled')
      .sort((left, right) => compareG081TailPriority({ counts: { overdue9188: left.level === 'overdue' ? 1 : 0, red9188: left.level === 'critical' ? 1 : 0, watch9188: left.level === 'warning' ? 1 : 0 } }, { counts: { overdue9188: right.level === 'overdue' ? 1 : 0, red9188: right.level === 'critical' ? 1 : 0, watch9188: right.level === 'warning' ? 1 : 0 } })
        || String(left.noun || '').localeCompare(String(right.noun || ''), undefined, { numeric: true }))
  ), [detail]);
  const mdsOptions = useMemo(() => (
    [...new Set((summary.tails || []).map(tail => tail.mds || 'Unspecified'))].sort()
  ), [summary.tails]);
  const filteredTails = useMemo(() => (
    filterAircraftScope(summary.tails || [], aircraftScopeFilter, mdsFilter)
  ), [summary.tails, aircraftScopeFilter, mdsFilter]);
  const availableTailsForTbdSwap = useCallback((aircraft, item) => {
    if (!isTbdTailSwapPlaceholder(item)) return [];
    const rowMds = aircraft?.mds || 'Unspecified';
    const plannedTail = String(aircraft?.tail || item?.tail || '').trim().toUpperCase();
    const swapStartMs = parseOptionalMs(item?.swapDateAt || item?.startDate || item?.endDate);
    return (summary.tails || [])
      .filter(tail => (tail.mds || 'Unspecified') === rowMds)
      .filter(tail => normalizedHsOs(tail.hsOs) === 'HS')
      .filter(tail => String(tail.tail || '').trim().toUpperCase() !== plannedTail)
      .map(tail => {
        const statusRow = statusReportByAircraftId.get(tail.id) || {};
        return { tail, statusRow };
      })
      .filter(({ tail, statusRow }) => tailAvailableForTbdSwapWindow(tail, item, statusRow))
      .map(({ tail, statusRow }) => {
        const pressure = readinessDriverPressure(statusRow);
        const openXClearance = openXClearanceSummary(statusRow, swapStartMs);
        return {
          ...tail,
          readinessDriverPressure: pressure,
          openXClearanceLabel: openXClearance.label,
        };
      })
      .sort((left, right) => (
        left.readinessDriverPressure.overdue - right.readinessDriverPressure.overdue
        || left.readinessDriverPressure.red - right.readinessDriverPressure.red
        || left.readinessDriverPressure.watch - right.readinessDriverPressure.watch
        || left.readinessDriverPressure.openK - right.readinessDriverPressure.openK
        || String(left.tail || '').localeCompare(String(right.tail || ''), undefined, { numeric: true })
      ));
  }, [summary.tails, statusReportByAircraftId]);
  const statusReportTails = useMemo(() => (
    filterAircraftScope(statusReport?.tails || [], aircraftScopeFilter, mdsFilter, row => row.aircraft)
      .sort(compareG081TailPriority)
  ), [statusReport, aircraftScopeFilter, mdsFilter]);
  const statusReportSummary = useMemo(() => summarizeStatusReportTails(statusReportTails), [statusReportTails]);
  const statusOpenXRows = useMemo(() => flattenStatusReportTailItems(statusReportTails, 'openX'), [statusReportTails]);
  const statusMissingDueStartRows = useMemo(() => flattenStatusReportTailItems(statusReportTails, 'missingDueStart'), [statusReportTails]);
  const statusOverdue9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'overdue9188'), [statusReportTails]);
  const statusRed9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'red9188'), [statusReportTails]);
  const statusWatch9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'watch9188'), [statusReportTails]);
  const statusMissingDueStartRollupRows = useMemo(() => summarizeMissingDueStartByLocation(statusMissingDueStartRows), [statusMissingDueStartRows]);
  const statusMissingDueStartTailRows = useMemo(() => summarizeMissingDueStartByTail(statusMissingDueStartRows), [statusMissingDueStartRows]);
  const selectedCrystalStatusPdfSectionCount = CRYSTAL_STATUS_PDF_SECTIONS.filter(section => crystalStatusPdfSections[section.key]).length;
  const capacityRows = useMemo(() => (
    (capacityAnalysis.rows || []).filter(row => mdsFilter === 'all' || row.mds === mdsFilter)
  ), [capacityAnalysis.rows, mdsFilter]);
  const capacitySiteRows = useMemo(() => (
    mdsFilter === 'all'
      ? (capacityAnalysis.siteRollup || [])
      : summarizeCapacityRowsByLocation(capacityRows, capacityPeriods)
  ), [capacityAnalysis.siteRollup, capacityRows, capacityPeriods, mdsFilter]);
  const capacityOvertaxedRows = useMemo(() => (
    capacityRows
      .filter(row => Number(row.shortfallHours || 0) > 0)
      .sort((left, right) => Number(right.shortfallHours || 0) - Number(left.shortfallHours || 0))
  ), [capacityRows]);
  const capacityExtraRows = useMemo(() => (
    capacityRows
      .filter(row => Number(row.spareHours || 0) > 0)
      .sort((left, right) => Number(right.spareHours || 0) - Number(left.spareHours || 0))
  ), [capacityRows]);
  const capacityPlanningRisks = useMemo(() => (
    (capacityAnalysis.planningRisks || []).filter(row => mdsFilter === 'all' || row.mds === mdsFilter)
  ), [capacityAnalysis.planningRisks, mdsFilter]);
  const capacityConcerns = useMemo(() => (
    buildCapacityConcerns(capacityRows, capacityPlanningRisks)
  ), [capacityPlanningRisks, capacityRows]);
  const capacityConcernsWithReview = useMemo(() => {
    return capacityConcerns.map(concern => ({
      ...concern,
      reviewed: Boolean(reviewedCapacityConcerns[concern.id]),
      reviewedBy: reviewedCapacityConcerns[concern.id]?.reviewedBy || '',
      reviewedAt: reviewedCapacityConcerns[concern.id]?.reviewedAt || null,
    }));
  }, [capacityConcerns, reviewedCapacityConcerns]);
  const filteredCapacityConcerns = useMemo(() => (
    capacityConcernFilter === 'reviewed'
      ? capacityConcernsWithReview.filter(concern => concern.reviewed)
      : capacityConcernsWithReview.filter(concern => (
        !concern.reviewed
        && (capacityConcernFilter === 'all' || concern.type === capacityConcernFilter)
      ))
  ), [capacityConcernFilter, capacityConcernsWithReview]);
  const capacityConcernPageCount = Math.max(1, Math.ceil(filteredCapacityConcerns.length / CAPACITY_CONCERN_PAGE_SIZE));
  const capacityConcernCurrentPage = Math.min(capacityConcernPage, capacityConcernPageCount);
  const capacityConcernStart = (capacityConcernCurrentPage - 1) * CAPACITY_CONCERN_PAGE_SIZE;
  const pagedCapacityConcerns = filteredCapacityConcerns.slice(
    capacityConcernStart,
    capacityConcernStart + CAPACITY_CONCERN_PAGE_SIZE,
  );
  const capacityShortfallInputGaps = useMemo(() => (
    capacityConcernInputGapStats(capacityConcernsWithReview)
  ), [capacityConcernsWithReview]);
  const capacityConcernSummary = useMemo(() => ({
    all: capacityConcernsWithReview.filter(concern => !concern.reviewed).length,
    critical: capacityConcernsWithReview.filter(concern => !concern.reviewed && concern.tone === 'critical').length,
    shortfall: capacityConcernsWithReview.filter(concern => !concern.reviewed && concern.type === 'shortfall').length,
    signoff: capacityConcernsWithReview.filter(concern => !concern.reviewed && concern.type === 'signoff').length,
    planning: capacityConcernsWithReview.filter(concern => !concern.reviewed && concern.type === 'planning').length,
    reviewed: capacityConcernsWithReview.filter(concern => concern.reviewed).length,
  }), [capacityConcernsWithReview]);
  const readinessPeriods = useMemo(() => (
    summary.periodsByGrain
      ? summary.periodsByGrain[readinessGrain] || (extraReadinessLayouts.source === summary ? extraReadinessLayouts.periods[readinessGrain] : null) || []
      : summary.mds?.[0]?.dates || []
  ), [summary, readinessGrain, extraReadinessLayouts]);
  useEffect(() => {
    if (!summary.periodsByGrain || summary.periodsByGrain[readinessGrain]
      || (extraReadinessLayouts.source === summary && extraReadinessLayouts.periods[readinessGrain])) {
      setReadinessPeriodsLoading(false);
      return;
    }
    let cancelled = false;
    setReadinessPeriodsLoading(true);
    api.get('/crystal-ball/readiness-periods', { params: { ...scope, readinessGrain } })
      .then(response => {
        if (cancelled) return;
        setExtraReadinessLayouts(current => ({
          source: summary,
          periods: { ...(current.source === summary ? current.periods : {}), [readinessGrain]: response.data.periods || [] },
        }));
      })
      .catch(requestError => {
        if (!cancelled) setError(requestError.response?.data?.message || 'Readiness periods could not be loaded. Switch views or refresh to retry.');
      })
      .finally(() => { if (!cancelled) setReadinessPeriodsLoading(false); });
    return () => { cancelled = true; };
  }, [api, scope, summary, readinessGrain, extraReadinessLayouts]);
  const filteredReadinessRows = useMemo(() => {
    const rows = new Map();
    filteredTails.forEach(tail => {
      const mds = tail.mds || 'Unspecified';
      if (!rows.has(mds)) rows.set(mds, { mds, assigned: 0, dates: readinessPeriods.map(period => ({ ...period, ready: 0, underWaiver: 0, unknown: 0, tbdSwapDemand: 0, alertCount: 0, alertDeduction: 0 })) });
      const row = rows.get(mds);
      row.assigned += 1;
      row.dates.forEach(period => {
        const forecast = tail.forecast;
        if (!forecast) {
          period.unknown += 1;
          return;
        }
        const state = forecastAvailableAtPeriod(forecast, period);
        period.ready += state.ready ? 1 : 0;
        period.alertCount += state.onAlert ? 1 : 0;
        period.alertDeduction += state.alertDeduction ? 1 : 0;
        period.underWaiver += state.ready && forecast.readyUnderWaiver ? 1 : 0;
        period.unknown += state.unknown ? 1 : 0;
      });
    });
    tailSwapWatchlist.forEach(({ aircraft, item }) => {
      if (!tailSwapTbdDurationMonths(item)) return;
      const mds = aircraft?.mds || 'Unspecified';
      const row = rows.get(mds);
      if (!row) return;
      row.dates.forEach(period => {
        if (tailSwapTbdActiveAtPeriod(item, period)) {
          period.tbdSwapDemand += 1;
        }
      });
    });
    rows.forEach(row => {
      row.dates.forEach(period => {
        if (period.tbdSwapDemand) {
          period.readyBeforeTbdDemand = period.ready;
          period.ready = Math.max(0, period.ready - period.tbdSwapDemand);
        }
      });
    });
    return [...rows.values()].sort((left, right) => left.mds.localeCompare(right.mds));
  }, [filteredTails, readinessPeriods, tailSwapWatchlist]);
  const readinessKpis = useMemo(() => {
    const tails = filteredTails;
    return {
      assigned: tails.length,
      readyNow: tails.filter(tail => tail.maintenanceReadiness?.ready === true).length,
      readyNowWithPlanningGaps: tails.filter(tail => tail.maintenanceReadiness?.ready === true && tail.maintenanceReadiness.planningWarnings?.length).length,
      review: tails.filter(requiresPlanReview).length,
      multiWaiver: tails.filter(tail => activeOrPendingWaiverCount(statusReportByAircraftId.get(tail.id) || tail) >= 2).length,
    };
  }, [filteredTails, statusReportByAircraftId]);
  const planReviewTails = useMemo(() => filteredTails.filter(requiresPlanReview), [filteredTails]);
  const multiWaiverTails = useMemo(() => (
    filteredTails.filter(tail => activeOrPendingWaiverCount(statusReportByAircraftId.get(tail.id) || tail) >= 2)
  ), [filteredTails, statusReportByAircraftId]);
  const filteredEtarRecords = useMemo(() => (
    sortEtarWaivers(etars.records || []).filter(row => etarMatchesSearch(row, etarSearchTerm))
  ), [etars.records, etarSearchTerm]);
  const readinessSelection = useMemo(() => {
    if (!selectedReadinessCell) return null;
    const period = readinessPeriods.find(item => item.date === selectedReadinessCell.date);
    if (!period) return null;
    const tails = filteredTails
      .filter(tail => (tail.mds || 'Unspecified') === selectedReadinessCell.mds)
      .map(tail => ({ ...tail, readinessCellState: readinessStateForPeriod(tail, period) }));
    const counts = tails.reduce((acc, tail) => {
      const key = tail.readinessCellState?.key || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return {
      ...selectedReadinessCell,
      period,
      tails,
      counts,
      label: `${selectedReadinessCell.mds} | ${readinessPeriodLabel(period)}`,
    };
  }, [filteredTails, readinessPeriods, selectedReadinessCell]);
  const evidenceTails = useMemo(() => {
    if (readinessSelection) return readinessSelection.tails;
    if (evidenceFilter === 'readyNow') return filteredTails.filter(tail => tail.maintenanceReadiness?.ready === true);
    if (evidenceFilter === 'planReview') return planReviewTails;
    if (evidenceFilter === 'multiWaiver') return multiWaiverTails;
    return filteredTails;
  }, [evidenceFilter, filteredTails, multiWaiverTails, planReviewTails, readinessSelection]);
  const evidencePeriod = readinessSelection?.period || readinessPeriods.find(period => period.date === evidencePeriodDate) || readinessPeriods[0];
  const filteredEvidenceTails = useMemo(() => evidenceTails.filter(tail => {
    if (evidenceDeploymentFilter !== 'all') {
      const statusRow = statusReportByAircraftId.get(tail.id);
      if (!statusRow || statusEvidenceState !== 'ready') return false;
      if (Boolean(statusRow.deployment?.active) !== (evidenceDeploymentFilter === 'deployed')) return false;
    }
    if (evidenceReadinessFilter === 'all') return true;
    if (!evidencePeriod) return false;
    const state = readinessStateForPeriod(tail, evidencePeriod);
    return evidenceReadinessFilter === 'ready' ? ['ready', 'waiver'].includes(state.key) : state.key === evidenceReadinessFilter;
  }).map(tail => evidenceReadinessFilter !== 'all' && evidencePeriod
    ? { ...tail, readinessCellState: readinessStateForPeriod(tail, evidencePeriod) } : tail),
  [evidenceTails, evidenceReadinessFilter, evidenceDeploymentFilter, evidencePeriod, statusReportByAircraftId, statusEvidenceState]);
  const tailDrivers = useMemo(() => [...filteredEvidenceTails].sort((left, right) => {
    const leftCounts = statusReportByAircraftId.get(left.id)?.counts || {};
    const rightCounts = statusReportByAircraftId.get(right.id)?.counts || {};
    for (const key of ['overdue9188', 'openX', 'red9188', 'watch9188', 'missingDueStart', 'form781K']) {
      const delta = Number(rightCounts[key] || 0) - Number(leftCounts[key] || 0);
      if (delta) return delta;
    }
    const rankDelta = forecastDriverRank(left) - forecastDriverRank(right);
    if (rankDelta) return rankDelta;
    const leftDate = new Date(left.forecast?.projectedReadyAt || 8640000000000000).getTime();
    const rightDate = new Date(right.forecast?.projectedReadyAt || 8640000000000000).getTime();
    if (leftDate !== rightDate) return rightDate - leftDate;
    return String(left.tail || '').localeCompare(String(right.tail || ''));
  }), [filteredEvidenceTails, statusReportByAircraftId]);
  const groupedTailDrivers = useMemo(() => [{
    label: 'Most overdue tasks first',
    tails: tailDrivers,
  }], [tailDrivers]);

  const loadAll = useCallback(async () => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    const isInitialLoad = !hasLoadedRef.current;
    if (isInitialLoad || (isHqRollup && !selectedLocations.length && !hqSiteSelectionInitializedRef.current)) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setRefreshPhase('Updating Readiness and RTG');
    setStatusEvidenceState('loading');
    setStatusReport(null);
    setSupportLoadStates({ status: 'loading', changes: 'loading', estimates: 'loading', etars: 'loading' });
    setChanges([]);
    setEstimates([]);
    setCompletionHistory([]);
    setEtars({ records: [], summary: {} });
    setError('');
    if (isHqRollup && !selectedLocations.length && hqSiteSelectionInitializedRef.current) {
      setSummary({ mds: [], tails: [], generatedAt: null });
      setRtg({ aircraft: [], supportRecommendations: [] });
      setStatusEvidenceState('idle');
      setDisplayedScopeKey(requestedScopeKey);
      setDisplayedScopeLabel(requestedScopeLabel);
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
      setRefreshPhase('');
      return;
    }
    let loadStage = isHqRollup && !selectedLocations.length ? 'HQ site list' : 'Readiness / RTG';
    try {
      const params = { params: scope };
      // An empty HQ selection means "all" to the API. Resolve the picker first
      // so visible data always comes from an explicit, reviewable site list.
      if (isHqRollup && !selectedLocations.length) {
        setRefreshPhase('Loading HQ sites');
        const response = await api.get('/crystal-ball/bootstrap', params);
        if (loadRequestRef.current !== requestId) return;
        const locations = response.data.scope?.availableLocations || [];
        const included = defaultIncludedRollupLocations(locations);
        hqSiteSelectionInitializedRef.current = true;
        setAvailableLocations(locations);
        setBootstrap(response.data);
        if (included.length) {
          setSelectedLocations(included);
        } else {
          setSummary({ mds: [], tails: [], generatedAt: null });
          setRtg({ aircraft: [], supportRecommendations: [] });
          setStatusEvidenceState('error');
          setError('No HQ sites are available. Select an available site or retry loading sites.');
        }
        return;
      }
      const [summaryRes, rtgRes] = await Promise.all([
        api.get('/crystal-ball/summary', params),
        api.get('/crystal-ball/return-to-green', params),
      ]);
      if (loadRequestRef.current !== requestId) return;
      setSummary(summaryRes.data);
      setRtg(rtgRes.data);
      setDisplayedScopeKey(requestedScopeKey);
      setDisplayedScopeLabel(requestedScopeLabel);
      hasLoadedRef.current = true;
      setLoading(false);

      setRefreshPhase('Updating remaining tabs and maintenance counts');
      loadStage = 'Supporting data';
      const failures = [];
      const requests = [
        { key: 'sites', label: 'Site list', path: '/crystal-ball/bootstrap', apply: data => {
          if (isHqRollup) setAvailableLocations(data.scope?.availableLocations || []);
          setBootstrap(data);
        } },
        { key: 'status', label: 'Backlog / Status and maintenance counts', path: '/g081-imports/status-report', apply: data => {
          setStatusReport(data || null);
          setStatusEvidenceState('ready');
        } },
        { key: 'changes', label: 'What Changed', path: '/crystal-ball/forecast-changes', apply: data => setChanges(data.changes || []) },
        { key: 'estimates', label: 'Estimate History', path: '/crystal-ball/estimate-performance', apply: data => setEstimates(data.estimates || []) },
        { key: 'completionHistory', label: 'Completed task history', path: '/crystal-ball/completion-history', params: { ...scope, limit: 200 }, apply: data => setCompletionHistory(data.records || []) },
        { key: 'etars', label: 'Waivers', path: '/crystal-ball/etars', apply: data => setEtars(data) },
      ];
      // Commit independently, but avoid flooding the backend with all of the
      // heavier rollup requests at once. Counts are in the first pair.
      const loadSection = async section => {
        const waiverVersion = waiverSaveVersionRef.current;
        const isCurrent = () => loadRequestRef.current === requestId
          && (!['status', 'etars'].includes(section.key) || waiverVersion === waiverSaveVersionRef.current);
        try {
          const response = await api.get(section.path, { params: section.params || scope, timeout: 120000 });
          if (!isCurrent()) return;
          section.apply(response.data);
          setSupportLoadStates(current => ({ ...current, [section.key]: 'ready' }));
        } catch (requestError) {
          if (!isCurrent()) return;
          if (section.key === 'status') setStatusEvidenceState('error');
          setSupportLoadStates(current => ({ ...current, [section.key]: 'error' }));
          const detail = requestError.response?.status ? `HTTP ${requestError.response.status}`
            : requestError.code === 'ECONNABORTED' ? 'request timed out' : 'connection or response error';
          failures.push(`${section.label} (${detail})`);
          setError(`Could not load: ${failures.join('; ')}. Other sections remain available. Refresh to retry.`);
        }
      };
      const pendingSections = [...requests];
      await Promise.all([0, 1].map(async () => {
        while (pendingSections.length && loadRequestRef.current === requestId) {
          await loadSection(pendingSections.shift());
        }
      }));
    } catch (requestError) {
      if (loadRequestRef.current !== requestId) return;
      setStatusEvidenceState(current => current === 'loading' ? 'error' : current);
      setSupportLoadStates(current => Object.fromEntries(Object.entries(current).map(([key, value]) => [key, value === 'loading' ? 'error' : value])));
      const detail = requestError.response?.status ? ` (HTTP ${requestError.response.status})` : '';
      setError(`${loadStage} could not be loaded${detail}. ${requestError.response?.data?.message || 'Refresh to retry.'}`);
    } finally {
      if (loadRequestRef.current === requestId) {
        setLoading(false);
        setRefreshing(false);
        setRefreshPhase('');
      }
    }
  }, [api, scope, isHqRollup, requestedScopeKey, requestedScopeLabel, selectedLocations.length]);

  const refreshCrystalBallRollups = useCallback(async () => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setRefreshing(true);
    setRefreshPhase('Refreshing Crystal Ball rollup numbers');
    setError('');
    try {
      const params = { params: scope };
      const [summaryRes, rtgRes] = await Promise.all([
        api.get('/crystal-ball/summary', params),
        api.get('/crystal-ball/return-to-green', params),
      ]);
      if (loadRequestRef.current !== requestId) return false;
      setSummary(summaryRes.data);
      setRtg(rtgRes.data);
      setDisplayedScopeKey(requestedScopeKey);
      setDisplayedScopeLabel(requestedScopeLabel);
      hasLoadedRef.current = true;
      return true;
    } finally {
      if (loadRequestRef.current === requestId) {
        setRefreshing(false);
        setRefreshPhase('');
      }
    }
  }, [api, scope, requestedScopeKey, requestedScopeLabel]);

  const recalculateForecasts = async () => {
    if (isHqRollup) return;
    setWorking(true);
    setError('');
    try {
      await api.post('/crystal-ball/recalculate', {
        email: user.email,
        location: selectedBase,
      });
      await loadAll();
      if (selectedAircraft) {
        const response = await api.get(`/crystal-ball/aircraft/${selectedAircraft.id}`, { params: scope });
        setDetail(response.data);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Crystal Ball forecasts could not be recalculated.');
    } finally {
      setWorking(false);
    }
  };

  const exportCrystalStatusReportToPDF = async () => {
    if (!crystalStatusReportRef.current) return;
    const selectedSectionKeys = new Set(
      CRYSTAL_STATUS_PDF_SECTIONS
        .filter(section => crystalStatusPdfSections[section.key])
        .map(section => section.key)
    );
    if (!selectedSectionKeys.size) return;

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);
    const sections = Array.from(crystalStatusReportRef.current.querySelectorAll('[data-pdf-section]'))
      .filter(section => {
        const key = section.getAttribute('data-pdf-section');
        return selectedSectionKeys.has(key);
      });
    let y = margin;

    for (const section of sections) {
      const canvas = await html2canvas(section, {
        backgroundColor: '#eef4fa',
        scale: CRYSTAL_STATUS_PDF_CAPTURE_SCALE,
        onclone: (documentClone) => {
          documentClone.querySelectorAll('.reports-status-export-actions, .reports-status-pdf-options').forEach(element => {
            element.style.display = 'none';
          });
        },
      });
      const imgData = canvas.toDataURL('image/jpeg', CRYSTAL_STATUS_PDF_IMAGE_QUALITY);
      const sectionHeight = (canvas.height * contentWidth) / canvas.width;
      const renderHeight = Math.min(sectionHeight, contentHeight);
      const renderWidth = sectionHeight > contentHeight
        ? (canvas.width * renderHeight) / canvas.height
        : contentWidth;

      if (y > margin && y + renderHeight > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }

      pdf.addImage(imgData, 'JPEG', margin, y, renderWidth, renderHeight, undefined, 'FAST');
      y += renderHeight + 12;
    }

    const locationLabel = isHqRollup ? `${selectedLocations.length || 'hq'}_sites` : selectedBase || 'selected_location';
    pdf.save(`crystal_ball_backlog_status_${String(locationLabel).replace(/[^a-z0-9]+/gi, '_')}.pdf`);
    setCrystalStatusPdfPickerOpen(false);
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!sitePickerOpen) return undefined;
    const handlePointerDown = event => {
      if (sitePickerRef.current && !sitePickerRef.current.contains(event.target)) {
        setSitePickerOpen(false);
      }
    };
    const handleFocusIn = event => {
      if (sitePickerRef.current && !sitePickerRef.current.contains(event.target)) {
        setSitePickerOpen(false);
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') setSitePickerOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sitePickerOpen]);

  useEffect(() => {
    if (!isHqRollup) setSitePickerOpen(false);
  }, [isHqRollup]);

  useEffect(() => {
    if (user.is_superuser || hasHqRole || globalView) setHqMode(true);
  }, [globalView, user.is_superuser, hasHqRole]);

  useEffect(() => {
    if (!isHqRollup && view === 'status') setView('overview');
  }, [isHqRollup, view]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(capacityReviewStorageKey);
      const parsed = saved ? JSON.parse(saved) : {};
      if (Array.isArray(parsed)) {
        setReviewedCapacityConcerns(Object.fromEntries(parsed.map(id => [id, { reviewedBy: 'Unknown user', reviewedAt: null }])));
      } else if (parsed && typeof parsed === 'object') {
        setReviewedCapacityConcerns(parsed);
      } else {
        setReviewedCapacityConcerns({});
      }
    } catch (storageError) {
      setReviewedCapacityConcerns({});
    }
  }, [capacityReviewStorageKey]);

  useEffect(() => {
    if (!capacityReviewFeedback) return undefined;
    const timeout = window.setTimeout(() => setCapacityReviewFeedback(''), 4500);
    return () => window.clearTimeout(timeout);
  }, [capacityReviewFeedback]);

  useEffect(() => {
    if (!tailSwapDurationFeedback) return undefined;
    if (isTailSwapDurationWorking) return undefined;
    const timeout = window.setTimeout(() => setTailSwapDurationFeedback(''), 4500);
    return () => window.clearTimeout(timeout);
  }, [isTailSwapDurationWorking, tailSwapDurationFeedback]);

  useEffect(() => {
    if (!capacityDriverModalConcern) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') setCapacityDriverModalConcern(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [capacityDriverModalConcern]);

  useEffect(() => {
    if (mdsFilter !== 'all' && !mdsOptions.includes(mdsFilter)) setMdsFilter('all');
  }, [mdsFilter, mdsOptions]);

  useEffect(() => {
    setCapacityConcernPage(1);
  }, [capacityConcernFilter, mdsFilter, requestedScopeKey]);

  useEffect(() => {
    if (capacityConcernPage > capacityConcernPageCount) {
      setCapacityConcernPage(capacityConcernPageCount);
    }
  }, [capacityConcernPage, capacityConcernPageCount]);

  useEffect(() => {
    if (selectedAircraft && !filteredTails.some(tail => tail.id === selectedAircraft.id)) {
      setSelectedAircraft(null);
      setDetail(null);
      setDetailFocus('');
    }
  }, [filteredTails, selectedAircraft]);

  useEffect(() => {
    if (!selectedReadinessCell) return;
    const hasPeriod = readinessPeriods.some(period => period.date === selectedReadinessCell.date);
    const hasMds = filteredTails.some(tail => (tail.mds || 'Unspecified') === selectedReadinessCell.mds);
    if (!hasPeriod || !hasMds) setSelectedReadinessCell(null);
  }, [filteredTails, readinessPeriods, selectedReadinessCell]);

  const toggleRollupLocation = location => {
    setSelectedLocations(current => {
      if (!current.includes(location)) return [...current, location];
      return current.filter(value => value !== location);
    });
  };

  const applyAfRcRollupPreset = () => {
    if (afrcRollupLocations.length) setSelectedLocations(afrcRollupLocations);
  };

  const loadDetail = async aircraft => {
    setSelectedAircraft(aircraft);
    setDetail(null);
    try {
      const response = await api.get(`/crystal-ball/aircraft/${aircraft.id}`, { params: scope });
      setDetail(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Aircraft detail could not be loaded.');
    }
  };

  const openCapacityConcernTail = tailValue => {
    const normalized = String(tailValue || '').trim();
    if (!normalized) return;
    const tailIdentity = { tail: normalized, serialNumber: normalized };
    const match = enhancedRtgAircraft.find(row => tailMatchesCapacityItem(tailIdentity, row))
      || filteredTails.find(row => tailMatchesCapacityItem(tailIdentity, row));
    if (!match?.id) return;
    setDetailFocus('');
    setView('overview');
    loadDetail(match);
  };

  const toggleCapacityConcernReview = concernId => {
    const wasReviewed = Boolean(reviewedCapacityConcerns[concernId]);
    setReviewedCapacityConcerns(current => {
      const next = { ...current };
      if (next[concernId]) {
        delete next[concernId];
      } else {
        next[concernId] = {
          reviewedBy: reviewActorLabel(user),
          reviewedAt: new Date().toISOString(),
        };
      }
      try {
        window.localStorage.setItem(capacityReviewStorageKey, JSON.stringify(next));
      } catch (storageError) {
        // Review state is a local convenience; failing to persist should not block queue work.
      }
      return next;
    });
    setCapacityReviewFeedback(wasReviewed
      ? 'Concern reopened and returned to the Needs Review list.'
      : 'Concern marked reviewed and moved to the Reviewed section.');
  };

  const updateTailSwapDurationDraft = (eventId, value) => {
    setTailSwapDurationDrafts(current => ({ ...current, [eventId]: value }));
  };

  const saveTailSwapTbdDuration = async (item) => {
    if (!item?.id || item.linkedTailSwap) return;
    const draftValue = tailSwapDurationDrafts[item.id] ?? item.swapTbdDurationMonths ?? '';
    const scrollPosition = typeof window !== 'undefined'
      ? { x: window.scrollX, y: window.scrollY }
      : null;
    let saveSucceeded = false;
    let savedMessage = 'TBD duration estimate saved.';
    setTailSwapDurationSaving(current => ({ ...current, [item.id]: 'saving' }));
    setTailSwapDurationFeedback('Saving TBD duration estimate...');
    setError('');
    try {
      const response = await api.patch(`/crystal-ball/tail-swaps/${item.id}/tbd-duration`, {
        ...scope,
        durationMonths: draftValue,
      });
      saveSucceeded = true;
      savedMessage = response.data?.message || savedMessage;
      setTailSwapDurationSaving(current => ({ ...current, [item.id]: 'refreshing' }));
      setTailSwapDurationFeedback('TBD duration saved. Refreshing Crystal Ball rollup numbers...');
      const refreshed = await refreshCrystalBallRollups();
      if (scrollPosition && typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => window.scrollTo(scrollPosition.x, scrollPosition.y));
        });
      }
      setTailSwapDurationDrafts(current => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      setTailSwapDurationFeedback(refreshed === false
        ? `${savedMessage} Another Crystal Ball refresh is already updating the rollup numbers.`
        : `${savedMessage} Crystal Ball rollup numbers refreshed.`);
    } catch (requestError) {
      if (saveSucceeded) {
        setTailSwapDurationFeedback(`${savedMessage} Crystal Ball rollup numbers could not be refreshed.`);
        setError(requestError.response?.data?.message || 'TBD duration saved, but Crystal Ball rollup numbers could not be refreshed. Use Refresh Crystal Ball to retry.');
      } else {
        setTailSwapDurationFeedback('');
        setError(requestError.response?.data?.message || 'TBD duration estimate could not be saved.');
      }
    } finally {
      setTailSwapDurationSaving(current => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
    }
  };

  const renderAvailableTailsCell = (aircraft, item) => {
    if (!isTbdTailSwapPlaceholder(item) || item.linkedTailSwap) {
      return <span className="crystal-ball__available-tails-inactive">Real tail linked</span>;
    }
    const candidates = availableTailsForTbdSwap(aircraft, item);
    if (!candidates.length) {
      return <span className="crystal-ball__available-tails-empty">No available HS tails shown</span>;
    }
    const visible = candidates.slice(0, 6);
    return (
      <div className="crystal-ball__available-tail-list">
        {visible.map(candidate => {
          const pressure = candidate.readinessDriverPressure || {};
          const openXLabel = candidate.openXClearanceLabel || 'No open X';
          return (
            <button
              key={candidate.id || candidate.tail}
              type="button"
              onClick={event => {
                event.stopPropagation();
                loadDetail(candidate);
              }}
              title={`${candidate.tail || 'Tail'} readiness drivers: overdue ${pressure.overdue || 0}, 10% ${pressure.red || 0}, 25% ${pressure.watch || 0}; ${openXLabel}; open K ${pressure.openK || 0}`}
            >
              <strong>{candidate.tail || '--'}</strong>
              <small>OD {pressure.overdue || 0} | 10% {pressure.red || 0} | 25% {pressure.watch || 0} | K {pressure.openK || 0}</small>
              {openXLabel !== 'No open X' ? <small>{openXLabel}</small> : null}
            </button>
          );
        })}
        {candidates.length > visible.length ? <span>+{candidates.length - visible.length} more</span> : null}
      </div>
    );
  };

  const openStatusReportDrilldown = (row, focus) => {
    if (!row?.aircraft?.id) return;
    setDetailFocus(focus);
    setView('overview');
    loadDetail(row.aircraft);
  };

  const statusCountButton = (row, focus, label) => {
    const count = Number(row.counts?.[focus] || 0);
    return (
      <button
        type="button"
        className="crystal-ball__status-count-button"
        disabled={count <= 0}
        onClick={() => openStatusReportDrilldown(row, focus)}
        aria-label={`${row.aircraft?.tail || 'Tail'} ${label}: ${count}`}
        title={count > 0 ? `View ${label} for ${row.aircraft?.tail || 'tail'}` : `No ${label}`}
      >
        {count}
      </button>
    );
  };

  const toggleMdsFilter = (mds) => {
    setMdsFilter(current => (current === mds ? 'all' : mds));
    setSelectedReadinessCell(current => (current && current.mds !== mds ? null : current));
  };

  const togglePlanReviewEvidence = () => {
    setSelectedReadinessCell(null);
    setEvidenceFilter(current => (current === 'planReview' ? '' : 'planReview'));
  };

  const toggleMultiWaiverEvidence = () => {
    setSelectedReadinessCell(null);
    setEvidenceFilter(current => (current === 'multiWaiver' ? '' : 'multiWaiver'));
  };

  const clearEvidenceSelection = () => {
    setSelectedReadinessCell(null);
    setEvidenceFilter('');
    setEvidenceReadinessFilter('all');
    setEvidenceDeploymentFilter('all');
    setEvidencePeriodDate('');
  };

  const clearDetail = () => {
    setSelectedAircraft(null);
    setDetail(null);
    setDetailFocus('');
  };

  const submitCrystalQuestion = async (nextQuestion, options = {}) => {
    const prompt = String(nextQuestion || '').trim();
    if (!prompt) return;
    setQuestion(prompt);
    setView('chat');
    setWorking(true);
    setChatWorking(true);
    setChatStartedAt(Date.now());
    setChatElapsedSeconds(0);
    setError('');
    try {
      const response = await api.post('/crystal-ball/chat', { ...scope, readinessGrain, question: prompt, ...options });
      setAnswer(response.data.answer);
      if (response.data.audit) {
        setChatHistory(current => [
          response.data.audit,
          ...current.filter(record => record.id !== response.data.audit.id),
        ].slice(0, 10));
      }
    } catch (requestError) {
      const responseMessage = requestError.response?.data?.message;
      const status = requestError.response?.status;
      if (responseMessage) {
        setError(responseMessage);
      } else if (status === 502 || status === 504) {
        setError('Crystal Ball exceeded the production response window before the backend returned an answer. Please retry; the failure was recorded as a gateway timeout.');
      } else if (requestError.code === 'ECONNABORTED') {
        setError('Crystal Ball timed out before it could return an answer. Please retry the question.');
      } else {
        setError('Crystal Ball could not answer the question because the request failed before a structured response was returned.');
      }
    } finally {
      setChatWorking(false);
      setWorking(false);
    }
  };

  const askQuestion = async event => {
    event.preventDefault();
    submitCrystalQuestion(question);
  };

  const runLaborAssessment = () => {
    submitCrystalQuestion(LABOR_ASSESSMENT_QUESTION, { assessmentType: 'labor' });
  };

  const submitExplanation = async (row, noteKey) => {
    const forecastSnapshotId = row.latest?.id;
    const note = String(explanationDrafts[noteKey] ?? row.explanation?.text ?? '').trim();
    if (!forecastSnapshotId || !noteKey) return;
    setWorking(true);
    try {
      const res = await api.post(`/crystal-ball/forecast-changes/${forecastSnapshotId}/note`, {
        ...scope,
        note,
      });
      const savedNote = res.data?.note || null;
      setChanges(current => current.map(change => (
        change.latest?.id === forecastSnapshotId
          ? { ...change, explanation: savedNote }
          : change
      )));
      setExplanationDrafts(current => {
        const next = { ...current };
        delete next[noteKey];
        return next;
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'The forecast-change note could not be saved.');
    } finally {
      setWorking(false);
    }
  };

  const importEtars = async event => {
    event.preventDefault();
    if (!etarFile) return;
    setWorking(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', etarFile);
      formData.append('email', user.email);
      formData.append('location', selectedBase);
      if (isHqRollup) {
        formData.append('global', 'true');
        formData.append('locations', selectedLocations.join('|'));
      }
      await api.post('/crystal-ball/etars/import', formData);
      setEtarFile(null);
      await loadAll();
      setView('waivers');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'FSID ETARS import failed.');
    } finally {
      setWorking(false);
    }
  };

  const linkEtarToRequirement = async (etar, requirementIds) => {
    if (!etar?.id || !Array.isArray(requirementIds) || !selectedAircraft?.id) return;
    const aircraftId = selectedAircraft.id;
    const scopeRequestId = loadRequestRef.current;
    const feedback = (status, message) => setEtarLinkFeedback(current => ({
      ...current, [etar.id]: { status, message },
    }));
    setWorking(true);
    setError('');
    feedback('saving', 'Saving ETAR task coverage...');
    try {
      const response = await api.patch(`/crystal-ball/etars/${etar.id}/requirement`, {
        ...scope,
        requirementIds,
      });
      const linkedRecord = response.data.record;
      if (loadRequestRef.current !== scopeRequestId) return;
      waiverSaveVersionRef.current += 1;
      setDetail(current => current?.aircraft?.id === aircraftId ? {
        ...current,
        etars: (current.etars || []).map(item => item.id === etar.id ? linkedRecord : item),
      } : current);
      setEtarLinkDrafts(current => ({ ...current, [etar.id]: requirementIds }));
      feedback('refreshing', 'Link saved. Refreshing aircraft evidence and readiness...');
      const refreshes = [
        { key: 'status', path: '/g081-imports/status-report', apply: data => {
          setStatusReport(data || null);
          setStatusEvidenceState('ready');
          setSupportLoadStates(current => ({ ...current, status: 'ready' }));
        } },
        { path: `/crystal-ball/aircraft/${aircraftId}`, apply: data => setDetail(current => current?.aircraft?.id === aircraftId ? data : current) },
        { key: 'etars', path: '/crystal-ball/etars', apply: data => {
          setEtars(data);
          setSupportLoadStates(current => ({ ...current, etars: 'ready' }));
        } },
        { path: '/crystal-ball/return-to-green', apply: data => setRtg(data) },
        { path: '/crystal-ball/summary', apply: data => setSummary(data) },
      ];
      let refreshFailed = false;
      // Counts apply as soon as they arrive, independently of slower forecasts.
      // Keep the same two-request limit used by the initial supporting-data load.
      await Promise.all([0, 1].map(async () => {
        while (refreshes.length && loadRequestRef.current === scopeRequestId) {
          const refresh = refreshes.shift();
          try {
            const result = await api.get(refresh.path, { params: scope, timeout: 120000 });
            if (loadRequestRef.current === scopeRequestId) refresh.apply(result.data);
          } catch {
            refreshFailed = true;
            if (loadRequestRef.current === scopeRequestId && refresh.key) {
              setSupportLoadStates(current => ({ ...current, [refresh.key]: 'error' }));
              if (refresh.key === 'status') setStatusEvidenceState('error');
            }
          }
        }
      }));
      if (loadRequestRef.current !== scopeRequestId) return;
      const requirementLabel = etarCoverageLabel(linkedRecord);
      feedback(refreshFailed ? 'warning' : 'success', refreshFailed
        ? `Saved ${requirementLabel}. Some data could not refresh; refresh Crystal Ball to update the remaining evidence.`
        : `Saved ${requirementLabel}.`);
    } catch (requestError) {
      feedback('error', requestError.response?.data?.message || 'ETAR waiver could not be linked to that 9188-6 task.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="crystal-ball reports-dashboard">
      <header className="crystal-ball__header ops-page-header reports-header">
        <div className="ops-page-header__copy">
          <div className="reports-eyebrow">Predictive Readiness</div>
          <h1 className="ops-page-header__title">Crystal Ball</h1>
          <p className="reports-subtitle">{isHqRollup ? rollupSubtitleScope : `${selectedBase} readiness projection`} <span>As of {fmtDate(summary.generatedAt, true)}</span></p>
        </div>
        <div className="crystal-ball__header-actions">
          {(loading || refreshing) && refreshPhase ? (
            <span className="crystal-ball__refresh-chip" role="status" aria-live="polite">
              <RefreshCw size={14} />{isShowingPreviousScope ? `Loading ${requestedScopeLabel}` : refreshPhase}
            </span>
          ) : null}
          <span
            className={`crystal-ball__freshness ${bootstrap.alerts?.pendingReview ? 'is-review' : 'is-current'}`}
            title={bootstrap.alerts?.pendingReview
              ? `Imported source reports pending review ${isHqRollup ? 'across the locations in this rollup' : 'at this location'}. Review them in Status under Import G081 or Import FSS; apply reviewed changes or discard the pending import.`
              : 'No source imports are pending review in this scope.'}
          >
            {bootstrap.alerts?.pendingReview ? `${bootstrap.alerts.pendingReview} source import${bootstrap.alerts.pendingReview === 1 ? '' : 's'} awaiting review` : 'Sources reviewed'}
          </span>
          {!isHqRollup && canSubmitExplanation ? (
            <button type="button" className="crystal-ball__recalculate-button" onClick={recalculateForecasts} disabled={loading || refreshing || working}>
              <RefreshCw size={16} /> Recalculate Forecasts
            </button>
          ) : null}
          <button type="button" className="crystal-ball__icon-button" onClick={loadAll} title="Refresh Crystal Ball" disabled={loading || refreshing || working}>
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      <div className="crystal-ball__scope-bar">
        {canUseHqRollup ? (
          <>
            <div className="crystal-ball__scope-modes" aria-label="Crystal Ball reporting scope">
              {!hasHqRole ? <button type="button" className={!isHqRollup ? 'is-active' : ''} onClick={() => setHqMode(false)}>Current Loc</button> : null}
              <button type="button" className={isHqRollup ? 'is-active' : ''} onClick={() => setHqMode(true)}>HQ Rollup</button>
            </div>
            {isHqRollup ? (
              <details ref={sitePickerRef} className="crystal-ball__site-picker" open={sitePickerOpen} aria-busy={isSiteScopeLoading ? 'true' : undefined}>
                <summary onClick={(event) => { event.preventDefault(); setSitePickerOpen(current => !current); }}>
                  <span>{includedSiteLabel}</span>
                  {isSiteScopeLoading ? <RefreshCw size={13} aria-hidden="true" /> : null}
                </summary>
                <div>
                  <header><strong>Sites in this rollup</strong><span>{isSiteScopeLoading ? 'Loading available locations' : 'Select at least one location'}</span></header>
                  <div className="crystal-ball__site-picker-actions">
                    <button type="button" onClick={() => setSelectedLocations(defaultIncludedRollupLocations(availableLocations))} disabled={isSiteScopeLoading || !availableLocations.length}>Select all</button>
                    <button type="button" onClick={() => setSelectedLocations([])} disabled={isSiteScopeLoading || !selectedLocations.length}>Clear all</button>
                    <button
                      type="button"
                      onClick={applyAfRcRollupPreset}
                      disabled={isSiteScopeLoading || !afrcRollupLocations.length}
                      title={afrcRollupLocations.length ? `Select ${afrcRollupLocations.length} configured AFRC location${afrcRollupLocations.length === 1 ? '' : 's'}` : undefined}
                    >
                      AFRC
                    </button>
                  </div>
                  <div className="crystal-ball__site-search">
                    <input
                      type="search"
                      value={siteSearchTerm}
                      onChange={event => setSiteSearchTerm(event.target.value)}
                      placeholder="Search location"
                      aria-label="Search rollup locations"
                    />
                    {siteSearchTerm ? <button type="button" onClick={() => setSiteSearchTerm('')}>Clear</button> : null}
                  </div>
                  <div className="crystal-ball__site-options">
                    {filteredAvailableLocations.length ? filteredAvailableLocations.map(location => (
                      <label key={location}>
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(location)}
                          onChange={() => toggleRollupLocation(location)}
                        />
                        <span>{location}</span>
                      </label>
                    )) : (
                      <p className={`crystal-ball__site-options-empty${isSiteScopeLoading ? ' is-loading' : ''}`}>
                        {isSiteScopeLoading ? <RefreshCw size={13} aria-hidden="true" /> : null}
                        {isSiteScopeLoading ? 'Loading sites...' : 'No locations found.'}
                      </p>
                    )}
                  </div>
                </div>
              </details>
            ) : <span className="crystal-ball__scope-location">{selectedBase}</span>}
          </>
        ) : <span className="crystal-ball__scope-location">{selectedBase}</span>}
        <div className="crystal-ball__filters" aria-label="Crystal Ball aircraft filters">
          <div className="crystal-ball__filter-segment" role="group" aria-label="Aircraft home station or offstation filter">
            {AIRCRAFT_SCOPE_FILTERS.map(([key, label]) => (
              <button key={key} type="button" className={aircraftScopeFilter === key ? 'is-active' : ''} onClick={() => setAircraftScopeFilter(key)}>
                {label}
              </button>
            ))}
          </div>
          <label className="crystal-ball__filter-select">
            <span>MDS</span>
            <select value={mdsFilter} onChange={event => setMdsFilter(event.target.value)}>
              <option value="all">All MDS</option>
              {mdsOptions.map(mds => <option key={mds} value={mds}>{mds}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="crystal-ball__nav-row">
        <nav className="crystal-ball__views reports-page-tabs" aria-label="Crystal Ball views">
          {viewTabs.map(([key, label]) => (
            <button key={key} type="button" className={view === key ? 'is-active' : ''} onClick={() => setView(key)}>{label}</button>
          ))}
        </nav>
      </div>

      {error ? <div className="crystal-ball__error"><AlertTriangle size={17} />{error}<button type="button" onClick={() => setError('')} title="Dismiss"><X size={16} /></button></div> : null}
      {isShowingPreviousScope ? (
        <div className="crystal-ball__scope-refreshing" role="status" aria-live="polite">
          <RefreshCw size={15} />
          <span>Loading {requestedScopeLabel}. Current tables still show {displayedScopeLabel}.</span>
        </div>
      ) : null}
      {loading ? <div className="crystal-ball__loading" role="status" aria-live="polite"><RefreshCw size={15} />Building the readiness picture...</div> : null}
      {!loading && activeSupportKey && !activeViewReady ? <div className="crystal-ball__loading" role="status" aria-live="polite">
        {activeSupportState === 'error' ? <AlertTriangle size={15} /> : <RefreshCw size={15} />}
        <span>{viewTabs.find(([key]) => key === view)?.[1] || 'This section'} {activeSupportState === 'error' ? 'could not be loaded. Its data is unavailable.' : 'is loading...'}</span>
        {activeSupportState === 'error' ? <button type="button" onClick={loadAll}>Retry loading</button> : null}
      </div> : null}

      {!loading && view === 'overview' ? (
        <div className="crystal-ball__workspace">
          <section className="crystal-ball__main">
            <div className="reports-kpi-grid crystal-ball__kpis">
              <article className="reports-kpi-card"><span className="reports-kpi-card__label">Assigned</span><strong>{readinessKpis.assigned}</strong><span className="reports-kpi-card__meta">tails in scope</span></article>
              <MaintenanceReadyKpi
                count={readinessKpis.readyNow}
                warningCount={readinessKpis.readyNowWithPlanningGaps}
                selected={evidenceFilter === 'readyNow'}
                onToggle={() => {
                  setSelectedReadinessCell(null);
                  setEvidenceFilter(current => (current === 'readyNow' ? '' : 'readyNow'));
                }}
              />
              <button
                type="button"
                className={`reports-kpi-card crystal-ball__kpi-button ${evidenceFilter === 'planReview' ? 'is-active' : ''}`}
                onClick={togglePlanReviewEvidence}
                aria-pressed={evidenceFilter === 'planReview'}
              >
                <span className="reports-kpi-card__label">Requires Plan Review</span>
                <strong>{readinessKpis.review}</strong>
                <span className="reports-kpi-card__meta">missing driver inputs or schedule logic</span>
              </button>
              <button
                type="button"
                className={`reports-kpi-card crystal-ball__kpi-button ${evidenceFilter === 'multiWaiver' ? 'is-active' : ''}`}
                onClick={toggleMultiWaiverEvidence}
                aria-pressed={evidenceFilter === 'multiWaiver'}
              >
                <span className="reports-kpi-card__label">Has 2 or more waivers</span>
                <strong>{readinessKpis.multiWaiver}</strong>
                <span className="reports-kpi-card__meta">includes active or pending status</span>
              </button>
            </div>

            <section className="reports-panel crystal-ball__readiness-panel">
              <div className="reports-panel__header crystal-ball__section-heading">
                <div><h2>Aircraft Ready to Fight</h2><p>FMC or PMC aircraft by MDS projected ready to use at Homestation by end of the selected period | {filteredTails.length} of {(summary.tails || []).length} tails shown</p></div>
                <div className="crystal-ball__rollup-controls" aria-label="Readiness rollup">
                  {READINESS_ROLLUPS.map(([key, label]) => (
                    <button key={key} type="button" className={readinessGrain === key ? 'is-active' : ''} onClick={() => setReadinessGrain(key)}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="crystal-ball__readiness-legend" aria-label="Aircraft Ready to Fight color legend">
                <span><i className="is-green" />80-100% projected ready</span>
                <span><i className="is-amber" />60-79% projected ready</span>
                <span><i className="is-red" />Less than 60% projected ready</span>
                <span><i className="is-unknown" />Unknown - tails missing critical forecast inputs are not counted as ready.</span>
              </div>
              {readinessPeriodsLoading ? <p role="status">Loading readiness periods…</p> : null}
              <div className="crystal-ball__table-scroll">
              <table className="crystal-ball__readiness-table">
                <thead><tr><th>MDS</th><th>Assigned</th>{readinessPeriods.map(day => <th key={day.date}>{readinessPeriodLabel(day)}</th>)}</tr></thead>
                <tbody>
                  {filteredReadinessRows.length ? filteredReadinessRows.map(row => (
                    <tr key={row.mds}>
                      <th>
                        <button
                          type="button"
                          className={`crystal-ball__mds-filter-button ${mdsFilter === row.mds ? 'is-active' : ''}`}
                          onClick={() => toggleMdsFilter(row.mds)}
                          title={mdsFilter === row.mds ? `Clear ${row.mds} MDS filter` : `Filter Crystal Ball to ${row.mds}`}
                          aria-pressed={mdsFilter === row.mds}
                        >
                          {row.mds}
                        </button>
                      </th><td>{row.assigned}</td>
                      {row.dates.map(day => (
                        <td key={day.date}>
                          <button
                            type="button"
                            className={[
                              'crystal-ball__readiness-cell-button',
                              selectedReadinessCell?.mds === row.mds && selectedReadinessCell?.date === day.date ? 'is-selected' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => {
                              setEvidenceFilter('');
                              setEvidencePeriodDate(day.date);
                              setSelectedReadinessCell(current => (
                                current?.mds === row.mds && current?.date === day.date
                                  ? null
                                  : { mds: row.mds, date: day.date }
                              ));
                            }}
                            title={`${day.ready} of ${row.assigned} projected ready by ${readinessPeriodLabel(day)}${day.tbdSwapDemand ? `; ${day.tbdSwapDemand} TBD tail-swap placeholder demand subtracted` : ''}${day.unknown ? `; ${day.unknown} missing forecast inputs` : ''}${day.alertCount ? `; ${day.alertCount} on Alert at period end: ${day.alertDeduction} otherwise available, ${day.alertCount - day.alertDeduction} already excluded by other forecast conditions. Each aircraft is excluded only once.` : ''}`}
                            aria-label={`Show ${row.mds} tail forecast evidence for ${readinessPeriodLabel(day)}`}
                            aria-pressed={selectedReadinessCell?.mds === row.mds && selectedReadinessCell?.date === day.date}
                          >
                            <span className={`crystal-ball__readiness-value is-${readinessTone(day.ready, row.assigned)}`}>{day.ready}</span>
                            <small>{day.ready} of {row.assigned} ready</small>
                            {day.tbdSwapDemand ? <small>-{day.tbdSwapDemand} TBD swap</small> : null}
                            {day.alertCount ? <small>-{day.alertCount} Alert</small> : null}
                            {day.unknown ? <small>{day.unknown} unknown</small> : null}
                          </button>
                        </td>
                      ))}
                    </tr>
                  )) : <tr><td colSpan={readinessPeriods.length + 2}>No aircraft match the current Crystal Ball filters.</td></tr>}
                </tbody>
              </table>
              </div>
            </section>

            <section className="reports-panel crystal-ball__tail-swap-watchlist">
              <div className="reports-panel__header crystal-ball__section-heading">
                <div>
                  <h2>Tail Swap Watchlist</h2>
                  <p>Commander view of planned swap movement in the current Crystal Ball scope | {tailSwapWatchlist.length} swap{tailSwapWatchlist.length === 1 ? '' : 's'}. TBD duration is only a placeholder until the real tail is known.</p>
                </div>
              </div>
              {tailSwapDurationFeedback ? (
                <div className={`crystal-ball__capacity-feedback crystal-ball__tail-swap-save-feedback ${isTailSwapDurationWorking ? 'is-working' : ''}`} role="status" aria-live="polite">
                  {isTailSwapDurationWorking ? <RefreshCw size={14} /> : null}
                  <span>{tailSwapDurationFeedback}</span>
                </div>
              ) : null}
              <div className="crystal-ball__table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Planned Date</th>
                      <th>Tail</th>
                      <th>MDS</th>
                      <th>Swap Direction</th>
                      <th>Homestation | Current Location</th>
                      <th>Swap With/For</th>
                      <th>Projected Location</th>
                      <th><span>TBD Duration</span><small>(only when tail TBD)</small></th>
                      <th>
                        <span className="crystal-ball__header-help-label">
                          Available Tails
                          <details className="crystal-ball__header-help">
                            <summary aria-label="Available tails help" title={AVAILABLE_TAILS_HELP_TEXT}>
                              <CircleHelp size={14} aria-hidden="true" />
                            </summary>
                            <span>{AVAILABLE_TAILS_HELP_TEXT}</span>
                          </details>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tailSwapWatchlist.length ? tailSwapWatchlist.map(({ id, aircraft, item, swapAt, pairKey, pairTone }) => (
                      <tr
                        key={id}
                        className={[
                          'crystal-ball__tail-swap-row',
                          pairKey ? 'is-paired' : '',
                          pairTone ? `is-pair-tone-${pairTone}` : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => { setView('overview'); loadDetail(aircraft); }}
                      >
                        <td>{fmtPlanDate(swapAt)}</td>
                        <td><strong>{aircraft.tail || '--'}</strong></td>
                        <td>{aircraft.mds || '--'}</td>
                        <td><span className={`crystal-ball__swap-direction is-${String(item.tailSwapRole || 'swap').toLowerCase() || 'swap'}`}>{tailSwapDirectionLabel(item)}</span></td>
                        <td>{aircraft.location || '--'} | <AircraftStationDisplay aircraft={aircraft} /></td>
                        <td>{item.swapWithTail || '--'}</td>
                        <td><OffstationLocationDisplay value={tailSwapLocationText(item.planLocation, item.swapForeignOsMasked)} emptyLabel="--" /></td>
                        <td className={isTbdTailSwapPlaceholder(item) && !item.linkedTailSwap ? '' : 'crystal-ball__tbd-duration-cell--inactive'}>
                          {isTbdTailSwapPlaceholder(item) && !item.linkedTailSwap ? (
                            <div className="crystal-ball__tbd-duration-control">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={tailSwapDurationDrafts[item.id] ?? item.swapTbdDurationMonths ?? ''}
                                onChange={event => updateTailSwapDurationDraft(item.id, event.target.value)}
                                onClick={event => event.stopPropagation()}
                                disabled={Boolean(tailSwapDurationSaving[item.id])}
                                placeholder="Months"
                                aria-label={`TBD placeholder duration in months for ${aircraft.tail || 'tail swap'}`}
                              />
                              <button
                                type="button"
                                onClick={event => {
                                  event.stopPropagation();
                                  saveTailSwapTbdDuration(item);
                                }}
                                disabled={Boolean(tailSwapDurationSaving[item.id])}
                              >
                                {tailSwapDurationSaving[item.id] === 'refreshing' ? 'Refreshing...' : tailSwapDurationSaving[item.id] ? 'Saving...' : 'Save'}
                              </button>
                              <small>{tailSwapTbdDurationLabel(item)} placeholder only</small>
                            </div>
                          ) : (
                            <span className="crystal-ball__tbd-duration-inactive">Real tail linked</span>
                          )}
                        </td>
                        <td>{renderAvailableTailsCell(aircraft, item)}</td>
                      </tr>
                    )) : <tr><td colSpan="9">No tail swaps match the current Crystal Ball scope.</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="reports-panel crystal-ball__tail-panel">
            <div className="reports-panel__header crystal-ball__section-heading crystal-ball__section-heading--tails">
              <div>
                <h2>Tail Forecast Evidence</h2>
                <p>
                  {readinessSelection
                    ? `${readinessSelection.label} selection | ${readinessSelection.tails.length} tails in selected cell | ${readinessSelection.counts.ready || 0} ready, ${readinessSelection.counts.waiver || 0} waiver, ${readinessSelection.counts['not-ready'] || 0} not ready, ${readinessSelection.counts.unknown || 0} unknown`
                    : evidenceFilter === 'readyNow'
                      ? `Maintenance Ready Now | ${tailDrivers.length} mission-capable tail${tailDrivers.length === 1 ? '' : 's'} with no known maintenance blockers throughout the next 30 days`
                    : evidenceFilter === 'planReview'
                      ? `Requires Plan Review | ${tailDrivers.length} tails with missing readiness-driver inputs or schedule logic issues`
                    : evidenceFilter === 'multiWaiver'
                      ? `Has 2 or more waivers | ${tailDrivers.length} tails with 2 or more waivers; includes active or pending status`
                    : 'Most overdue tasks first; open X, due 10%, then due 25% break ties. HS and OS tails ranked together.'}
                </p>
              </div>
              {readinessSelection || evidenceFilter || evidenceReadinessFilter !== 'all' || evidenceDeploymentFilter !== 'all' ? <button type="button" className="crystal-ball__clear-selection" onClick={clearEvidenceSelection}>Show all evidence</button> : null}
            </div>
            <div className="crystal-ball__backlog-controls" role="group" aria-label="Tail Forecast Evidence filters">
              <label>Period <select aria-label="Evidence forecast period" value={evidencePeriod?.date || ''} disabled={!readinessPeriods.length}
                onChange={event => {
                  const date = event.target.value;
                  setEvidencePeriodDate(date);
                  setSelectedReadinessCell(current => current ? { ...current, date } : current);
                }}>
                {!readinessPeriods.length ? <option value="">No forecast periods available</option> : readinessPeriods.map(period => <option key={period.date} value={period.date}>{readinessPeriodLabel(period)}</option>)}
              </select></label>
              <label>Readiness at period end <select aria-label="Evidence period readiness" value={evidenceReadinessFilter} disabled={!evidencePeriod}
                onChange={event => setEvidenceReadinessFilter(event.target.value)}>
                <option value="all">All readiness states</option><option value="ready">Ready</option><option value="not-ready">Not ready</option><option value="unknown">Unknown forecast</option>
              </select></label>
              <label>Deployment now (ACFT Plans) <select aria-label="Evidence deployment status" value={evidenceDeploymentFilter} disabled={statusEvidenceState !== 'ready'}
                onChange={event => setEvidenceDeploymentFilter(event.target.value)}>
                <option value="all">All</option><option value="deployed">Deployed</option><option value="not-deployed">Not deployed</option>
              </select></label>
              <span>{tailDrivers.length} of {evidenceTails.length} tails shown</span>
            </div>
            {statusEvidenceState !== 'ready' ? <p className="crystal-ball__status-note">Deployment filtering {statusEvidenceState === 'loading' ? 'is loading' : 'is unavailable'} until ACFT Plans deployment status loads.</p> : null}
            <div className="crystal-ball__tail-list">
              {groupedTailDrivers.map(group => (
                <React.Fragment key={group.label}>
                  <div className="crystal-ball__tail-group-heading">
                    <span>{group.label}</span>
                    <strong>{group.tails.length}</strong>
                  </div>
                  {group.tails.map(tail => {
                    const forecast = tail.forecast;
                    const reviewItems = planReviewItems(forecast);
                    const g081Counts = statusReportByAircraftId.get(tail.id)?.counts;
                    const warnings = scheduleWarnings(forecast);
                    const warningText = warnings.map(item => item.message || item.label || 'Schedule dates need review').join(' | ');
                    return (
                      <button key={tail.id} type="button" className={selectedAircraft?.id === tail.id ? 'is-selected' : ''} onClick={() => { setDetailFocus(''); loadDetail(tail); }}>
                        <span className="crystal-ball__tail-identity">
                          <strong><span>{tail.tail}</span><CurrentStatusPill status={tail.status} /></strong>
                          <small>{tail.mds || 'Unspecified'} | <AircraftStationDisplay aircraft={tail} /></small>
                        </span>
                        <span className="crystal-ball__tail-timing">
                          <strong>{tailTimingLabel(tail)}</strong>
                          <small>{tail.readinessCellState?.onAlert ? 'Availability driver: Alert Status' : forecast?.authorizedReadyNow ? 'Mission capable, no maintenance blockers' : `Forecast driver: ${forecastDriverLabel(tail)}`}</small>
                          {tail.readinessCellState?.onAlert && !forecast?.authorizedReadyNow ? <small>Maintenance forecast driver: {forecastDriverLabel(tail)}</small> : null}
                          {tail.maintenanceReadiness ? <small title={(tail.maintenanceReadiness.blockers || []).map(item => `${item.label}: ${item.reason}`).join('\n')}>
                            {tail.maintenanceReadiness.ready ? (tail.maintenanceReadiness.planningWarnings?.length ? '30-day Mx gate: No known blockers*' : '30-day Mx gate: Clear') : `30-day Mx gate: ${tail.maintenanceReadiness.blockers?.[0]?.label || 'Needs review'}`}
                          </small> : null}
                          {tail.maintenanceReadiness?.ready && tail.maintenanceReadiness.planningWarnings?.length ? <small className="crystal-ball__planning-footnote">* Counted with planning gaps</small> : null}
                          {reviewItems.length ? <small className="crystal-ball__tail-plan-review">{reviewItems.length} plan review item{reviewItems.length === 1 ? '' : 's'}</small> : null}
                          {g081Counts ? <>
                            <small className="crystal-ball__tail-maintenance-counts">Open X {g081Counts.openX || 0} | Overdue {g081Counts.overdue9188 || 0} | Due 10% {g081Counts.red9188 || 0} | Due 25% {g081Counts.watch9188 || 0}</small>
                            <small className="crystal-ball__tail-six-counts">Drivers missing dates {g081Counts.missingDueStart || 0} | 781K {g081Counts.form781K || 0} | {tailWaiverCoverageLabel({ counts: g081Counts }, true)}</small>
                          </> : <small className="crystal-ball__tail-counts-unavailable">{statusEvidenceState === 'loading' ? 'Loading maintenance counts...' : 'Maintenance counts unavailable; priority may be incomplete'}</small>}
                          {tail.etic && !forecast?.authorizedReadyNow ? <small>Projected ready source: current Status ETIC</small> : null}
                          {tail.readinessCellState ? <small className={`crystal-ball__tail-state is-${tail.readinessCellState.key}`}>{tail.readinessCellState.label}</small> : null}
                        </span>
                        {warnings.length ? <span className="is-review" title={`Schedule review: ${warningText}`}><AlertTriangle size={18} aria-label={`Schedule review: ${warningText}`} /></span> : forecast?.readyUnderWaiver ? <ShieldCheck size={18} className="is-waiver" /> : <ChevronRight size={18} />}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
              {!tailDrivers.length ? <p>No tail forecast evidence is available for this scope.</p> : null}
            </div>
            </section>
          </section>

          <aside className={`crystal-ball__detail ${selectedAircraft ? 'is-open' : ''}`}>
            {selectedAircraft ? (
              <>
                <div className="crystal-ball__detail-header">
                  <div>
                    <span>{selectedAircraft.mds}</span>
                    <h2>{selectedAircraft.tail}</h2>
                  </div>
                  <button type="button" onClick={clearDetail} title="Close tail detail"><X size={18} /></button>
                </div>
                {!detail ? <p>Loading evidence...</p> : (
                  <>
                    <dl className="crystal-ball__facts">
                      <div><dt>Projected ready</dt><dd>{detail.forecast?.authorizedReadyNow ? 'Ready now' : fmtDate(detail.forecast?.projectedReadyAt, true)}</dd></div>
                      <div><dt>Current status</dt><dd><CurrentStatusPill status={detail.aircraft.status} /></dd></div>
                      <div><dt>Current ETIC</dt><dd>{fmtDayMonthYear(detail.aircraft.etic)}</dd></div>
                      <div><dt>Confidence</dt><dd>{detail.forecast?.confidence || 'Not calculated'}</dd></div>
                      <div><dt>Primary driver</dt><dd>{detail.forecast?.primaryDriver || '--'}</dd></div>
                      <div><dt>HS/OS</dt><dd><AircraftStationDisplay aircraft={detail.aircraft} /></dd></div>
                      <div><dt>Offstation return</dt><dd>{fmtDate(detail.aircraft.actualReturnAt || detail.aircraft.estimatedReturnAt, true)}</dd></div>
                      <div><dt>Home station</dt><dd>{detail.aircraft.location || '--'}</dd></div>
                    </dl>
                    {detail.tailSwaps?.length ? (
                      <>
                        <h3>Tail Swap Plans</h3>
                        <div className="crystal-ball__requirements crystal-ball__tail-swaps">
                          {detail.tailSwaps.map(item => (
                            <article key={item.id}>
                              <div>
                                <strong>{item.eventType || 'Tail Swap'}</strong>
                                <span>{fmtPlanDate(item.swapDateAt)} | swap with/for {item.swapWithTail || '--'}</span>
                                <span>Swap base: <OffstationLocationDisplay value={item.swapWithLocation} emptyLabel="--" />{item.planLocation ? <> | Projected location: <OffstationLocationDisplay value={tailSwapLocationText(item.planLocation, item.swapForeignOsMasked)} emptyLabel="--" /></> : null}</span>
                                {item.remarks ? <span>{item.remarks}</span> : null}
                              </div>
                            </article>
                          ))}
                        </div>
                      </>
                    ) : null}
                    {detail.forecast?.missingInputs?.length ? <div className="crystal-ball__missing"><strong>Missing inputs</strong>{detail.forecast.missingInputs.map(item => <span key={item}>{item}</span>)}</div> : null}
                    {detailPlanningWarnings.length ? <div className="crystal-ball__missing">
                      <strong>Readiness planning gaps</strong>
                      {detailPlanningWarnings.map((item, index) => <span key={`${item.type}-${item.id || index}`}>{item.label}: {item.reason}</span>)}
                    </div> : null}
                    {scheduleWarnings(detail.forecast).length ? <div className="crystal-ball__missing crystal-ball__missing--review"><strong>Schedule review</strong>{scheduleWarnings(detail.forecast).map(item => <span key={`${item.entityType}-${item.id}`}>{item.message}</span>)}</div> : null}
                    <p className="crystal-ball__detail-note">The following are all items from the -6 scheduled maintenance that are overdue or coming due.</p>
                    <div className={[detailFocus === 'overdue9188' ? 'is-focused' : '', 'crystal-ball__detail-title', 'is-overdue-9188'].filter(Boolean).join(' ')}><h3>9188 -6 Overdue</h3></div>
                    <div className={detailFocus === 'overdue9188' ? 'crystal-ball__requirements is-focused' : 'crystal-ball__requirements'}>
                      {detailOverdueRequirements.length ? detailOverdueRequirements.map(requirement => (
                        <article key={requirement.id}>
                          <div><strong>{requirement.noun}</strong><span>{requirement.wuc || 'No WUC'} | {requirement.shopName || 'Unassigned'} | <b className="crystal-ball__overdue-days">{requirement.overdueDays}d overdue</b></span></div>
                          <RequirementReadinessDriverTag requirement={requirement} />
                          {requirement.scheduleWarning ? <span className="crystal-ball__review-warning"><AlertTriangle size={14} />Review start</span> : null}
                          <RequirementWaiverLinks requirement={requirement} etars={detail.etars} />
                        </article>
                      )) : <p>No overdue 9188-6 inspections for this tail.</p>}
                    </div>
                    {detailRedRequirements.length || detailFocus === 'red9188' ? (
                      <>
                        <div className={detailFocus === 'red9188' ? 'crystal-ball__detail-title is-focused' : 'crystal-ball__detail-title'}><h3>9188 -6 Due Soon (10%)</h3></div>
                        <div className={detailFocus === 'red9188' ? 'crystal-ball__requirements is-focused' : 'crystal-ball__requirements'}>
                          {detailRedRequirements.length ? detailRedRequirements.map(requirement => (
                            <article key={requirement.id}>
                              <div><strong>{requirement.noun}</strong><span>{requirement.wuc || 'No WUC'} | {requirement.shopName || 'Unassigned'} | {requirement.remainingLabel || 'remaining unknown'}</span></div>
                              <RequirementReadinessDriverTag requirement={requirement} />
                              {requirement.scheduleWarning ? <span className="crystal-ball__review-warning"><AlertTriangle size={14} />Review start</span> : null}
                              <RequirementWaiverLinks requirement={requirement} etars={detail.etars} />
                            </article>
                          )) : <p>No 9188 -6 items are inside the 10% due-soon window for this tail.</p>}
                        </div>
                      </>
                    ) : null}
                    {detailWatchRequirements.length || detailFocus === 'watch9188' ? (
                      <>
                        <div className={detailFocus === 'watch9188' ? 'crystal-ball__detail-title is-focused' : 'crystal-ball__detail-title'}><h3>9188 -6 Due Soon (25%)</h3></div>
                        <div className={detailFocus === 'watch9188' ? 'crystal-ball__requirements is-focused' : 'crystal-ball__requirements'}>
                          {detailWatchRequirements.length ? detailWatchRequirements.map(requirement => (
                            <article key={requirement.id}>
                              <div><strong>{requirement.noun}</strong><span>{requirement.wuc || 'No WUC'} | {requirement.shopName || 'Unassigned'} | {requirement.remainingLabel || 'remaining unknown'}</span></div>
                              <RequirementReadinessDriverTag requirement={requirement} />
                              {requirement.scheduleWarning ? <span className="crystal-ball__review-warning"><AlertTriangle size={14} />Review start</span> : null}
                              <RequirementWaiverLinks requirement={requirement} etars={detail.etars} />
                            </article>
                          )) : <p>No 9188 -6 items are inside the 25% due-soon watch window for this tail.</p>}
                        </div>
                      </>
                    ) : null}
                    <h3>Readiness Driver Planned Work After Offstation Return</h3>
                    <div className="crystal-ball__requirements">
                      {returnDriverJobs.length ? returnDriverJobs.map(item => (
                        <article key={item.id}>
                          <div>
                            <strong>{item.title}</strong>
                            <span>{item.source} | {item.shopName || 'Unassigned'}{item.wuc ? ` | WUC ${item.wuc}` : ''}{item.symbolCode ? ` | SYM ${item.symbolCode}` : ''} | {plannedWorkDateLabel('Start', item.plannedStartAt)} | {plannedWorkDateLabel('Finish', item.plannedFinishAt)}</span>
                            {item.description ? <span>{item.description}</span> : null}
                          </div>
                          {item.scheduleWarning ? <span className="crystal-ball__review-warning"><AlertTriangle size={14} />Review start</span> : null}
                        </article>
                      )) : <p>{hasOffstationReturnDate ? 'No readiness-driver work is planned after the offstation return date.' : 'No offstation return date is set for this tail.'}</p>}
                    </div>
                    <h3>FSID ETARS</h3>
                    <div className="crystal-ball__requirements">
                      {detail.etars?.length ? sortEtarWaivers(detail.etars).map(item => {
                        const linkedTasks = matchedEtarTasks(item);
                        const draftRequirementIds = etarLinkDrafts[item.id] ?? linkedTasks.map(task => task.id);
                        const linksChanged = [...draftRequirementIds].sort().join(',') !== linkedTasks.map(task => task.id).sort().join(',');
                        const linkFeedback = etarLinkFeedback[item.id];
                        const canLink = canSubmitExplanation && detail9188RequirementOptions.length > 0;
                        const issuedAt = etarIssuedDate(item);
                        return (
                          <article key={item.id}>
                            <div>
                              <strong>{etarDisplayTitle(item)}</strong>
                              <span>{item.etarNumber} | {item.classification || 'No classification'} | {matchLabel(item.matchStatus)} | {etarWaiverStatusLabel(item)}{issuedAt ? ` | issued ${fmtDate(issuedAt)}` : ''}</span>
                              {linkedTasks.length ? <span>{etarCoverageLabel(item)}: {linkedTasks.map(task => task.noun || task.wuc || 'task').join(', ')}</span> : null}
                              {canLink ? (
                                <div className="crystal-ball__etar-link">
                                  <EtarTaskPicker
                                    etarNumber={item.etarNumber}
                                    requirements={detail9188RequirementOptions}
                                    selectedIds={draftRequirementIds}
                                    disabled={working}
                                    saved={['refreshing', 'success', 'warning'].includes(linkFeedback?.status)}
                                    onChange={ids => setEtarLinkDrafts(current => ({ ...current, [item.id]: ids }))}
                                  />
                                  <button
                                    type="button"
                                    disabled={working || !linksChanged}
                                    onClick={() => linkEtarToRequirement(item, draftRequirementIds)}
                                  >
                                    {linkFeedback?.status === 'saving' ? 'Linking...' : linkFeedback?.status === 'refreshing' ? 'Refreshing...' : !linksChanged && linkedTasks.length ? 'Linked' : 'Save links'}
                                  </button>
                                </div>
                              ) : null}
                              {linkFeedback ? <span className={`crystal-ball__etar-link-feedback is-${linkFeedback.status}`} role={linkFeedback.status === 'error' ? 'alert' : 'status'}>{linkFeedback.message}</span> : null}
                            </div>
                            <span className={`crystal-ball__waiver ${etarWaiverStatusClass(item)}`}><ShieldCheck size={14} />{etarWaiverStatusLabel(item)}</span>
                          </article>
                        );
                      }) : <p>No FSID ETARS waivers matched to this tail.</p>}
                    </div>
                  </>
                )}
              </>
            ) : <div className="crystal-ball__detail-empty"><ChevronRight size={22} /><p>Select a tail to answer why and inspect the evidence.</p></div>}
          </aside>
        </div>
      ) : null}

      {!loading && view === 'rtg' ? (
        <div className="crystal-ball__single-view">
          <div className="crystal-ball__section-heading">
            <div>
              <h2>Return to Green</h2>
              <p>Date axis shows offstation return gate, planned readiness-driver work, and projected mission-capable date | {filteredRtgAircraft.length} of {baseRtgAircraft.length} tails shown</p>
            </div>
            <div className="crystal-ball__rtg-heading-controls">
              <label className="crystal-ball__toggle-filter">
                <input type="checkbox" checked={tailSwapsOnly} onChange={event => setTailSwapsOnly(event.target.checked)} />
                <span>Tail swaps only</span>
              </label>
              <button
                type="button"
                className="crystal-ball__stacked-toggle"
                onClick={() => setRtgStackedRows(current => !current)}
                aria-label={`Switch to ${rtgStackedRows ? 'unstacked' : 'stacked'} RTG view`}
              >
                {rtgStackedRows ? 'Unstacked' : 'Stacked'}
              </button>
              <div className="crystal-ball__rollup-controls" aria-label="RTG timeline scale">
                {READINESS_ROLLUPS.map(([key, label]) => (
                  <button key={key} type="button" className={rtgTimelineGrain === key ? 'is-active' : ''} onClick={() => setRtgTimelineGrain(key)}>{label}</button>
                ))}
              </div>
              <div className="crystal-ball__rtg-zoom-controls" aria-label="RTG timeline zoom">
                <button
                  type="button"
                  onClick={() => setRtgTimelineZoom(current => RTG_TIMELINE_ZOOM_STEPS[Math.max(0, RTG_TIMELINE_ZOOM_STEPS.indexOf(current) - 1)])}
                  disabled={rtgTimelineZoom === RTG_TIMELINE_ZOOM_STEPS[0]}
                  aria-label="Zoom RTG timeline out"
                  title="Zoom RTG timeline out"
                >-</button>
                <button
                  type="button"
                  className="crystal-ball__rtg-zoom-level"
                  onClick={() => setRtgTimelineZoom(RTG_TIMELINE_ZOOM_STEPS[0])}
                  disabled={rtgTimelineZoom === RTG_TIMELINE_ZOOM_STEPS[0]}
                  aria-label={`Reset RTG timeline zoom, currently ${rtgTimelineZoom}%`}
                  title="Reset RTG timeline zoom"
                >{rtgTimelineZoom}%</button>
                <button
                  type="button"
                  onClick={() => setRtgTimelineZoom(current => RTG_TIMELINE_ZOOM_STEPS[Math.min(RTG_TIMELINE_ZOOM_STEPS.length - 1, RTG_TIMELINE_ZOOM_STEPS.indexOf(current) + 1)])}
                  disabled={rtgTimelineZoom === RTG_TIMELINE_ZOOM_STEPS[RTG_TIMELINE_ZOOM_STEPS.length - 1]}
                  aria-label="Zoom RTG timeline in"
                  title="Zoom RTG timeline in"
                >+</button>
              </div>
            </div>
          </div>
          <div className="crystal-ball__rtg-legend" aria-label="Return to Green timeline legend">
            <span><i className="is-work" />RTG blocked / in work</span>
            <span><i className="is-ready-window" />Ready window</span>
            <button
              type="button"
              className={rtgHighlightSet.has(RTG_RETURN_HIGHLIGHT) ? 'is-selected' : ''}
              aria-pressed={rtgHighlightSet.has(RTG_RETURN_HIGHLIGHT)}
              onClick={() => toggleRtgHighlight(RTG_RETURN_HIGHLIGHT)}
              title="Highlight return dates in the timeline"
            ><i className="is-return" />Return date</button>
            <button
              type="button"
              className={rtgHighlightSet.has(RTG_TAIL_SWAP_HIGHLIGHT) ? 'is-selected' : ''}
              aria-pressed={rtgHighlightSet.has(RTG_TAIL_SWAP_HIGHLIGHT)}
              onClick={() => toggleRtgHighlight(RTG_TAIL_SWAP_HIGHLIGHT)}
              title="Highlight tail swap markers in the timeline"
            ><i className="is-tail-swap" />Tail swap marker</button>
            <span><i className="is-ready" />Projected usable point</span>
          </div>
            <div className="crystal-ball__rtg-driver-legend" aria-label="RTG readiness driver color legend">
              {STACKED_RTG_DRIVER_CATEGORIES.map(category => (
                <button
                  type="button"
                  key={category.key}
                  className={rtgHighlightSet.has(category.key) ? 'is-selected' : ''}
                  aria-pressed={rtgHighlightSet.has(category.key)}
                  onClick={() => toggleRtgHighlight(category.key)}
                  title={`Highlight ${category.label} work in the timeline`}
                ><i className={category.className} />{category.label}</button>
              ))}
              {rtgHighlightsActive ? <button
                type="button"
                className="crystal-ball__rtg-clear-highlights"
                onClick={() => setRtgHighlights([])}
              >Clear highlights</button> : null}
            </div>
          <p className="crystal-ball__status-note">{rtgStackedRows
            ? 'Select one or more legend items to highlight them.'
            : 'Select one or more legend items to highlight them. Alert appears below maintenance, and overlapping maintenance items use separate lanes.'}</p>
          <section
            className={rtgStackedRows ? 'crystal-ball__rtg-timeline is-stacked' : 'crystal-ball__rtg-timeline'}
            aria-label="Return to Green timeline"
            style={{ '--rtg-timeline-zoom': rtgTimelineZoom / 100 }}
          >
            <header>
              <span>Tail</span>
              <div className={`crystal-ball__rtg-axis is-${rtgTimelineGrain}`}>
                {rtgTimeline.ticks.map(tick => (
                  <span key={tick.id} style={{ left: tick.left, width: tick.width }}>
                    <strong>{tick.label}</strong>
                  </span>
                ))}
              </div>
              {!rtgStackedRows ? <span>Ready</span> : null}
            </header>
            <div className="crystal-ball__rtg-timeline-body">
              {rtgTimeline.groups.length ? rtgTimeline.groups.map(group => (
                <React.Fragment key={`timeline-group-${group.id}`}>
                  <div className="crystal-ball__rtg-mds-divider">
                    <strong>{group.label}</strong>
                    <span>{group.mdsLabel}</span>
                    <span>{group.rows.length} tail{group.rows.length === 1 ? '' : 's'}</span>
                    <span>{group.notReady} not ready</span>
                    <span>{group.inputNeeds} need inputs</span>
                    {group.latestReadyMs ? <span>Latest {fmtDate(group.latestReadyMs, true)}</span> : null}
                  </div>
                  {group.rows.map(row => (
                    <button type="button" key={`timeline-${row.id}`} onClick={() => { setDetailFocus(''); setView('overview'); loadDetail(row); }}>
                      <span>
                        <strong>{row.tail}</strong>
                        {rtgStackedRows
                          ? <small className="crystal-ball__rtg-stacked-meta">{row.mds || 'Unspecified'} | {normalizedHsOs(row.hsOs)}</small>
                          : <small>{row.mds} | <AircraftStationDisplay aircraft={row} /></small>}
                        {row.tailSwaps?.length ? <small className="crystal-ball__tail-swap-summary">{tailSwapSummary(row.tailSwaps[0])}</small> : null}
                      </span>
                      <i aria-label={`Timeline for ${row.tail}`} style={{ height: rtgStackedRows ? row.stackedWork.height : row.unstackedWork.height }}>
                        {rtgTimeline.ticks.map(tick => <em key={tick.id} style={{ left: tick.lineLeft || tick.left }} />)}
                        {row.readyWindows.map(window => <b key={window.id} className={`is-ready-window${rtgHighlightsActive ? ' is-rtg-muted' : ''}`} style={{ left: window.left, width: window.width }} title={window.label} />)}
                        {row.returnLeft ? (
                          <b className={`is-return${rtgHighlightsActive ? (rtgHighlightSet.has(RTG_RETURN_HIGHLIGHT) ? ' is-rtg-highlighted' : ' is-rtg-muted') : ''}`} style={{ left: row.returnLeft }} title={`Return date ${fmtDate(row.returnAt)}`}>
                            <span>Return</span>
                          </b>
                        ) : null}
                        {row.tailSwapMarkers.map(marker => (
                          <b key={marker.id} className={`is-tail-swap is-${marker.role}${rtgHighlightsActive ? (rtgHighlightSet.has(RTG_TAIL_SWAP_HIGHLIGHT) ? ' is-rtg-highlighted' : ' is-rtg-muted') : ''}`} style={{ left: marker.left }} title={marker.title}>
                            <span>{marker.shortLabel}</span>
                          </b>
                        ))}
                        {rtgStackedRows ? <>
                          {row.availabilityWindows.map(window => <b key={window.id} className={`is-work${rtgHighlightsActive ? ' is-rtg-muted' : ''}`} style={{ left: window.left, width: window.width }} title={window.label} />)}
                          {row.stackedWork.segments.map(segment => {
                            const containsHighlight = segment.driverKeys.some(key => rtgHighlightSet.has(key));
                            return (
                            <span
                              key={segment.id}
                              className={`crystal-ball__rtg-work-segment ${segment.driverClass}${segment.estimated ? ' is-estimated' : ''}${segment.hasDivider ? ' has-driver-boundary' : ''}${rtgHighlightsActive ? (containsHighlight ? ' is-rtg-highlighted' : ' is-rtg-muted') : ''}`}
                              style={{ left: segment.left, width: segment.width, top: segment.top, height: segment.height,
                                backgroundImage: stackedDriverStripeBackground(segment.driverCategories, highlightedDriverKeys, rtgHighlightsActive) }}
                              title={segment.label}
                              aria-label={segment.label}
                            >
                              {segment.showLabel ? <small>{segment.shortLabel}</small> : null}
                            </span>
                            );
                          })}
                        </> : <UnstackedRtgWork windows={row.availabilityWindows} layout={row.unstackedWork}
                          highlightSet={rtgHighlightSet} highlightsActive={rtgHighlightsActive} />}
                        {row.readyMarkers.map(marker => <b key={marker.id} className={`is-ready${rtgHighlightsActive ? ' is-rtg-muted' : ''}`} style={{ left: marker.left }} title={marker.title} />)}
                        {row.showPlanMissing && (!rtgStackedRows || !row.forecast) ? <span className="crystal-ball__rtg-plan-missing">{row.forecast ? 'Plan missing' : 'Forecast missing'}</span> : null}
                      </i>
                      {!rtgStackedRows ? <span><strong>{forecastReadyLabel(row)}</strong><small>{row.forecast?.primaryDriver || '--'}</small><WorkloadRealismPill assessment={row.workloadRealism} /><LaborRiskChips assessment={row.workloadRealism} /></span> : null}
                    </button>
                  ))}
                </React.Fragment>
              )) : <p className="crystal-ball__empty-row">No Return to Green aircraft match the current filters.</p>}
            </div>
          </section>
          <div className="crystal-ball__table-scroll"><table><thead><tr><th>Priority</th><th>Tail</th><th>Location</th><th>HS/OS</th><th>MDS</th><th>Status</th><th>ETIC</th><th>Return Date</th><th>Tail Swap</th><th>Projected Ready</th><th>Labor / Workload Risk</th><th>OD -6</th><th>Waiver Coverage</th><th>Waiver</th><th>781Ks</th><th>Open X</th><th>Inputs Needed</th><th>Primary Driver</th></tr></thead><tbody>{enhancedRtgAircraft.map((row, index) => <tr key={row.id} onClick={() => { setView('overview'); loadDetail(row); }}><td>{index + 1}</td><td>{row.tail}</td><td>{row.location}</td><td><AircraftStationDisplay aircraft={row} /></td><td>{row.mds}</td><td>{row.status}</td><td>{fmtDate(row.etic, true)}</td><td>{fmtDate(row.actualReturnAt || row.estimatedReturnAt, true)}</td><td>{tailSwapSummary(row.tailSwaps?.[0]) || '--'}</td><td>{forecastReadyLabel(row)}</td><td><WorkloadRealismPill assessment={row.workloadRealism} /><LaborRiskChips assessment={row.workloadRealism} /></td><td>{row.counts?.overdue9188 || 0}</td><td>{tailWaiverCoverageLabel(row)}</td><td>{row.waiverLabel}</td><td>{row.counts?.form781K || 0}</td><td>{row.counts?.openX || 0}</td><td>{missingInputCount(row.forecast)}</td><td>{row.forecast?.primaryDriver || '--'}</td></tr>)}</tbody></table></div>
          <section className="reports-panel crystal-ball__rtg-offstation">
            <div className="reports-panel__header"><div><h3>Offstation Return Gates</h3><span>Return date starts the recovery plan; overdue -6 items and waivers show whether normal ops is blocked or deferred.</span></div></div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table"><thead><tr><th>Tail</th><th>OS Location</th><th>Expected Return</th><th>ETIC</th><th>OD -6</th><th>Waiver Coverage</th><th>Waiver</th><th>781Ks</th><th>Open X</th><th>Projected Ready</th><th>Workload Realism</th><th>Plan Status</th></tr></thead><tbody>
                {enhancedRtgAircraft.filter(row => normalizedHsOs(row.hsOs) === 'OS').length ? enhancedRtgAircraft.filter(row => normalizedHsOs(row.hsOs) === 'OS').map(row => <tr key={`os-${row.id}`} onClick={() => { setView('overview'); loadDetail(row); }}><td>{row.tail}</td><td><OffstationLocationDisplay value={row.osLocation} emptyLabel="Needed" /></td><td>{fmtDate(row.actualReturnAt || row.estimatedReturnAt, true)}</td><td>{fmtDate(row.etic, true)}</td><td>{row.counts?.overdue9188 || 0}</td><td>{tailWaiverCoverageLabel(row)}</td><td>{row.waiverLabel}</td><td>{row.counts?.form781K || 0}</td><td>{row.counts?.openX || 0}</td><td>{forecastReadyLabel(row)}</td><td><WorkloadRealismPill assessment={row.workloadRealism} /><LaborRiskChips assessment={row.workloadRealism} /></td><td>{missingInputCount(row.forecast) ? `${missingInputCount(row.forecast)} inputs needed` : 'Planned'}</td></tr>) : <tr><td colSpan="12">No offstation aircraft match the current filters.</td></tr>}
              </tbody></table>
            </div>
          </section>
        </div>
      ) : null}

      {!loading && view === 'capacity' ? (
        <div className="crystal-ball__single-view crystal-ball__capacity-view">
          <div className="crystal-ball__section-heading">
            <div>
              <h2>Shop Capacity</h2>
              <p>Concern-first labor triage by month, site, MDS, shop, aircraft, and driver work. Detail tables remain below as evidence.</p>
            </div>
            <button type="button" className="crystal-ball__command-button" onClick={runLaborAssessment} disabled={working}>
              <Bot size={16} />Run Labor Assessment
            </button>
          </div>
          <p className="crystal-ball__status-note">{capacityAnalysis.qualificationBasis || 'Capacity is compared by site, MDS, and shop.'}</p>
          <div className="reports-kpi-grid reports-kpi-grid--status">
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Critical Concerns</span><strong>{capacityConcernSummary.critical}</strong><span className="reports-kpi-card__meta">labor or signoff risk</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Calculated Shortfalls</span><strong>{capacityConcernSummary.shortfall}</strong><span className="reports-kpi-card__meta">labor placed in a month</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">7/9 Signoff</span><strong>{capacityConcernSummary.signoff}</strong><span className="reports-kpi-card__meta">coverage gaps</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Timing/Input Gaps</span><strong>{capacityConcernSummary.planning}</strong><span className="reports-kpi-card__meta">dates or labor missing</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Support Options</span><strong>{capacityOvertaxedRows.filter(row => (row.supportEligible || []).length).length}</strong><span className="reports-kpi-card__meta">same-MDS or 9-level basis</span></article>
          </div>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <div><h3>Concerns To Address</h3><span>Top 10 needs-review concerns shown per page. {capacityConcernSummary.reviewed ? `${capacityConcernSummary.reviewed} reviewed hidden from Needs Review.` : 'Reviewed concerns can be reopened.'}</span></div>
              <div className="crystal-ball__capacity-filters" role="group" aria-label="Capacity concern filter">
                {CAPACITY_CONCERN_FILTERS.map(([key, label]) => (
                  <button key={key} type="button" className={capacityConcernFilter === key ? 'is-active' : ''} onClick={() => setCapacityConcernFilter(key)}>
                    <span>{label}</span>
                    <strong>{capacityConcernSummary[key] ?? 0}</strong>
                  </button>
                ))}
              </div>
            </div>
            <div className="crystal-ball__capacity-legend">
              <strong>Legend</strong>
              <span><code>--</code> means the input or condition is unknown or not computable from current planning data.</span>
              <span>In the "When" field, <code>--</code> usually means planned dates are missing, so labor timing needs review.</span>
              <span>Calculated Shortfalls only includes labor placed inside a capacity month.</span>
            </div>
            {capacityReviewFeedback ? (
              <div className="crystal-ball__capacity-feedback" role="status">
                {capacityReviewFeedback}
              </div>
            ) : null}
            {filteredCapacityConcerns.length ? (
              <div className="reports-table-shell">
                <table className="reports-table reports-status-table crystal-ball__capacity-risk-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Concern</th>
                      <th>When</th>
                      <th>Impact</th>
                      <th>Aircraft</th>
                      <th>Top Drivers</th>
                      <th>Support Option</th>
                      <th>Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCapacityConcerns.map((concern, index) => (
                      <CapacityConcernRow
                        key={concern.id}
                        concern={concern}
                        rank={capacityConcernStart + index + 1}
                        onTailClick={openCapacityConcernTail}
                        onReviewToggle={toggleCapacityConcernReview}
                        onDriverOverflow={setCapacityDriverModalConcern}
                      />
                    ))}
                  </tbody>
                </table>
                <div className="crystal-ball__capacity-pagination">
                  <span>
                    Showing {capacityConcernStart + 1}-{Math.min(capacityConcernStart + CAPACITY_CONCERN_PAGE_SIZE, filteredCapacityConcerns.length)} of {filteredCapacityConcerns.length}
                  </span>
                  <div>
                    <button
                      type="button"
                      onClick={() => setCapacityConcernPage(page => Math.max(1, page - 1))}
                      disabled={capacityConcernCurrentPage <= 1}
                      aria-label="Previous capacity concern page"
                    >
                      <ChevronLeft size={15} />Previous 10
                    </button>
                    <strong>Page {capacityConcernCurrentPage} of {capacityConcernPageCount}</strong>
                    <button
                      type="button"
                      onClick={() => setCapacityConcernPage(page => Math.min(capacityConcernPageCount, page + 1))}
                      disabled={capacityConcernCurrentPage >= capacityConcernPageCount}
                      aria-label="Next capacity concern page"
                    >
                      Next 10<ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ) : capacityConcernFilter === 'shortfall' && capacityShortfallInputGaps.concerns > 0 ? (
              <div className="crystal-ball__capacity-empty">
                <strong>Inputs needed before shortfalls can be calculated</strong>
                <span>
                  Crystal Ball found {capacityShortfallInputGaps.concerns} timing/input gap{capacityShortfallInputGaps.concerns === 1 ? '' : 's'} across {capacityShortfallInputGaps.tasks} task{capacityShortfallInputGaps.tasks === 1 ? '' : 's'}.
                  {capacityShortfallInputGaps.laborTasks ? ` ${capacityShortfallInputGaps.laborTasks} task${capacityShortfallInputGaps.laborTasks === 1 ? '' : 's'} need labor estimates before demand can be placed against capacity.` : ''}
                  {capacityShortfallInputGaps.timingTasks ? ` ${capacityShortfallInputGaps.timingTasks} task${capacityShortfallInputGaps.timingTasks === 1 ? '' : 's'} need planning dates before labor timing can be calculated.` : ''}
                </span>
                <button type="button" onClick={() => setCapacityConcernFilter('planning')}>
                  View Timing/Input Gaps
                </button>
              </div>
            ) : (
              <p className="crystal-ball__empty-row">No capacity concerns match the current filters.</p>
            )}
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <div><h3>Location Evidence</h3><span>Site comparison across the planning horizon. MDS filter narrows demand to that MDS.</span></div>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table crystal-ball__capacity-table">
                <thead><tr><th>Location</th><th>MDS in View</th><th>Demand</th><th>Capacity</th><th>Shortfall</th><th>Extra</th><th>Peak Utilization</th><th><CapacityHelpHeader help={LABOR_CAPACITY_COUNT_HELP}>Labor Capacity Count</CapacityHelpHeader></th><th>7/9 Signoff</th><th>Problem Lanes</th></tr></thead>
                <tbody>{capacitySiteRows.length ? capacitySiteRows.map(row => (
                  <tr key={row.location}>
                    <td><strong>{row.location || '--'}</strong></td>
                    <td>{Array.isArray(row.mds) && row.mds.length ? row.mds.join(', ') : '--'}</td>
                    <td>{fmtHours(row.workHours)}</td>
                    <td>{fmtHours(row.capacityHours)}</td>
                    <td className={Number(row.shortfallHours || 0) > 0 ? 'is-capacity-risk' : ''}>{fmtHours(row.shortfallHours)}</td>
                    <td>{fmtHours(row.spareHours)}</td>
                    <td>{Number(row.peakUtilizationPercent || 0).toFixed(0)}%</td>
                    <td>{row.productivePeople ?? row.fullTimePeople ?? 0}</td>
                    <td className={Number(row.signoffRiskCells || 0) > 0 ? 'is-capacity-risk' : ''}>{row.signoffQualifiedPeople ?? '--'}</td>
                    <td>{row.overtaxedCells || 0}{row.signoffRiskCells ? ` + ${row.signoffRiskCells} signoff` : ''}</td>
                  </tr>
                )) : <tr><td colSpan="10">No site capacity data is available for the current scope.</td></tr>}</tbody>
              </table>
            </div>
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <div><h3>Overtaxed MDS / Shop Lanes</h3><span>These lanes have more planned readiness-driver labor than available full-time shop capacity in at least one month.</span></div>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table crystal-ball__capacity-table">
                <thead><tr><th>Location</th><th>MDS</th><th>Shop</th><th>Demand</th><th>Capacity</th><th>Shortfall</th><th>Peak Month</th><th><CapacityHelpHeader help={LABOR_CAPACITY_COUNT_HELP}>Labor Capacity Count</CapacityHelpHeader></th><th>7/9 Signoff</th><th>Tails</th><th>Top Work</th><th>Same-MDS Support</th></tr></thead>
                <tbody>{capacityOvertaxedRows.length ? capacityOvertaxedRows.map(row => {
                  const peak = capacityPeakPeriod(row, 'shortfallHours');
                  return (
                    <tr key={`${row.location}-${row.mds}-${row.shopName}`}>
                      <td>{row.location}</td>
                      <td>{row.mds}</td>
                      <td>{row.shopName}</td>
                      <td>{fmtHours(row.workHours)}</td>
                      <td>{fmtHours(row.capacityHours)}</td>
                      <td className="is-capacity-risk">{fmtHours(row.shortfallHours)}</td>
                      <td>{peak?.label || '--'}{peak?.shortfallHours ? ` (${fmtHours(peak.shortfallHours)})` : ''}</td>
                      <td>{row.productivePeople || 0}</td>
                      <td className={row.signoffRisk ? 'is-capacity-risk' : ''}>{row.signoffQualifiedPeople || 0}</td>
                      <td>{(row.tails || []).join(', ') || '--'}</td>
                      <td>{(row.items || []).slice(0, 2).map(item => `${item.tail}: ${truncateText(item.label, 52)}`).join(' | ') || '--'}</td>
                      <td>{(row.supportEligible || []).length ? row.supportEligible.map(item => `${item.location} ${fmtHours(item.spareHours)} (${item.basis})`).join(', ') : '--'}</td>
                    </tr>
                  );
                }) : <tr><td colSpan="12">No MDS/shop labor bottlenecks detected from current planned readiness-driver labor rows.</td></tr>}</tbody>
              </table>
            </div>
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <div><h3>Potential Extra Capacity</h3><span>Spare capacity is shown by site, MDS, and shop. It should only be considered for matching MDS work.</span></div>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table crystal-ball__capacity-table">
                <thead><tr><th>Location</th><th>MDS</th><th>Shop</th><th>Extra</th><th>Capacity</th><th>Demand</th><th>Peak Spare Month</th><th><CapacityHelpHeader help={LABOR_CAPACITY_COUNT_HELP}>Labor Capacity Count</CapacityHelpHeader></th><th>7/9 Signoff</th></tr></thead>
                <tbody>{capacityExtraRows.length ? capacityExtraRows.slice(0, 80).map(row => {
                  const peak = capacityPeakPeriod(row, 'spareHours');
                  return (
                    <tr key={`extra-${row.location}-${row.mds}-${row.shopName}`}>
                      <td>{row.location}</td>
                      <td>{row.mds}</td>
                      <td>{row.shopName}</td>
                      <td>{fmtHours(row.spareHours)}</td>
                      <td>{fmtHours(row.capacityHours)}</td>
                      <td>{fmtHours(row.workHours)}</td>
                      <td>{peak?.label || '--'}{peak?.spareHours ? ` (${fmtHours(peak.spareHours)})` : ''}</td>
                      <td>{row.productivePeople || 0}</td>
                      <td className={row.signoffRisk ? 'is-capacity-risk' : ''}>{row.signoffQualifiedPeople || 0}</td>
                    </tr>
                  );
                }) : <tr><td colSpan="9">No extra capacity lanes detected from current personnel and planned workload.</td></tr>}</tbody>
              </table>
            </div>
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <div><h3>Incomplete Capacity Inputs</h3><span>Readiness-driver work here cannot be trusted in capacity math until labor rows and planned dates are entered.</span></div>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table">
                <thead><tr><th>Location</th><th>Tail</th><th>MDS</th><th>Risk</th><th>Task</th><th>Planned Start</th><th>Planned Finish</th></tr></thead>
                <tbody>{capacityPlanningRisks.length ? capacityPlanningRisks.slice(0, 120).map((row, index) => (
                  <tr key={`${row.tail}-${row.type}-${row.jcn}-${row.risk}-${index}`}>
                    <td>{row.location || '--'}</td>
                    <td>{row.tail || row.serialNumber || '--'}</td>
                    <td>{row.mds || '--'}</td>
                    <td className="is-capacity-risk">{row.risk}</td>
                    <td>{truncateText(row.label || row.jcn, 120)}</td>
                    <td>{fmtDate(row.plannedStartAt, true)}</td>
                    <td>{fmtDate(row.plannedFinishAt, true)}</td>
                  </tr>
                )) : <tr><td colSpan="7">No missing labor or date inputs detected for readiness-driver work in this scope.</td></tr>}</tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}

      {!loading && activeViewReady && isHqRollup && view === 'status' ? (
        <div className="crystal-ball__single-view crystal-ball__status-report" ref={crystalStatusReportRef}>
          <div className="crystal-ball__section-heading" data-pdf-section="overview">
            <div>
              <h2>HQ Backlog / Status</h2>
              <p>Inspection backlog by task and G081 readiness drivers by tail across the selected HQ rollup scope | {statusReportTails.length} of {(statusReport?.tails || []).length} tails shown</p>
            </div>
            <div className="reports-status-export-actions">
              <button type="button" className="reports-export-button" onClick={() => setCrystalStatusPdfPickerOpen(current => !current)}>
                Export PDF
              </button>
              {crystalStatusPdfPickerOpen ? (
                <div className="reports-status-pdf-options reports-status-pdf-popover" role="dialog" aria-label="Crystal Ball Status PDF table selection">
                  <span>PDF tables</span>
                  {CRYSTAL_STATUS_PDF_SECTIONS.map(section => (
                    <label key={section.key}>
                      <input
                        type="checkbox"
                        checked={Boolean(crystalStatusPdfSections[section.key])}
                        onChange={event => setCrystalStatusPdfSections(current => ({
                          ...current,
                          [section.key]: event.target.checked,
                        }))}
                      />
                      {section.label}
                    </label>
                  ))}
                  <div className="reports-status-pdf-popover__actions">
                    <button type="button" onClick={() => setCrystalStatusPdfSections(DEFAULT_CRYSTAL_STATUS_PDF_SECTIONS)}>All</button>
                    <button type="button" onClick={() => setCrystalStatusPdfSections(Object.fromEntries(CRYSTAL_STATUS_PDF_SECTIONS.map(section => [section.key, false])))}>None</button>
                    <small>{selectedCrystalStatusPdfSectionCount} selected</small>
                    <button type="button" className="reports-status-pdf-popover__primary" onClick={exportCrystalStatusReportToPDF} disabled={!selectedCrystalStatusPdfSectionCount}>Generate PDF</button>
                    <button type="button" onClick={() => setCrystalStatusPdfPickerOpen(false)}>Cancel</button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <InspectionBacklog tails={statusReportTails} generatedAt={statusReport?.generatedAt} onOpenAircraft={aircraft => { setView('overview'); loadDetail(aircraft); }} />
          <div className="reports-kpi-grid reports-kpi-grid--status" data-pdf-section="overview">
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Tails</span><strong>{statusReportSummary.tails}</strong><span className="reports-kpi-card__meta">{statusReportSummary.tailsWithOpenX} with open X jobs</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Open X Jobs</span><strong>{statusReportSummary.openX}</strong><span className="reports-kpi-card__meta">8035 symbol X</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">9188 -6 Overdue</span><strong>{statusReportSummary.overdue9188}</strong><span className="reports-kpi-card__meta">Past due baseline</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Active Waivers</span><strong>{statusReportSummary.waiverCount}</strong><span className="reports-kpi-card__meta">{waiverCoverageLabel(statusReportSummary.waiverCount, statusReportSummary.waived9188)}</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">9188 -6 Due Soon (10%)</span><strong>{statusReportSummary.red9188}</strong><span className="reports-kpi-card__meta">Inside 10% trigger</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">9188 -6 Due Soon (25%)</span><strong>{statusReportSummary.watch9188}</strong><span className="reports-kpi-card__meta">Inside 25% watch</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">Missing Due/Start</span><strong>{statusReportSummary.missingDueStart}</strong><span className="reports-kpi-card__meta">Needs source cleanup</span></article>
            <article className="reports-kpi-card"><span className="reports-kpi-card__label">781Ks</span><strong>{statusReportSummary.form781K}</strong><span className="reports-kpi-card__meta">Open delayed discrepancies</span></article>
          </div>

          <section className="reports-panel" data-pdf-section="tail-summary">
            <div className="reports-panel__header">
              <div><h3>Tail Summary</h3><span>Sorted by open X, overdue 9188-6, due-soon tasks, and watch items.</span></div>
            </div>
            <p className="crystal-ball__status-note">Tasks are only counted in one category</p>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table crystal-ball__status-table">
                <thead>
                  <tr><th>Tail</th><th>MDS</th><th>Location</th><th>HS/OS</th><th>Status</th><th>ETIC</th><th>Open X</th><th className="crystal-ball__status-count-heading">Missing Due/Start</th><th className="crystal-ball__status-count-heading">9188 -6 Overdue</th><th className="crystal-ball__status-count-heading">Waiver Coverage</th><th className="crystal-ball__status-count-heading">9188 -6 Due Soon (10%)</th><th className="crystal-ball__status-count-heading">9188 -6 Due Soon (25%)</th><th>781Ks</th><th>Top Drivers</th></tr>
                </thead>
                <tbody>
                  {statusReportTails.length ? statusReportTails.map((row) => {
                    const drivers = [
                      ...(row.openX || []).slice(0, 1).map(item => `X: ${item.discrepancy || item.jcn || ''}`.trim()),
                      ...(row.missingDueStart || []).slice(0, 1).map(item => `Missing dates: ${item.discrepancy || item.noun || item.jcn || item.wuc || ''}`.trim()),
                      ...(row.overdue9188 || []).slice(0, 1).map(item => `Overdue ${item.noun || item.wuc || ''}`.trim()),
                      ...(row.red9188 || []).slice(0, 1).map(item => `Red ${item.noun || item.wuc || ''}`.trim()),
                      ...(row.watch9188 || []).slice(0, 1).map(item => `Watch ${item.noun || item.wuc || ''}`.trim()),
                    ];
                    return (
                      <tr key={row.aircraft?.id || row.aircraft?.tail}>
                        <td><strong>{row.aircraft?.tail || '--'}</strong></td>
                        <td>{row.aircraft?.mds || '--'}</td>
                        <td>{row.aircraft?.location || '--'}</td>
                        <td><AircraftStationDisplay aircraft={row.aircraft} /></td>
                        <td><CurrentStatusPill status={row.aircraft?.status} /></td>
                        <td>{fmtDate(row.aircraft?.etic, true)}</td>
                        <td>{row.counts?.openX || 0}</td>
                        <td className="crystal-ball__status-count-cell">{statusCountButton(row, 'missingDueStart', 'items missing due and start dates')}</td>
                        <td className="crystal-ball__status-count-cell">{statusCountButton(row, 'overdue9188', '9188 -6 overdue tasks')}</td>
                        <td className="crystal-ball__status-count-cell">{tailWaiverCoverageLabel(row)}</td>
                        <td className="crystal-ball__status-count-cell">{statusCountButton(row, 'red9188', '9188 -6 due soon 10 percent tasks')}</td>
                        <td className="crystal-ball__status-count-cell">{statusCountButton(row, 'watch9188', '9188 -6 due soon 25 percent tasks')}</td>
                        <td>{row.counts?.form781K || 0}</td>
                        <td>{drivers.length ? truncateText(drivers.join(' | '), 150) : '--'}</td>
                      </tr>
                    );
                  }) : <tr><td colSpan="14">No G081 status rows match the current HQ filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <div className="reports-grid reports-grid--two">
            <section className="reports-panel" data-pdf-section="open-x">
              <div className="reports-panel__header"><div><h3>Open X from 8035</h3><span>Non-flyable 8035 jobs by selected HQ scope.</span></div></div>
              <div className="reports-table-shell">
                <table className="reports-table reports-status-table"><thead><tr><th>Tail</th><th>Location</th><th>JCN</th><th>Shop</th><th>WUC</th><th>Discrepancy</th></tr></thead><tbody>
                  {statusOpenXRows.length ? statusOpenXRows.map(row => <tr key={row.id}><td>{row.tail}</td><td>{row.tailLocation || '--'}</td><td>{row.jcn || '--'}</td><td>{row.shopName || '--'}</td><td>{row.wuc || '--'}</td><td>{truncateText(row.discrepancy, 120)}</td></tr>) : <tr><td colSpan="6">No open X jobs match the current filters.</td></tr>}
                </tbody></table>
              </div>
            </section>
            <section className="reports-panel" data-pdf-section="overdue-9188">
              <div className="reports-panel__header"><div><h3>9188 -6 Overdue</h3><span>Recurring requirements already past due.</span></div></div>
              <div className="reports-table-shell">
                <table className="reports-table reports-status-table"><thead><tr><th>Tail</th><th>WUC</th><th>Requirement</th><th>Due</th><th>Days Overdue</th></tr></thead><tbody>
                  {statusOverdue9188Groups.length ? statusOverdue9188Groups.map(group => (
                    <React.Fragment key={`hq-overdue-${group.id}`}>
                      <StatusReportTailGroupHeader group={group} colSpan={5} label="overdue 9188-6 item" />
                      {group.items.map(row => (
                        <tr key={`${row.id}-${row.level}`} className="reports-status-tail-item-row">
                          <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                          <td>{row.wuc || '--'}</td>
                          <td>{truncateText(row.noun, 110)}</td>
                          <td>{fmtDate(row.dueAt)}</td>
                          <td><span className="reports-status-pill reports-status-pill--high">{row.overdueDays || 0} days</span></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )) : <tr><td colSpan="5">No overdue 9188 -6 items match the current filters.</td></tr>}
                </tbody></table>
              </div>
            </section>
          </div>

          <section className="reports-panel" data-pdf-section="red-9188">
            <div className="reports-panel__header"><div><h3>9188 -6 Due Soon (10%)</h3><span>Inside the 10% trigger window; overdue tasks are listed separately above.</span></div></div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table"><thead><tr><th>Tail</th><th>WUC</th><th>Requirement</th><th>Due</th><th>Remaining</th><th>LOC / Serial#</th></tr></thead><tbody>
                {statusRed9188Groups.length ? statusRed9188Groups.map(group => (
                  <React.Fragment key={`hq-red-${group.id}`}>
                    <StatusReportTailGroupHeader group={group} colSpan={6} label="10% due-soon item" />
                    {group.items.map(row => (
                      <tr key={row.id} className="reports-status-tail-item-row">
                        <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                        <td>{row.wuc || '--'}</td>
                        <td>{truncateText(row.noun, 140)}</td>
                        <td>{fmtDate(row.dueAt)}</td>
                        <td><span className="reports-status-pill reports-status-pill--high">{row.remainingLabel || '--'}</span></td>
                        <td>{[row.componentLocation, row.componentSerial].filter(Boolean).join(' / ') || '--'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                )) : <tr><td colSpan="6">No 10% due-soon 9188 -6 items match the current filters.</td></tr>}
              </tbody></table>
            </div>
          </section>

          <section className="reports-panel" data-pdf-section="watch-9188">
            <div className="reports-panel__header"><div><h3>9188 -6 Due Soon (25%)</h3><span>Tasks are only counted in one category.</span></div></div>
            <div className="reports-table-shell">
              <table className="reports-table reports-status-table"><thead><tr><th>Tail</th><th>Location</th><th>WUC</th><th>Requirement</th><th>Due</th><th>Remaining</th><th>LOC / Serial#</th></tr></thead><tbody>
                {statusWatch9188Groups.length ? statusWatch9188Groups.map(group => (
                  <React.Fragment key={`hq-watch-${group.id}`}>
                    <StatusReportTailGroupHeader group={group} colSpan={7} label="25% watch item" />
                    {group.items.map(row => (
                      <tr key={row.id} className="reports-status-tail-item-row">
                        <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                        <td>{row.tailLocation || '--'}</td>
                        <td>{row.wuc || '--'}</td>
                        <td>{truncateText(row.noun, 140)}</td>
                        <td>{fmtDate(row.dueAt)}</td>
                        <td><span className="reports-status-pill reports-status-pill--watch">{row.remainingLabel || '--'}</span></td>
                        <td>{[row.componentLocation, row.componentSerial].filter(Boolean).join(' / ') || '--'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                )) : <tr><td colSpan="7">No watch-window 9188-6 items match the current filters.</td></tr>}
              </tbody></table>
            </div>
          </section>

          <section className="reports-panel" data-pdf-section="missing-dates">
            <div className="reports-panel__header">
              <div><h3>Items Missing Due & Start Dates</h3><span>Open scheduled tasks and 8035 jobs without both due basis and planned start, grouped by location and MDS.</span></div>
            </div>
            <div className="reports-grid reports-grid--two">
              <div className="reports-table-shell">
                <table className="reports-table reports-status-table">
                  <thead><tr><th>Location</th><th>MDS</th><th>Tails</th><th>Scheduled Tasks</th><th>8035 Jobs</th><th>Readiness Drivers</th></tr></thead>
                  <tbody>
                    {statusMissingDueStartRollupRows.length ? statusMissingDueStartRollupRows.map(row => (
                      <tr key={row.id}>
                        <td>{row.location}</td>
                        <td>{row.mds}</td>
                        <td>{row.tailCount}</td>
                        <td>{row.scheduledTasks}</td>
                        <td>{row.f8035Jobs}</td>
                        <td>{row.readinessDrivers}</td>
                      </tr>
                    )) : <tr><td colSpan="6">No items missing due and start dates match the current filters.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table reports-status-table">
                  <thead><tr><th>Tail</th><th>Location</th><th>MDS</th><th>Status</th><th>Readiness Driver</th><th>Items</th><th>Top Missing Inputs</th></tr></thead>
                  <tbody>
                    {statusMissingDueStartTailRows.length ? statusMissingDueStartTailRows.map(row => (
                      <tr key={row.id} className="crystal-ball__drilldown-row" onClick={() => openStatusReportDrilldown({ aircraft: { id: row.aircraftId, tail: row.tail } }, 'missingDueStart')}>
                        <td><strong>{row.tail}</strong></td>
                        <td>{row.tailLocation || '--'}</td>
                        <td>{row.mds || '--'}</td>
                        <td><CurrentStatusPill status={row.status} /></td>
                        <td>{row.readinessDrivers ? 'Yes' : 'No'}</td>
                        <td>{row.total} ({row.readinessDrivers} driver{row.readinessDrivers === 1 ? '' : 's'}, {row.otherItems} other)</td>
                        <td>{truncateText(row.items.slice(0, 3).map(item => `${item.itemType || item.sourceReportType || 'G081'}: ${item.discrepancy || item.noun || item.jcn || item.wuc || 'Missing-date item'}`).join(' | '), 150)}</td>
                      </tr>
                    )) : <tr><td colSpan="7">No tail drilldown rows available.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {!loading && activeViewReady && view === 'changes' ? (
        <div className="crystal-ball__single-view">
          <div className="crystal-ball__section-heading">
            <div><h2>What Changed</h2><p>Latest forecast compared with the immediately prior forecast for each tail. Delays of 24h or more should get a unit explanation.</p></div>
          </div>
          <div className="crystal-ball__table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>Tail</th>
                  <th>Location</th>
                  <th>Prior Projected Ready Date</th>
                  <th>Latest Projected Ready Date</th>
                  <th>Change</th>
                  <th>Likely Change Driver</th>
                  <th>Notes / Action</th>
                </tr>
              </thead>
              <tbody>
                {changes.length ? changes.map(row => (
                  <tr key={row.aircraft.id}>
                    <td>{fmtProjectedReadyDate(row.latest.calculatedAt)}</td>
                    <td>{row.aircraft.tail}</td>
                    <td>{row.aircraft.location}</td>
                    <td>{(row.prior || row.baseline)?.projectedReadyAt ? fmtProjectedReadyDate((row.prior || row.baseline).projectedReadyAt) : 'No prior projected date'}</td>
                    <td>{row.latest.projectedReadyAt ? fmtProjectedReadyDate(row.latest.projectedReadyAt) : 'Projected date unavailable'}</td>
                    <td>{row.changeType === 'date_established' ? 'Date established' : row.changeType === 'date_unavailable' ? 'Date unavailable' : fmtForecastChange(row.deltaHours)}</td>
                    <td>{forecastChangeDriver(row)}</td>
                    <td>{forecastChangeNote(row, canSubmitExplanation, explanationDrafts, setExplanationDrafts, submitExplanation)}</td>
                  </tr>
                )) : <tr><td colSpan="8">No projected ready date changes between the latest and prior forecasts.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {!loading && activeViewReady && view === 'history' ? (
        <div className="crystal-ball__single-view">
          <div className="crystal-ball__section-heading"><div><h2>Estimate History</h2><p>Search completed 9188-6 and 8035 work, then compare planned duration with actual performance.</p></div></div>
          <div className="crystal-ball__waiver-search">
            <input
              type="search"
              value={completionHistorySearch}
              onChange={event => setCompletionHistorySearch(event.target.value)}
              placeholder="Search tail, task, WUC, JCN, shop, MDS, or location"
              aria-label="Search completed task history"
            />
            {completionHistorySearch ? <button type="button" onClick={() => setCompletionHistorySearch('')}>Clear</button> : null}
            <label><input type="checkbox" checked={showArchivedCompletionHistory} onChange={event => setShowArchivedCompletionHistory(event.target.checked)} /> Show archived</label>
            <span>{filteredCompletionHistory.length} of {completionHistory.length}</span>
          </div>
          <div className="crystal-ball__table-scroll">
            <table>
              <thead><tr><th>Completed</th><th>Tail</th><th>Location / MDS</th><th>Task</th><th>WUC / JCN</th><th>Shop</th><th>Planned</th><th>Actual</th><th>Crew / Labor</th><th>Wait context</th><th>Review</th></tr></thead>
              <tbody>{filteredCompletionHistory.length ? filteredCompletionHistory.map(row => (
                <tr key={row.id}>
                  <td>{fmtDate(row.actualFinishAt, true)}</td>
                  <td>{row.tail}</td>
                  <td>{row.location}<br />{row.mds || '--'}</td>
                  <td><strong>{row.taskName}</strong><br />{row.sourceEntityType === '9188_requirement' ? '9188-6' : '8035'}</td>
                  <td>{row.wuc || '--'}{row.jcn ? <><br />JCN {row.jcn}{row.wes ? ` / WES ${row.wes}` : ''}</> : null}</td>
                  <td>{row.shopName || 'Unassigned'}</td>
                  <td>{row.plannedElapsedHours ?? row.estimatedDurationHours ?? '--'} hr<br />{fmtDate(row.plannedStartAt, true)} to {fmtDate(row.plannedFinishAt, true)}</td>
                  <td>{row.actualElapsedHours ?? '--'} hr<br />{fmtDate(row.actualStartAt, true)} to {fmtDate(row.actualFinishAt, true)}</td>
                  <td>
                    <details>
                      <summary>{row.crewSize ?? '--'} / {row.laborHours ?? '--'} hr</summary>
                      {(row.laborRequirements || []).length
                        ? row.laborRequirements.map((labor, index) => <div key={`${row.id}-labor-${index}`}>{labor.shopName || labor.shop || 'Unassigned'}: {labor.peopleRequired ?? '--'} people / {labor.laborHours ?? '--'} hr</div>)
                        : <div>No labor mix recorded.</div>}
                    </details>
                  </td>
                  <td>
                    {(row.supplyContext || []).length || (row.waitConditions || []).length ? (
                      <details>
                        <summary>{(row.supplyContext || []).length} supply; {(row.waitConditions || []).length} wait</summary>
                        {(row.supplyContext || []).map((supply, index) => <div key={`${row.id}-supply-${index}`}>{supply.partNumber || 'Part'}: {supply.status || 'status not recorded'}</div>)}
                        {(row.waitConditions || []).map((wait, index) => <div key={`${row.id}-wait-${index}`}>{String(wait.kind || 'wait').replace(/_/g, ' ')}{wait.hours != null ? `: ${wait.hours} hr` : wait.waitHours != null ? `: ${wait.waitHours} hr` : ''}</div>)}
                      </details>
                    ) : 'None recorded'}
                  </td>
                  <td>
                    {row.excludedFromAnalysis ? <><strong>Archived</strong><br />{row.exclusionReason || 'No reason recorded'}</> : 'Included'}
                    {canSubmitExplanation ? <><br /><button type="button" disabled={completionHistoryReviewId === row.id} onClick={() => setCompletionHistoryExclusion(row, !row.excludedFromAnalysis)}>{completionHistoryReviewId === row.id ? 'Saving...' : row.excludedFromAnalysis ? 'Restore' : 'Archive'}</button></> : null}
                  </td>
                </tr>
              )) : <tr><td colSpan="11">No completed task history matches this search.</td></tr>}</tbody>
            </table>
          </div>
          <div className="crystal-ball__table-scroll">
            <table>
              <thead><tr><th>Task</th><th>Shop</th><th>Samples</th><th>Planned Avg</th><th>Actual Median</th><th>P75</th><th>P90</th><th>Assessment</th></tr></thead>
              <tbody>{estimates.length ? estimates.map(row => <tr key={`${row.noun}-${row.shopName}`}><td>{row.noun}</td><td>{row.shopName}</td><td>{row.sampleCount}</td><td>{row.averagePlannedHours ?? '--'}</td><td>{row.medianHours}</td><td>{row.p75Hours}</td><td>{row.p90Hours}</td><td>{row.estimateUnsupported ? <span className="crystal-ball__estimate-warning"><AlertTriangle size={14} />Estimate not supported by history</span> : row.credibility}</td></tr>) : <tr><td colSpan="8">Actual start and finish history has not accumulated yet.</td></tr>}</tbody>
            </table>
          </div>
        </div>
      ) : null}

      {!loading && activeViewReady && view === 'waivers' ? (
        <div className="crystal-ball__single-view">
          <div className="crystal-ball__section-heading">
            <div><h2>FSID ETARS Waivers</h2><p>Engineering waiver source matched against tails and open requirements</p></div>
          </div>
          <form className="crystal-ball__upload" onSubmit={importEtars}>
            <input type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" onChange={event => setEtarFile(event.target.files?.[0] || null)} />
            <button type="submit" disabled={working || !etarFile}><Upload size={16} />Import FSID ETARS</button>
          </form>
          <div className="crystal-ball__waiver-search">
            <input
              type="search"
              value={etarSearchTerm}
              onChange={event => setEtarSearchTerm(event.target.value)}
              placeholder="Search ETAR, tail, requirement, WUC, subject, org, or status"
              aria-label="Search FSID ETARS waivers"
            />
            {etarSearchTerm ? <button type="button" onClick={() => setEtarSearchTerm('')}>Clear</button> : null}
            <span>{filteredEtarRecords.length} of {(etars.records || []).length}</span>
          </div>
          <div className="crystal-ball__waiver-summary">
            <span><strong>{etarSummaryCount(etars.summary, 'matched')}</strong>{matchLabel('matched')}</span>
            <span><strong>{etarSummaryCount(etars.summary, 'tail_matched')}</strong>{matchLabel('tail_matched')}</span>
            <span><strong>{etarSummaryCount(etars.summary, 'unmatched')}</strong>{matchLabel('unmatched')}</span>
            <span><strong>{etarSummaryCount(etars.summary, 'ambiguous_tail')}</strong>{matchLabel('ambiguous_tail')}</span>
            <span><strong>{etarSummaryCount(etars.summary, 'issued', 'active')}</strong>{matchLabel('issued')}</span>
            <span><strong>{etarSummaryCount(etars.summary, 'pending', 'expired')}</strong>{matchLabel('pending')}</span>
            <span><strong>{etarSummaryCount(etars.summary, 'closed')}</strong>{matchLabel('closed')}</span>
          </div>
          <div className="crystal-ball__table-scroll">
            <table>
              <thead><tr><th>Nomenclature / Subject</th><th>ETAR</th><th>Class</th><th>Tail</th><th>Days Since Issued</th><th>Issued</th><th>Status</th><th>Match</th><th>Covered Tasks</th><th>Org</th></tr></thead>
              <tbody>{filteredEtarRecords.length ? filteredEtarRecords.map(row => (
                <tr key={row.id}>
                  <td><strong>{etarDisplayTitle(row)}</strong></td>
                  <td>{row.etarNumber}</td>
                  <td>{row.classification || '--'}</td>
                  <td>{row.aircraft?.tail || row.tailNumber || row.normalizedTail || '--'}</td>
                  <td>{etarIssuedAgeDays(row) ?? '--'}</td>
                  <td>{fmtDate(etarIssuedDate(row))}</td>
                  <td><span className={`crystal-ball__waiver ${etarWaiverStatusClass(row)}`}><ShieldCheck size={14} />{etarWaiverStatusLabel(row)}</span></td>
                  <td>{matchLabel(row.matchStatus)}</td>
                  <td>{matchedEtarTasks(row).length ? `${etarCoverageLabel(row)}: ${matchedEtarTasks(row).map(task => task.noun || task.wuc).join(', ')}` : '--'}</td>
                  <td>{row.aflcmcOrg || row.submissionOrg || '--'}</td>
                </tr>
              )) : <tr><td colSpan="10">{(etars.records || []).length ? 'No FSID ETARS waivers match the current search.' : 'No FSID ETARS waivers have been imported for this scope.'}</td></tr>}</tbody>
            </table>
          </div>
        </div>
      ) : null}

      {!loading && view === 'sources' ? (
        <DataCurrencyTab api={api} scope={scope} formatDate={fmtDate} refreshKey={loadRequestRef.current}
          enabled={!isHqRollup || selectedLocations.length > 0} />
      ) : null}

      {!loading && view === 'chat' ? (
        <div className="crystal-ball__chat">
          <div className="crystal-ball__chat-intro">
            <Bot size={28} />
            <div>
              <h2>Ask Crystal Ball</h2>
              <p>Answers separate confirmed facts, inferences, missing inputs, and recommended next actions.</p>
            </div>
          </div>
          <form onSubmit={askQuestion}>
            <textarea value={question} onChange={event => setQuestion(event.target.value)} placeholder="How many C-17s will be ready on Friday, and what is keeping the others red?" />
            <button type="submit" disabled={working || !question.trim()}>{chatWorking ? 'Assessing...' : 'Ask'}</button>
          </form>
          <section className="crystal-ball__chat-report-options" aria-label="Optional AI assessment reports">
            <header>
              <h3>AI Assessment Reports</h3>
              <span>Preset reports summarize leadership-level actions first; detailed evidence stays available below.</span>
            </header>
            <button type="button" onClick={runLaborAssessment} disabled={working}>
              <Bot size={15} />Run Labor Assessment Report
            </button>
          </section>
          {chatWorking ? (
            <div className="crystal-ball__chat-thinking" role="status" aria-live="polite">
              <RefreshCw size={16} />
              <div>
                <strong>Crystal Ball is assessing the current readiness picture...</strong>
                <span>
                  {chatElapsedSeconds < 15
                    ? `Still processing | ${chatElapsedSeconds}s elapsed`
                    : `Still working through the aircraft, blocker, schedule, and labor context | ${chatElapsedSeconds}s elapsed`}
                </span>
              </div>
            </div>
          ) : null}
          {answer ? (
            <CrystalBallAnswer answer={answer} question={question} refreshing={chatWorking} />
          ) : (
            <div className="crystal-ball__chat-empty"><Clock3 size={22} /><p>Crystal Ball will say what it knows, what it inferred, and what the unit still needs to provide.</p></div>
          )}
          <section className="crystal-ball__chat-history" aria-label="Recent Ask Crystal Ball runs">
            <div className="crystal-ball__chat-history-header">
              <h3>Last 10 Questions</h3>
              {chatHistoryLoading ? <span>Loading...</span> : <span>{chatHistory.length} saved</span>}
            </div>
            {chatHistory.length ? (
              <div className="crystal-ball__chat-history-list">
                {chatHistory.map(record => (
                  <button
                    key={record.id}
                    type="button"
                    onClick={() => {
                      setQuestion(record.question || '');
                      setAnswer(record.answer || null);
                    }}
                  >
                    <strong>{record.question || 'Untitled question'}</strong>
                    <span>{fmtDate(record.createdAt, true)} | {record.provider || 'unknown'}{record.modelId ? ` | ${record.modelId}` : ''}</span>
                    <small>{record.answer?.answer || '--'}</small>
                  </button>
                ))}
              </div>
            ) : (
              <p className="crystal-ball__chat-history-empty">No saved Crystal Ball questions yet.</p>
            )}
          </section>
        </div>
      ) : null}

      {capacityDriverModalConcern ? (
        <CapacityDriversModal
          concern={capacityDriverModalConcern}
          onClose={() => setCapacityDriverModalConcern(null)}
          onTailClick={tail => {
            setCapacityDriverModalConcern(null);
            openCapacityConcernTail(tail);
          }}
        />
      ) : null}
    </section>
  );
}

export default CrystalBallTab;
